import { useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { SMS_PAY_URL } from "@/config/site";
import PorWidget from "../components/PorWidget";

const API = "/api"; // same-origin — the ingress/worker proxies /api to the backend

const fmt = (n) => "$" + Number(n).toLocaleString("en-US");
const AssessmentRequestsPanel = () => {
  const [requests, setRequests] = useState(null);
  const [notes, setNotes] = useState({});
  const [granting, setGranting] = useState(null);
  const [windows, setWindows] = useState({});
  const [error, setError] = useState("");

  const headers = () => ({
    Authorization: `Bearer ${sessionStorage.getItem("portal_token")}`,
    "Content-Type": "application/json",
  });

  const load = async () => {
    try {
      const res = await fetch(`${API}/portal/encounter/requests`, { headers: headers() });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setRequests(data.requests);
    } catch {
      setError("Assessment requests unavailable.");
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const decide = async (reference, decision) => {
    setError("");
    try {
      const body = { decision, note: notes[reference] || "" };
      if (decision === "GRANT") {
        const offered = (windows[reference] || []).filter(Boolean);
        if (offered.length) body.proposed_windows = offered;
      }
      const res = await fetch(`${API}/portal/encounter/${reference}/decision`, {
        method: "POST",
        headers: headers(),
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Decision failed");
      }
      setGranting(null);
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  if (requests === null && !error) return null;

  return (
    <div data-testid="assessment-requests-panel" className="overflow-x-auto" style={{ marginTop: 32 }}>
      <p className="portal-sub-tag" style={{ marginBottom: 12 }}>
        Assessment requests — agents asking for an encounter, awaiting family review
      </p>
      {error && <p className="portal-error" data-testid="assessment-panel-error">{error}</p>}
      {requests && requests.length === 0 && (
        <p className="portal-error" style={{ color: "#b0b7bd" }} data-testid="assessment-panel-empty">
          No assessment requests on file.
        </p>
      )}
      {requests && requests.map((r) => (
        <div key={r.reference} data-testid={`assessment-row-${r.reference}`}
          style={{ border: "1px solid #2c2a26", borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
            <strong style={{ color: "#e8e4dc" }}>{r.agent_name} — {r.agency}</strong>
            <span data-testid={`assessment-status-${r.reference}`}
              style={{ color: r.status === "PENDING_FAMILY_REVIEW" ? "#eec643" : "#8a8578", fontSize: "0.75rem", letterSpacing: "1px" }}>
              {r.status.replaceAll("_", " ")}
            </span>
          </div>
          <p style={{ color: "#b0b7bd", fontSize: "0.85rem", margin: "8px 0" }}>
            {r.role_title} · {r.contact} · requests: {r.requested_format}
          </p>
          <p style={{ color: "#d6d3d1", fontSize: "0.9rem", margin: "8px 0" }}>{r.reason}</p>
          <p style={{ color: "#78716c", fontSize: "0.75rem" }}>
            {r.reference} · received {new Date(r.created_at).toLocaleString()}
            {r.family_reference ? ` · ref ${r.family_reference}` : ""}
            {r.chain_contact_tx ? ` · logged on chain 9940 (${r.chain_contact_tx.slice(0, 12)}…)` : ""}
          </p>
          {r.status === "PENDING_FAMILY_REVIEW" && (
            <div style={{ marginTop: 12 }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <input
                  data-testid={`assessment-note-${r.reference}`}
                  placeholder="Note (optional)"
                  value={notes[r.reference] || ""}
                  onChange={(e) => setNotes({ ...notes, [r.reference]: e.target.value })}
                  style={{ flex: 1, minWidth: 180, background: "#171512", border: "1px solid #2c2a26", borderRadius: 6, color: "#d6d3d1", padding: "8px 12px", fontSize: "0.85rem" }}
                />
                <button className="prime-clearing-btn" data-testid={`assessment-grant-${r.reference}`}
                  onClick={() => setGranting(granting === r.reference ? null : r.reference)}>
                  {granting === r.reference ? "Cancel grant" : "Grant encounter…"}
                </button>
                <button className="prime-clearing-btn" data-testid={`assessment-decline-${r.reference}`}
                  style={{ borderColor: "#7f1d1d" }}
                  onClick={() => decide(r.reference, "DECLINE")}>Decline</button>
              </div>
              {granting === r.reference && (
                <div data-testid={`assessment-windows-${r.reference}`}
                  style={{ marginTop: 10, padding: 12, border: "1px solid #2c2a26", borderRadius: 8 }}>
                  <p style={{ color: "#8a8578", fontSize: "0.75rem", marginBottom: 8 }}>
                    Offer up to three windows — the agent confirms one from their reference page:
                  </p>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                    {[0, 1, 2].map((i) => (
                      <input key={i} type="datetime-local" data-testid={`assessment-window-${i}-${r.reference}`}
                        value={(windows[r.reference] || [])[i] || ""}
                        onChange={(e) => {
                          const next = [...(windows[r.reference] || [])];
                          next[i] = e.target.value;
                          setWindows({ ...windows, [r.reference]: next });
                        }}
                        style={{ background: "#171512", border: "1px solid #2c2a26", borderRadius: 6, color: "#d6d3d1", padding: "8px 10px", fontSize: "0.8rem" }}
                      />
                    ))}
                  </div>
                  <button className="prime-clearing-btn" data-testid={`assessment-confirm-grant-${r.reference}`}
                    onClick={() => decide(r.reference, "GRANT")}>Confirm grant</button>
                </div>
              )}
            </div>
          )}
          {r.status === "ENCOUNTER_SCHEDULED" && r.confirmed_window && (
            <p data-testid={`assessment-confirmed-${r.reference}`}
              style={{ color: "#6ee7b7", fontSize: "0.85rem", marginTop: 8 }}>
              Encounter confirmed for {new Date(r.confirmed_window).toLocaleString()}
            </p>
          )}
          {r.status === "ENCOUNTER_GRANTED" && (r.proposed_windows || []).length > 0 && (
            <p style={{ color: "#8a8578", fontSize: "0.8rem", marginTop: 8 }}>
              Windows offered: {r.proposed_windows.map((w) => new Date(w).toLocaleString()).join(" · ")} — awaiting the agent's pick
            </p>
          )}
          {r.decision_note && (
            <p style={{ color: "#8a8578", fontSize: "0.8rem", marginTop: 8 }}>Note: {r.decision_note}</p>
          )}
        </div>
      ))}
    </div>
  );
};



const GOVERNING_CLAUSES = [
  {
    id: "4.1",
    title: "Prohibition of Bite and Increase (Neshekh)",
    body: "The contracting parties explicitly acknowledge and agree that this transaction is governed exclusively by Natural Law, Biblical Economic Ordinances, and Private Sovereign Right. At no point shall any trailing interest, compounding interest, variable yield, or amortized usury accrue or be demanded under this agreement. Debt is recognized strictly as a fixed, bounded credit facility containing zero trailing tail risk.",
  },
  {
    id: "4.2",
    title: "Present-Value Monetization & Up-Front Yield",
    body: "All transactional clearing fees, administrative costs, or repository margins shall be fully calculated, settled, and retained up-front at the precise moment of execution via a Present-Value Discount (PV = FV − ΔV). Once net capital is advanced through the Warehouse Line, the Final Redemption Value (FV) remains fixed and unalterable.",
  },
  {
    id: "4.3",
    title: "Absolute Inherent Capitalization",
    body: "Because the debt asset created under this private contract is anchored 1:1 by non-derivative, physically un-hypothecated collateral (audited Tier-1 Specie and Perennial Crop Assets), it carries intrinsic clearing value in the primary, secondary, and capital markets. The physical presence of the collateral base renders the charging of trailing open-ended premiums legally null, void, and unenforceable under the natural jurisdiction of this ledger.",
  },
];

const PLACEMENT_ARTICLES = [
  {
    id: "I",
    title: "Jurisdictional Separation & Private Rights",
    body: "This Private Master Agreement governs the acquisition and settlement of tokenized asset-backed debt tranches, cryptographically designated as \"$abDP\" on the BGNZN Ledger. Every transaction occurring hereunder is an arm's-length, private, peer-to-peer capital transaction. The parties explicitly state that this contract is written under Private Contract Law and Sovereign Prerogative, operating outside the administrative oversight of public regulatory bodies, including the SEC, FINRA, or FinCEN.",
  },
  {
    id: "II",
    title: "Protection of Inherent Property and Zero-Tranching Laws",
    body: "Unlike traditional public commercial entities bound by banking charters to liquidate paper debt instruments within a Trade Day + 3 (T+3) window, the Originating Repository — the BOURG MERCHANT EXCHANGE (BMEX), a 501(c)(2) privatized non-marketable exchange — retains permanent, absolute ownership of the underlying physical capital (including Tier-1 Vault Specie, sugar and cacao warehouse inventories, and the 1-acre Zone 9b agroforestry land assets). The external capital market buyer acquires a non-transferable, fractionalized yield right. Any attempt by a public purchasing institution to slice, re-tranche, or hypothecate the underlying physical asset base into public secondary debt markets triggers an immediate, automatic cancelation of the connected digital keys and an immediate forfeiture of the asset position.",
  },
  {
    id: "III",
    title: "Zero-Usury Recapture and Natural Law Accounting",
    body: "In complete compliance with Biblical Economic Ordinances and Natural Law, no continuous, variable, or compounding interest calculations are recognized under this instrument. All transaction yield is structured exclusively as an up-front present-value discount (PV = FV − ΔV) and fully settled on Trade Day + 0. The transaction itself constitutes final execution and settlement.",
  },
];

const Portal = () => {
  const [keyId, setKeyId] = useState("");
  const [passphrase, setPassphrase] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [ledger, setLedger] = useState(null);
  const [faceValue, setFaceValue] = useState("1000000");
  const [ratePercent, setRatePercent] = useState("6");
  const [durationDays, setDurationDays] = useState("180");
  const [advance, setAdvance] = useState(null);
  const [advanceError, setAdvanceError] = useState("");
  const [eqInputs, setEqInputs] = useState({
    vault_specie: "2144000",
    crop_value: "145000",
    decay_percent: "10",
    netting_efficiency_percent: "90",
    liabilities: "296000",
  });
  const [equilibrium, setEquilibrium] = useState(null);
  const [eqError, setEqError] = useState("");
  const [contracts, setContracts] = useState([]);
  const [mojaveLiab, setMojaveLiab] = useState("296000");
  const [mojaveSeason, setMojaveSeason] = useState("OPTIMAL_FALL_HARVEST");
  const [mojave, setMojave] = useState(null);
  const [mojaveError, setMojaveError] = useState("");
  const [gold, setGold] = useState(null);
  const [onb, setOnb] = useState({ client_id: "SOV-COOP-MOJAVE-04", face: "50000", rate: "5.5", days: "120" });
  const [onbResult, setOnbResult] = useState(null);
  const [onbError, setOnbError] = useState("");
  const [recon, setRecon] = useState(null);
  const [genesis, setGenesis] = useState(null);
  const [nodeStatus, setNodeStatus] = useState(null);
  const [contract, setContract] = useState(null);
  const [contractOpen, setContractOpen] = useState(false);
  const [margin, setMargin] = useState({ tree_id: "buDT-01", yield_lbs: "135", spot: "8.50" });
  const [marginResult, setMarginResult] = useState(null);
  const [swap, setSwap] = useState({ buyer: "1000", seller: "1000", asset: "Desert_Fruit_Yield" });
  const [swapResult, setSwapResult] = useState(null);
  const [handshake, setHandshake] = useState(null);
  const [lineId, setLineId] = useState(
    "0xac2c8ef44556f78478726ccc709130c35f5aa60fe8537e0e0800a68785f24455"
  );
  const [lineResult, setLineResult] = useState(null);
  const [lineError, setLineError] = useState("");
  const [netting, setNetting] = useState({
    counterparty: "SOV-REP-GLOBAL-09",
    asset: "Cacao_Bulk_MT",
    tons: "25",
    vault: "VAULT-CHAMBER-3A",
  });
  const [nettingResult, setNettingResult] = useState(null);
  const [oracle, setOracle] = useState({
    desk: "DESK_01_LONDON_OTC",
    ticker: "Au_Gold_g",
    rate: "135.35",
  });
  const [oracleResult, setOracleResult] = useState(null);
  const [oracleMatrix, setOracleMatrix] = useState(null);
  const [ledgerSchema, setLedgerSchema] = useState(null);
  const [schemaOpen, setSchemaOpen] = useState(false);
  const [receipts, setReceipts] = useState([]);
  const [receiptForm, setReceiptForm] = useState({
    asset: "AU",
    units: "12442.5",
    cost: "1580000",
    haircut: "0",
    vault: "VAULT-CHAMBER-3A",
    params: "400 oz London Good Delivery bars, sealed cage 7, assay 99.99",
  });
  const [receiptResult, setReceiptResult] = useState(null);
  const [receiptError, setReceiptError] = useState("");
  const [lookupHash, setLookupHash] = useState("");
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupError, setLookupError] = useState("");
  const [settleBusy, setSettleBusy] = useState(false);
  const [settleResult, setSettleResult] = useState(null);
  const [settleError, setSettleError] = useState("");
  const [fiatWallet, setFiatWallet] = useState(null);
  const [smsForm, setSmsForm] = useState({
    sender: "Sovereign_Entity_A",
    recipient: "Sovereign_Entity_B",
    amount: "250",
  });
  const [smsResult, setSmsResult] = useState(null);
  const [escrowId, setEscrowId] = useState("");
  const [escrowResult, setEscrowResult] = useState(null);
  const [escrowError, setEscrowError] = useState("");
  const [escrowBusy, setEscrowBusy] = useState(false);
  const [escrowLifecycle, setEscrowLifecycle] = useState(null);
  const [nexor, setNexor] = useState(null);
  const [nexorAccounts, setNexorAccounts] = useState(null);
  const [nexorBalances, setNexorBalances] = useState(null);
  const [nexorTxns, setNexorTxns] = useState(null);
  const [nexorEvents, setNexorEvents] = useState([]);
  const [liqBusy, setLiqBusy] = useState(false);
  const [liqResult, setLiqResult] = useState(null);
  const [liqError, setLiqError] = useState("");
  const [liqLookupId, setLiqLookupId] = useState("");
  const [liqLookupResult, setLiqLookupResult] = useState(null);
  const [liqLookupError, setLiqLookupError] = useState("");
  const [auditResult, setAuditResult] = useState(null);
  const [rbLineId, setRbLineId] = useState(
    "0xac2c8ef44556f78478726ccc709130c35f5aa60fe8537e0e0800a68785f24455"
  );
  const [rbReason, setRbReason] = useState("Telemetry oracle fault — erroneous biological margin call");
  const [rbEvent, setRbEvent] = useState(null);
  const [rbEventId, setRbEventId] = useState("");
  const [rbBusy, setRbBusy] = useState(false);
  const [rbError, setRbError] = useState("");
  const [treasury, setTreasury] = useState(null);
  const [wdAddress, setWdAddress] = useState("");
  const [wdAmount, setWdAmount] = useState("");
  const [wdBusy, setWdBusy] = useState(false);
  const [wdResult, setWdResult] = useState(null);
  const [wdError, setWdError] = useState("");
  const [documents, setDocuments] = useState(null);
  const [solvency, setSolvency] = useState(null);
  const [docBusy, setDocBusy] = useState("");
  const [roster, setRoster] = useState(null);
  const [onboardForm, setOnboardForm] = useState({ label: "", phone: "", float: "" });
  const [onboardBusy, setOnboardBusy] = useState(false);
  const [onboardResult, setOnboardResult] = useState(null);
  const [onboardError, setOnboardError] = useState("");
  const [fundingDesk, setFundingDesk] = useState(null);
  const [settleForm, setSettleForm] = useState({}); // intention_id -> {amount, reference}
  const [fdBusy, setFdBusy] = useState("");
  const [fdMsg, setFdMsg] = useState("");
  const [matchForm, setMatchForm] = useState({ amount: "", reference: "" });
  const [matchResults, setMatchResults] = useState(null);
  const [matchBusy, setMatchBusy] = useState(false);

  const matchBankLine = async (e) => {
    e.preventDefault();
    setMatchBusy(true);
    setMatchResults(null);
    try {
      const res = await fetch(`${API}/portal/funding-desk/match`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("portal_token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          amount_received_usd: parseFloat(matchForm.amount),
          external_reference: matchForm.reference || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "match failed");
      setMatchResults(data.matches || []);
    } catch (err) {
      setFdMsg(`Match fault — ${err.message}`);
    } finally {
      setMatchBusy(false);
    }
  };

  const applyMatch = (m) => {
    setSettleForm({
      ...settleForm,
      [m.intention_id]: { amount: matchForm.amount, reference: matchForm.reference },
    });
    setMatchResults(null);
    document.querySelector(`[data-testid="funding-row-${m.intention_id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const dismissIntention = async (intentionId) => {
    setFdBusy(intentionId);
    setFdMsg("");
    try {
      const res = await fetch(`${API}/portal/funding-desk/${intentionId}/dismiss`, {
        method: "POST",
        headers: { Authorization: `Bearer ${sessionStorage.getItem("portal_token")}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "dismiss failed");
      setFdMsg(`${intentionId} dismissed — cleared from the open queue.`);
      loadFundingDesk();
      loadBackfills();
    } catch (err) {
      setFdMsg(`Dismiss fault — ${err.message}`);
    } finally {
      setFdBusy("");
    }
  };

  const loadFundingDesk = async () => {
    try {
      const res = await fetch(`${API}/portal/funding-desk`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("portal_token")}` },
      });
      if (res.ok) setFundingDesk(await res.json());
    } catch {
      /* funding desk non-blocking */
    }
  };

  // sales backfill queue — sale lines merchants texted in, waiting for one tap
  const [backfills, setBackfills] = useState(null);
  const [bfBusy, setBfBusy] = useState("");
  // on-chain clearing registry — the desk's T+0 settle rail with the 125% floor enforced
  const [registry, setRegistry] = useState(null);
  const [regBusy, setRegBusy] = useState(false);
  // ledger room — deeds on chain 9940 + vault/SKR custody (Mongo)
  const deskInput = { background: "rgba(11,15,25,0.9)", border: "1px solid rgba(176,183,189,0.3)", borderRadius: 4, color: "#e8e9ea", fontFamily: "inherit", fontSize: "0.7rem", padding: "4px 8px" };
  const deskBtn = { background: "rgba(229,193,88,0.12)", border: "1px solid rgba(229,193,88,0.5)", color: "#e5c158", borderRadius: 4, padding: "4px 10px", cursor: "pointer", fontFamily: "inherit", fontSize: "0.7rem" };
  const [deeds, setDeeds] = useState(null);
  const [vault, setVault] = useState(null);
  const [deedForm, setDeedForm] = useState({ parcel_apn: "", county: "", legal_description: "", fiat_value_usd: "" });
  const [deedAbsorb, setDeedAbsorb] = useState({});
  const [barForm, setBarForm] = useState({ bar_serial_number: "", element: "GOLD", purity_percentage: "", gross_weight_grams: "" });
  const [skrForm, setSkrForm] = useState({ member_uuid: "", bar_serial_numbers: "" });
  const loadLedgerRoom = async () => {
    const headers = { Authorization: `Bearer ${sessionStorage.getItem("portal_token")}` };
    try {
      const [dr, vr] = await Promise.all([
        fetch(`${API}/portal/deeds`, { headers }),
        fetch(`${API}/portal/vault/overview`, { headers }),
      ]);
      if (dr.ok) setDeeds(await dr.json());
      if (vr.ok) setVault(await vr.json());
    } catch { /* ledger room is additive */ }
  };
  const ledgerPost = async (path, body) => {
    setRegBusy(true);
    try {
      const res = await fetch(`${API}${path}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${sessionStorage.getItem("portal_token")}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "fault");
      await loadLedgerRoom();
      return data;
    } catch (err) {
      alert(err.message);
      return null;
    } finally {
      setRegBusy(false);
    }
  };
  const deployDeedRegistry = async () => {
    setRegBusy(true);
    try {
      const res = await fetch(`${API}/portal/treasury/deploy-deed-registry`, {
        method: "POST",
        headers: { Authorization: `Bearer ${sessionStorage.getItem("portal_token")}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "deploy failed");
      alert(data.status === "ALREADY_DEPLOYED"
        ? `Deed registry already live at ${data.real_estate_registry.address}`
        : `Deed registry DEPLOYED at ${data.real_estate_registry.address}`);
      await loadLedgerRoom();
    } catch (err) {
      alert(err.message);
    } finally {
      setRegBusy(false);
    }
  };
  const registerDeed = async () => {
    const ok = await ledgerPost("/portal/deeds/register", {
      parcel_apn: deedForm.parcel_apn, county: deedForm.county,
      legal_description: deedForm.legal_description,
      fiat_value_usd: Number(deedForm.fiat_value_usd) || 0,
    });
    if (ok) setDeedForm({ parcel_apn: "", county: "", legal_description: "", fiat_value_usd: "" });
  };
  const absorbDeed = async (assetId) => {
    const form = deedAbsorb[assetId] || {};
    await ledgerPost(`/portal/deeds/${assetId}/absorb`, {
      member_uuid: form.member_uuid || "", fractions: Number(form.fractions) || 0,
    });
  };
  const registerBar = async () => {
    const ok = await ledgerPost("/portal/vault/bars", {
      bar_serial_number: barForm.bar_serial_number, element: barForm.element,
      purity_percentage: Number(barForm.purity_percentage) || 0,
      gross_weight_grams: Number(barForm.gross_weight_grams) || 0,
    });
    if (ok) setBarForm({ bar_serial_number: "", element: "GOLD", purity_percentage: "", gross_weight_grams: "" });
  };
  const issueSkr = async () => {
    const ok = await ledgerPost("/portal/vault/skr", {
      member_uuid: skrForm.member_uuid,
      bar_serial_numbers: skrForm.bar_serial_numbers.split(",").map((s) => s.trim()).filter(Boolean),
    });
    if (ok) {
      alert(`SKR issued: ${ok.skr_number} — ${ok.net_pure_weight_grams}g net pure across ${ok.bar_serial_numbers.length} bar(s)`);
      setSkrForm({ member_uuid: "", bar_serial_numbers: "" });
    }
  };
  // infrastructure — tangible assets absorbed with decommissioned cash, sealed on chain 9940
  const [infra, setInfra] = useState(null);
  const [infraForm, setInfraForm] = useState({ asset_class: "LAND", legal_descriptor: "", acquisition_cost_usd: "" });
  const loadInfra = async () => {
    try {
      const res = await fetch(`${API}/portal/infrastructure`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("portal_token")}` },
      });
      if (res.ok) setInfra(await res.json());
    } catch { /* additive */ }
  };
  const deployInfraRegistry = async () => {
    setRegBusy(true);
    try {
      const res = await fetch(`${API}/portal/treasury/deploy-infra-registry`, {
        method: "POST",
        headers: { Authorization: `Bearer ${sessionStorage.getItem("portal_token")}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "deploy failed");
      alert(data.status === "ALREADY_DEPLOYED"
        ? `Infrastructure registry already live at ${data.infrastructure_registry.address}`
        : `Infrastructure registry DEPLOYED at ${data.infrastructure_registry.address}`);
      await loadInfra();
    } catch (err) {
      alert(err.message);
    } finally {
      setRegBusy(false);
    }
  };
  const absorbInfra = async () => {
    const ok = await ledgerPost("/portal/infrastructure/absorb", {
      asset_class: infraForm.asset_class,
      legal_descriptor: infraForm.legal_descriptor,
      acquisition_cost_usd: Number(infraForm.acquisition_cost_usd) || 0,
    });
    if (ok) {
      if (ok.status === "STAGED_PENDING_MULTISIG") {
        alert(`High-value asset STAGED for council multi-sig — ID ${ok.short_id}. Awaiting ${ok.required_approvals} council signatures by SMS (/APPROVE ${ok.short_id}).`);
      }
      setInfraForm({ asset_class: "LAND", legal_descriptor: "", acquisition_cost_usd: "" });
      await loadInfra();
    }
  };
  // asset absorption engine — tangible property titled to a member's name on chain 9940;
  // equity is attributed non-derivative backing, never a spendable balance
  const [assetEngine, setAssetEngine] = useState(null);
  const [assetForm, setAssetForm] = useState({ property_class: "REAL_ESTATE", legal_metadata: "", valuation_usd: "", member_uuid: "" });
  const [titleMove, setTitleMove] = useState({});
  const loadAssetEngine = async () => {
    try {
      const res = await fetch(`${API}/portal/asset-engine`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("portal_token")}` },
      });
      if (res.ok) setAssetEngine(await res.json());
    } catch { /* additive */ }
  };
  const deployAssetEngine = async () => {
    setRegBusy(true);
    try {
      const res = await fetch(`${API}/portal/treasury/deploy-asset-engine`, {
        method: "POST",
        headers: { Authorization: `Bearer ${sessionStorage.getItem("portal_token")}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "deploy failed");
      alert(data.status === "ALREADY_DEPLOYED"
        ? `Asset engine already live at ${data.asset_absorption_engine.address}`
        : `Asset engine DEPLOYED at ${data.asset_absorption_engine.address}`);
      await loadAssetEngine();
    } catch (err) {
      alert(err.message);
    } finally {
      setRegBusy(false);
    }
  };
  const absorbAsset = async () => {
    const ok = await ledgerPost("/portal/asset-engine/absorb", {
      property_class: assetForm.property_class,
      legal_metadata: assetForm.legal_metadata,
      valuation_usd: Number(assetForm.valuation_usd) || 0,
      member_uuid: assetForm.member_uuid,
    });
    if (ok) {
      setAssetForm({ property_class: "REAL_ESTATE", legal_metadata: "", valuation_usd: "", member_uuid: "" });
      await loadAssetEngine();
    }
  };
  const liquidateTitle = async (propertyId) => {
    const to = (titleMove[propertyId] || "").trim();
    if (!to) return;
    const ok = await ledgerPost("/portal/asset-engine/liquidate", { property_id: propertyId, to_member_uuid: to });
    if (ok) await loadAssetEngine();
  };
  // family covenants — link two co-parent members so either can text /MAINTAIN on the 50/50 rail
  const [covenants, setCovenants] = useState(null);
  const [covenantForm, setCovenantForm] = useState({ member_a_uuid: "", member_b_uuid: "" });
  const [escrows, setEscrows] = useState({});
  const [escrowForm, setEscrowForm] = useState({});
  const loadCovenants = async () => {
    const headers = { Authorization: `Bearer ${sessionStorage.getItem("portal_token")}` };
    try {
      const res = await fetch(`${API}/portal/covenants`, { headers });
      if (res.ok) {
        const data = await res.json();
        setCovenants(data);
        const views = {};
        for (const c of data.covenants || []) {
          try {
            const r = await fetch(`${API}/portal/covenants/${c.covenant_id}/escrow`, { headers });
            if (r.ok) views[c.covenant_id] = await r.json();
          } catch { /* additive */ }
        }
        setEscrows(views);
      }
    } catch { /* additive */ }
  };
  const linkCovenant = async () => {
    const ok = await ledgerPost("/portal/covenants", {
      member_a_uuid: covenantForm.member_a_uuid, member_b_uuid: covenantForm.member_b_uuid,
    });
    if (ok) {
      setCovenantForm({ member_a_uuid: "", member_b_uuid: "" });
      await loadCovenants();
    }
  };
  const ef = (cid) => escrowForm[cid] || {};
  const setEf = (cid, patch) => setEscrowForm({ ...escrowForm, [cid]: { ...ef(cid), ...patch } });
  const deployEscrow = async (cid) => {
    const ok = await ledgerPost(`/portal/covenants/${cid}/deploy-escrow`, {});
    if (ok) await loadCovenants();
  };
  const lockCovenant = async (cid) => {
    const ok = await ledgerPost(`/portal/covenants/${cid}/lock`, {});
    if (ok) await loadCovenants();
  };
  const fundEscrow = async (cid) => {
    const f = ef(cid);
    const ok = await ledgerPost(`/portal/covenants/${cid}/escrow/fund`, {
      amount_usd: Number(f.fund_amount) || 0,
      source_member_uuid: f.fund_source && f.fund_source !== "TREASURY" ? f.fund_source : undefined,
    });
    if (ok) await loadCovenants();
  };
  const proposeEscrow = async (cid, c) => {
    const f = ef(cid);
    const ok = await ledgerPost(`/portal/covenants/${cid}/escrow/propose`, {
      acting_member_uuid: f.act_member || c.member_a_uuid,
      amount_usd: Number(f.amount) || 0,
      purpose: f.purpose || "",
      recipient_address: f.recipient || "",
    });
    if (ok) await loadCovenants();
  };
  const countersignEscrow = async (cid, proposalId, actingUuid) => {
    const ok = await ledgerPost(`/portal/covenants/${cid}/escrow/countersign`, {
      acting_member_uuid: actingUuid, proposal_id: proposalId,
    });
    if (ok) await loadCovenants();
  };
  // TCU register — the trust's 100 certificate units; committee rule: one director proposes,
  // a second, distinct director approves, then the motion executes on chain 9940
  const [tcu, setTcu] = useState(null);
  const [tcuForm, setTcuForm] = useState({ action: "ASSIGN", from_member_uuid: "", member_uuid: "", units: "", resolution_note: "" });
  const loadTcu = async () => {
    try {
      const res = await fetch(`${API}/portal/tcu`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("portal_token")}` },
      });
      if (res.ok) setTcu(await res.json());
    } catch { /* additive */ }
  };
  const deployTcuRegistry = async () => {
    setRegBusy(true);
    try {
      const res = await fetch(`${API}/portal/treasury/deploy-tcu-registry`, {
        method: "POST",
        headers: { Authorization: `Bearer ${sessionStorage.getItem("portal_token")}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "deploy failed");
      alert(data.status === "ALREADY_DEPLOYED"
        ? `TCU register already live at ${data.tcu_registry.address}`
        : `TCU register DEPLOYED at ${data.tcu_registry.address} — 100 units in the Board reserve`);
      await loadTcu();
    } catch (err) {
      alert(err.message);
    } finally {
      setRegBusy(false);
    }
  };

  const deployBourgExchangeRegistry = async () => {
    setRegBusy(true);
    try {
      const res = await fetch(`${API}/portal/treasury/deploy-bourg-exchange-registry`, {
        method: "POST",
        headers: { Authorization: `Bearer ${sessionStorage.getItem("portal_token")}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "deploy failed");
      alert(data.status === "ALREADY_DEPLOYED"
        ? `Bourg Exchange registry already live at ${data.bourg_exchange_registry.address}`
        : `Bourg Exchange registry DEPLOYED at ${data.bourg_exchange_registry.address} — cutting house, securitization, treasury, warehouse`);
      await loadTcu();
    } catch (err) {
      alert(err.message);
    } finally {
      setRegBusy(false);
    }
  };
  const proposeTcuMotion = async () => {
    const ok = await ledgerPost("/portal/tcu/motions", {
      action: tcuForm.action,
      from_member_uuid: tcuForm.from_member_uuid || null,
      member_uuid: tcuForm.member_uuid,
      units: Number(tcuForm.units) || 0,
      resolution_note: tcuForm.resolution_note || "committee motion",
    });
    if (ok) {
      setTcuForm({ action: "ASSIGN", from_member_uuid: "", member_uuid: "", units: "", resolution_note: "" });
      await loadTcu();
    }
  };
  const approveTcuMotion = async (id) => {
    const ok = await ledgerPost(`/portal/tcu/motions/${id}/approve`, {});
    if (ok) alert(`Motion ${id} EXECUTED on chain 9940, block ${ok.block}`);
    await loadTcu();
  };
  const dismissTcuMotion = async (id) => {
    await ledgerPost(`/portal/tcu/motions/${id}/dismiss`, {});
    await loadTcu();
  };
  const renderAnnualMinute = async () => {
    const ok = await ledgerPost("/portal/trust/annual-minute", {});
    if (ok) {
      alert(`Annual minute filed: ${ok.filename} — ${ok.tcu_holders} TCU holders, ${ok.deeds} deeds, ${ok.bars} bars, ${ok.infrastructure_assets} infrastructure assets. Find it in the document vault.`);
      loadDocuments();
    }
  };
  const loadRegistry = async () => {
    try {
      const res = await fetch(`${API}/portal/clearing-registry`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("portal_token")}` },
      });
      if (res.ok) setRegistry(await res.json());
    } catch {
      /* registry view non-blocking */
    }
  };
  const deployRegistry = async () => {
    setRegBusy(true);
    try {
      const res = await fetch(`${API}/portal/treasury/deploy-clearing-registry`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("portal_token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "deploy failed");
      alert(data.status === "ALREADY_DEPLOYED"
        ? `Clearing registry already live at ${data.clearing_registry.address}`
        : `Clearing registry DEPLOYED at ${data.clearing_registry.address} — desk settles now ride executeAutonomousMatch on-chain, 125% floor enforced.`);
      await loadRegistry();
    } catch (err) {
      alert(err.message);
    } finally {
      setRegBusy(false);
    }
  };
  const loadBackfills = async () => {
    try {
      const res = await fetch(`${API}/portal/sale-backfills?status=QUEUED`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("portal_token")}` },
      });
      if (res.ok) setBackfills(await res.json());
    } catch {
      /* backfill queue non-blocking */
    }
  };
  const backfillAction = async (id, action) => {
    setBfBusy(id);
    try {
      const res = await fetch(`${API}/portal/sale-backfills/${id}/${action}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${sessionStorage.getItem("portal_token")}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : `${action} failed`);
      await loadBackfills();
    } catch (err) {
      alert(err.message);
    } finally {
      setBfBusy("");
    }
  };
  const backfillLedgerAll = async () => {
    setBfBusy("ALL");
    try {
      const res = await fetch(`${API}/portal/sale-backfills/ledger-all`, {
        method: "POST",
        headers: { Authorization: `Bearer ${sessionStorage.getItem("portal_token")}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "ledger-all failed");
      const faults = (data.faults || []).length;
      alert(`Ledgered ${(data.ledgered || []).length} sale(s), $${Number(data.gross_usd || 0).toFixed(2)} gross onto the tape.${faults ? ` ${faults} row(s) faulted and stayed queued.` : ""}`);
      await loadBackfills();
    } catch (err) {
      alert(err.message);
    } finally {
      setBfBusy("");
    }
  };
  // desk paste-in — a merchant's order text that arrived off-rail gets the same one-tap queue
  const [bfPaste, setBfPaste] = useState("");  const backfillIntake = async () => {
    if (!bfPaste.trim()) return;
    setBfBusy("PASTE");
    try {
      const res = await fetch(`${API}/portal/sale-backfills/intake`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("portal_token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ raw_text: bfPaste }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "intake failed");
      alert(`Queued ${(data.queued || []).length} sale line(s), $${Number(data.gross_usd || 0).toFixed(2)} gross — review below, then ledger.`);
      setBfPaste("");
      await loadBackfills();
    } catch (err) {
      alert(err.message);
    } finally {
      setBfBusy("");
    }
  };
  // reverse stale rows a ledger run swept in — returns their value to treasury on-chain,
  // strikes them off the tape. Only touches rows whose label still carries a raw "$".
  const backfillReverseDuplicates = async () => {
    setBfBusy("REVERSE");
    try {
      const res = await fetch(`${API}/portal/sale-backfills/reverse-duplicates`, {
        method: "POST",
        headers: { Authorization: `Bearer ${sessionStorage.getItem("portal_token")}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "reversal failed");
      const n = (data.reversed || []).filter((r) => r.reversed_usd).length;
      alert(n
        ? `Reversed ${n} stale row(s) — $${Number(data.returned_usd || 0).toFixed(2)} returned to treasury on-chain and struck from the tape.`
        : "No stale rows found — nothing to reverse.");
      await loadBackfills();
    } catch (err) {
      alert(err.message);
    } finally {
      setBfBusy("");
    }
  };

  const [reserve, setReserve] = useState(null);
  const loadReserve = async () => {
    try {
      const res = await fetch(`${API}/portal/reserve-position`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("portal_token")}` },
      });
      if (res.ok) setReserve(await res.json());
    } catch {
      /* reserve read non-blocking */
    }
  };

  const [floatData, setFloatData] = useState(null);
  const loadFloat = async () => {
    try {
      const res = await fetch(`${API}/portal/float`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("portal_token")}` },
      });
      if (res.ok) setFloatData(await res.json());
    } catch {
      /* float read non-blocking */
    }
  };

  const settleIntention = async (intentionId) => {
    const form = settleForm[intentionId] || {};
    setFdBusy(intentionId);
    setFdMsg("");
    try {
      const res = await fetch(`${API}/portal/funding-desk/${intentionId}/settle`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("portal_token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          amount_received_usd: parseFloat(form.amount),
          external_reference: form.reference || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "settlement failed");
      setFdMsg(`${intentionId} settled — $${data.amount_received_usd} $sUSD to ${data.member_label} (block ${data.settlement_tx?.block})`);
      loadFundingDesk();
      loadBackfills();
      loadRoster();
    } catch (err) {
      setFdMsg(`Settle fault — ${err.message}`);
    } finally {
      setFdBusy("");
    }
  };

  const onboardMember = async (e) => {
    e.preventDefault();
    setOnboardBusy(true);
    setOnboardError("");
    setOnboardResult(null);
    try {
      const res = await fetch(`${API}/portal/members/onboard`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("portal_token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          label: onboardForm.label,
          phone: onboardForm.phone,
          initial_float_usd: parseFloat(onboardForm.float) || 0,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Onboarding failed");
      setOnboardResult(data);
      setOnboardForm({ label: "", phone: "", float: "" });
      loadRoster();
    } catch (err) {
      setOnboardError(err.message);
    } finally {
      setOnboardBusy(false);
    }
  };

  const portalPost = async (path, body) => {
    const res = await fetch(`${API}/portal/${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${sessionStorage.getItem("portal_token")}`,
      },
      body: JSON.stringify(body),
    });
    if (res.status === 401) throw new Error("session");
    if (!res.ok) throw new Error("bad request");
    return res.json();
  };

  const runLiquidationLifecycle = async (mode) => {
    setLiqBusy(true);
    setLiqResult(null);
    setLiqError("");
    try {
      const res = await fetch(`${API}/portal/liquidation/lifecycle`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("portal_token")}`,
        },
        body: JSON.stringify({ mode }),
      });
      if (res.status === 401) {
        setLiqError("Session expired — close and re-initialize the session.");
        return;
      }
      if (!res.ok) throw new Error("bad request");
      const data = await res.json();
      setLiqResult(data);
      if (data.agreement_id) setLiqLookupId(data.agreement_id);
    } catch {
      setLiqError("Liquidation engine fault — the node may be offline.");
    } finally {
      setLiqBusy(false);
    }
  };

  const runLiquidationLookup = async (e) => {
    e.preventDefault();
    setLiqLookupResult(null);
    setLiqLookupError("");
    try {
      const res = await fetch(
        `${API}/portal/liquidation/${encodeURIComponent(liqLookupId.trim())}`,
        { headers: { Authorization: `Bearer ${sessionStorage.getItem("portal_token")}` } }
      );
      if (res.status === 401) {
        setLiqLookupError("Session expired — close and re-initialize the session.");
        return;
      }
      if (!res.ok) throw new Error("bad request");
      setLiqLookupResult(await res.json());
    } catch {
      setLiqLookupError("Agreement query failed — verify the identifier and retry.");
    }
  };

  const runBreachAudit = async () => {
    setAuditResult(null);
    try {
      const res = await fetch(`${API}/portal/liquidation/audit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("portal_token")}`,
        },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error("bad request");
      setAuditResult(await res.json());
    } catch {
      setAuditResult({ faults: ["AUDIT_CYCLE_UNAVAILABLE"] });
    }
  };

  const runRollbackInitiate = async (e) => {
    e.preventDefault();
    setRbBusy(true);
    setRbError("");
    setRbEvent(null);
    try {
      const res = await fetch(`${API}/portal/rollback/initiate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("portal_token")}`,
        },
        body: JSON.stringify({ line_id: rbLineId.trim(), reason: rbReason }),
      });
      if (res.status === 401) {
        setRbError("Session expired — close and re-initialize the session.");
        return;
      }
      if (!res.ok) throw new Error("bad request");
      const data = await res.json();
      setRbEvent(data);
      setRbEventId(data.event_id);
    } catch {
      setRbError("Rollback initiation fault — the circuit or node may be offline.");
    } finally {
      setRbBusy(false);
    }
  };

  const runRollbackCosign = async () => {
    setRbBusy(true);
    setRbError("");
    try {
      const res = await fetch(`${API}/portal/rollback/cosign`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("portal_token")}`,
        },
        body: JSON.stringify({ event_id: rbEventId.trim() }),
      });
      if (res.status === 401) {
        setRbError("Session expired — close and re-initialize the session.");
        return;
      }
      if (!res.ok) throw new Error("bad request");
      const data = await res.json();
      setRbEvent((prev) => ({ ...prev, ...data }));
    } catch {
      setRbError("Dual-signature execution fault — verify the event identifier.");
    } finally {
      setRbBusy(false);
    }
  };

  const loadTreasury = async () => {
    try {
      const res = await fetch(`${API}/portal/treasury`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("portal_token")}` },
      });
      if (res.ok) setTreasury(await res.json());
    } catch {
      /* treasury book non-blocking */
    }
  };

  const loadDocuments = async () => {
    try {
      const res = await fetch(`${API}/portal/documents`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("portal_token")}` },
      });
      if (res.ok) setDocuments(await res.json());
    } catch {
      /* document vault non-blocking */
    }
  };

  const loadSolvency = async () => {
    try {
      const res = await fetch(`${API}/portal/solvency`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("portal_token")}` },
      });
      if (res.ok) setSolvency(await res.json());
    } catch {
      /* solvency console non-blocking */
    }
  };

  // Live solvency audit — re-polls on the chain's 2.00s block pace while signed in
  useEffect(() => {
    if (!ledger) return undefined;
    const id = setInterval(loadSolvency, 2000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ledger]);

  const loadRoster = async () => {
    try {
      const res = await fetch(`${API}/portal/members/roster`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("portal_token")}` },
      });
      if (res.ok) setRoster(await res.json());
    } catch {
      /* member roster non-blocking */
    }
  };

  const downloadDocument = async (filename) => {
    setDocBusy(filename);
    try {
      const res = await fetch(`${API}/portal/documents/${filename}`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("portal_token")}` },
      });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      /* transient download fault */
    } finally {
      setDocBusy("");
    }
  };

  const runTreasuryWithdraw = async (e) => {
    e.preventDefault();
    setWdBusy(true);
    setWdResult(null);
    setWdError("");
    try {
      const res = await fetch(`${API}/portal/withdraw`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("portal_token")}`,
        },
        body: JSON.stringify({ to_address: wdAddress.trim(), amount_usd: Number(wdAmount) }),
      });
      if (res.status === 401) {
        setWdError("Session expired — close and re-initialize the session.");
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "bad request");
      }
      setWdResult(await res.json());
      loadTreasury();
    } catch (error) {
      setWdError(`Treasury withdrawal fault — ${error.message || "the node may be offline."}`);
    } finally {
      setWdBusy(false);
    }
  };

  const loadNexorone = async () => {
    const token = sessionStorage.getItem("portal_token");
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const res = await fetch(`${API}/portal/nexorone/status`, auth);
      if (res.ok) {
        const st = await res.json();
        setNexor(st);
        if (st.state === "UP") {
          for (const [path, setter] of [
            ["accounts", setNexorAccounts],
            ["balances", setNexorBalances],
            ["transactions", setNexorTxns],
          ]) {
            try {
              const r = await fetch(`${API}/portal/nexorone/${path}`, auth);
              if (r.ok) setter(await r.json());
            } catch {
              /* individual core-banking feeds non-blocking */
            }
          }
        }
      }
    } catch {
      /* nexorone status non-blocking */
    }
    try {
      const res = await fetch(`${API}/portal/nexorone/events`, auth);
      if (res.ok) {
        const data = await res.json();
        setNexorEvents(data.events || []);
      }
    } catch {
      /* settlement mirror feed non-blocking */
    }
  };

  const runMargin = async (e) => {
    e.preventDefault();
    setMarginResult(null);
    try {
      setMarginResult(
        await portalPost("margin-check", {
          tree_id: margin.tree_id,
          audited_yield_lbs: Number(margin.yield_lbs),
          baseline_spot_value: Number(margin.spot),
        })
      );
    } catch {
      setMarginResult({ Validation_Status: "TERMINAL_UNAVAILABLE", Action_Required: "Retry" });
    }
  };

  const runSwap = async (e) => {
    e.preventDefault();
    setSwapResult(null);
    try {
      setSwapResult(
        await portalPost("atomic-swap", {
          buyer_specie_tokens: Number(swap.buyer),
          seller_agri_tokens: Number(swap.seller),
          asset_type: swap.asset,
        })
      );
    } catch {
      setSwapResult({ Settlement_Status: "TERMINAL_UNAVAILABLE" });
    }
  };

  const runNetting = async (e) => {
    e.preventDefault();
    setNettingResult(null);
    try {
      setNettingResult(
        await portalPost("cross-netting", {
          counterparty_id: netting.counterparty,
          source_asset_type: netting.asset,
          metric_tons: Number(netting.tons),
          target_vault_line: netting.vault,
        })
      );
    } catch {
      setNettingResult({ status: "TERMINAL_UNAVAILABLE" });
    }
  };

  const runWarehouseQuery = async (e) => {
    e.preventDefault();
    setLineResult(null);
    setLineError("");
    try {
      const res = await fetch(
        `${API}/portal/warehouse-line/${encodeURIComponent(lineId.trim())}`,
        { headers: { Authorization: `Bearer ${sessionStorage.getItem("portal_token")}` } }
      );
      if (res.status === 401) {
        setLineError("Session expired — close and re-initialize the session.");
        return;
      }
      if (!res.ok) throw new Error("bad request");
      setLineResult(await res.json());
    } catch {
      setLineError("Ledger query failed — verify the line identifier and retry.");
    }
  };

  const runOracle = async (e) => {
    e.preventDefault();
    setOracleResult(null);
    try {
      const result = await portalPost("oracle-feed", {
        desk_id: oracle.desk,
        asset_ticker: oracle.ticker,
        spot_rate_usd: Number(oracle.rate),
      });
      setOracleResult(result);
      if (result.status === "ORACLE_MATRIX_UPDATED" && oracleMatrix) {
        setOracleMatrix({
          ...oracleMatrix,
          commodity_market_rates: {
            ...oracleMatrix.commodity_market_rates,
            [result.asset_ticker]: result.synchronized_rate_usd,
          },
        });
      }
    } catch {
      setOracleResult({ status: "TERMINAL_UNAVAILABLE" });
    }
  };

  const runReceiptRegister = async (e) => {
    e.preventDefault();
    setReceiptResult(null);
    setReceiptError("");
    try {
      const res = await fetch(`${API}/portal/receipts`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("portal_token")}`,
        },
        body: JSON.stringify({
          asset_class_code: receiptForm.asset,
          fractional_units_nominal: Number(receiptForm.units),
          asset_acquisition_cost_usd: Number(receiptForm.cost),
          degradation_haircut_bps: Number(receiptForm.haircut),
          is_unhypothecated: true,
          escrow_vault_id: receiptForm.vault,
          custom_parameters: receiptForm.params,
        }),
      });
      if (res.status === 401) {
        setReceiptError("Session expired — close and re-initialize the session.");
        return;
      }
      if (res.status === 409) {
        setReceiptError("Receipt already registered under this blind index hash.");
        return;
      }
      if (!res.ok) throw new Error("bad request");
      const created = await res.json();
      setReceiptResult(created);
      setReceipts((prev) => [created, ...prev]);
    } catch {
      setReceiptError("Receipt registration failed — verify the parameters and retry.");
    }
  };

  const runReceiptLookup = async (e) => {
    e.preventDefault();
    setLookupResult(null);
    setLookupError("");
    try {
      const res = await fetch(
        `${API}/portal/receipts/${encodeURIComponent(lookupHash.trim())}`,
        { headers: { Authorization: `Bearer ${sessionStorage.getItem("portal_token")}` } }
      );
      if (res.status === 401) {
        setLookupError("Session expired — close and re-initialize the session.");
        return;
      }
      if (res.status === 404) {
        setLookupError("No receipt registered under this blind index hash.");
        return;
      }
      if (!res.ok) throw new Error("bad request");
      setLookupResult(await res.json());
    } catch {
      setLookupError("Blind index query failed — verify the hash and retry.");
    }
  };

  const runSettleLifecycle = async () => {
    setSettleBusy(true);
    setSettleResult(null);
    setSettleError("");
    try {
      const res = await fetch(`${API}/portal/settle-lifecycle`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("portal_token")}`,
        },
        body: JSON.stringify({}),
      });
      if (res.status === 401) {
        setSettleError("Session expired — close and re-initialize the session.");
        return;
      }
      if (!res.ok) throw new Error("bad request");
      setSettleResult(await res.json());
    } catch {
      setSettleError("Ledger execution fault — the node may be offline or the vault depleted.");
    } finally {
      setSettleBusy(false);
    }
  };

  const runSmsPayment = async (e) => {
    e.preventDefault();
    setSmsResult(null);
    try {
      const result = await portalPost("sms-payment", {
        sender_id: smsForm.sender,
        recipient_id: smsForm.recipient,
        amount_usd: Number(smsForm.amount),
      });
      setSmsResult(result);
      if (result.ledger_event && fiatWallet) {
        setFiatWallet({
          ...fiatWallet,
          ledger_balances_susd: {
            ...fiatWallet.ledger_balances_susd,
            [smsForm.sender]: result.balance_sheet_updates.sender_remaining_balance_susd,
            [smsForm.recipient]: result.balance_sheet_updates.recipient_new_balance_susd,
          },
        });
      }
    } catch {
      setSmsResult({ status: "TERMINAL_UNAVAILABLE" });
    }
  };

  const runEscrowLookup = async (e) => {
    e.preventDefault();
    setEscrowResult(null);
    setEscrowError("");
    try {
      const res = await fetch(
        `${API}/portal/escrow/${encodeURIComponent(escrowId.trim())}`,
        { headers: { Authorization: `Bearer ${sessionStorage.getItem("portal_token")}` } }
      );
      if (res.status === 401) {
        setEscrowError("Session expired — close and re-initialize the session.");
        return;
      }
      if (!res.ok) throw new Error("bad request");
      setEscrowResult(await res.json());
    } catch {
      setEscrowError("Escrow query failed — verify the agreement identifier and retry.");
    }
  };

  const runEscrowLifecycle = async () => {
    setEscrowBusy(true);
    setEscrowLifecycle(null);
    setEscrowError("");
    try {
      const res = await fetch(`${API}/portal/escrow/lifecycle`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("portal_token")}`,
        },
        body: JSON.stringify({}),
      });
      if (res.status === 401) {
        setEscrowError("Session expired — close and re-initialize the session.");
        return;
      }
      if (!res.ok) throw new Error("bad request");
      setEscrowLifecycle(await res.json());
    } catch {
      setEscrowError("Escrow execution fault — the node may be offline.");
    } finally {
      setEscrowBusy(false);
    }
  };

  const runOnboarding = async (e) => {
    e.preventDefault();
    setOnbError("");
    setOnbResult(null);
    try {
      const res = await fetch(`${API}/portal/onboarding`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("portal_token")}`,
        },
        body: JSON.stringify({
          client_id: onb.client_id,
          note_face_value: Number(onb.face),
          rate_percent: Number(onb.rate),
          term_days: Number(onb.days),
        }),
      });
      if (res.status === 401) {
        setOnbError("Session expired — close and re-initialize the session.");
        return;
      }
      if (!res.ok) throw new Error("bad request");
      setOnbResult(await res.json());
    } catch {
      setOnbError("Check the origination figures and try again.");
    }
  };

  const loadGoldAndRecon = async () => {
    const token = sessionStorage.getItem("portal_token");
    try {
      const res = await fetch(`${API}/portal/gold-price`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setGold(await res.json());
    } catch {
      /* gold quote non-blocking */
    }
    try {
      const res = await fetch(`${API}/portal/recon`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setRecon(await res.json());
    } catch {
      /* recon non-blocking */
    }
    try {
      const res = await fetch(`${API}/portal/genesis`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setGenesis(await res.json());
    } catch {
      /* genesis non-blocking */
    }
    try {
      const res = await fetch(`${API}/portal/node-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const ns = await res.json();
        setNodeStatus(ns);
        const demoAid = ns?.deployment?.escrow_engine?.demo_agreement?.agreement_id;
        if (demoAid) setEscrowId(demoAid);
      }
    } catch {
      /* node status non-blocking */
    }
    try {
      const res = await fetch(`${API}/portal/node-handshake`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setHandshake(await res.json());
    } catch {
      /* handshake non-blocking */
    }
    try {
      const res = await fetch(`${API}/portal/oracle-matrix`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setOracleMatrix(await res.json());
    } catch {
      /* oracle matrix non-blocking */
    }
    try {
      const res = await fetch(`${API}/portal/ledger-schema`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setLedgerSchema(await res.json());
    } catch {
      /* ledger schema non-blocking */
    }
    try {
      const res = await fetch(`${API}/portal/receipts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setReceipts((await res.json()).receipts || []);
    } catch {
      /* receipts non-blocking */
    }
    try {
      const res = await fetch(`${API}/portal/fiat-wallet`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setFiatWallet(await res.json());
    } catch {
      /* fiat wallet non-blocking */
    }
    try {
      const res = await fetch(`${API}/portal/clearing-contract`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setContract(await res.json());
    } catch {
      /* contract non-blocking */
    }
  };

  const runMojave = async (e) => {
    e.preventDefault();
    setMojaveError("");
    setMojave(null);
    try {
      const res = await fetch(`${API}/portal/vault-distribution`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("portal_token")}`,
        },
        body: JSON.stringify({
          target_liabilities: Number(mojaveLiab),
          season_profile: mojaveSeason,
        }),
      });
      if (res.status === 401) {
        setMojaveError("Session expired — close and re-initialize the session.");
        return;
      }
      if (!res.ok) throw new Error("bad request");
      setMojave(await res.json());
    } catch {
      setMojaveError("Check the reserve figures and try again.");
    }
  };

  const runEquilibrium = async (e) => {
    e.preventDefault();
    setEqError("");
    setEquilibrium(null);
    try {
      const res = await fetch(`${API}/portal/reserve-equilibrium`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("portal_token")}`,
        },
        body: JSON.stringify({
          vault_specie: Number(eqInputs.vault_specie),
          crop_value: Number(eqInputs.crop_value),
          decay_percent: Number(eqInputs.decay_percent),
          netting_efficiency_percent: Number(eqInputs.netting_efficiency_percent),
          liabilities: Number(eqInputs.liabilities),
        }),
      });
      if (res.status === 401) {
        setEqError("Session expired — close and re-initialize the session.");
        return;
      }
      if (!res.ok) throw new Error("bad request");
      setEquilibrium(await res.json());
    } catch {
      setEqError("Check the reserve figures and try again.");
    }
  };

  const loadContracts = async () => {
    try {
      const res = await fetch(`${API}/portal/transit-contracts`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("portal_token")}` },
      });
      if (res.ok) {
        const data = await res.json();
        setContracts(data.contracts || []);
      }
    } catch {
      /* registry unavailable — non-blocking */
    }
  };

  const runAdvance = async (e) => {
    e.preventDefault();
    setAdvanceError("");
    setAdvance(null);
    try {
      const res = await fetch(`${API}/portal/advance`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("portal_token")}`,
        },
        body: JSON.stringify({
          face_value: Number(faceValue),
          rate_percent: Number(ratePercent),
          duration_days: Number(durationDays),
        }),
      });
      if (res.status === 401) {
        setAdvanceError("Session expired — close and re-initialize the session.");
        return;
      }
      if (!res.ok) throw new Error("bad request");
      setAdvance(await res.json());
    } catch {
      setAdvanceError("Check the facility figures and try again.");
    }
  };

  const loadLedger = async (token) => {
    const res = await fetch(`${API}/portal/ledger`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error("ledger unavailable");
    setLedger(await res.json());
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/portal/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ key_id: keyId, passphrase }),
      });
      if (res.status === 401) {
        setError("Invalid sovereign key or cipher pass.");
        return;
      }
      if (!res.ok) throw new Error("login failed");
      const data = await res.json();
      sessionStorage.setItem("portal_token", data.token);
      await loadLedger(data.token);
      loadContracts();
      loadGoldAndRecon();
      loadTreasury();
      loadNexorone();
      loadDocuments();
      loadRoster();
      loadFundingDesk();
      loadBackfills();
      loadRegistry();
      loadLedgerRoom();
      loadInfra();
      loadTcu();
      loadAssetEngine();
      loadCovenants();
      loadReserve();
      loadFloat();
      loadSolvency();
    } catch {
      setError("The clearing terminal is temporarily unavailable.");
    } finally {
      setBusy(false);
    }
  };

  const total = (rows) => rows.reduce((sum, r) => sum + r.value, 0);

  return (
    <section className="sovereign-portal-viewport" data-testid="portal-page">
      <div className="earthen-backdrop-wrapper">
        <img
          src="/assets/specie-vaulting-branded.jpg"
          alt="BGNZN Vault & Agroforestry Production Repository"
          className="fluid-matrix-img"
        />
      </div>

      <div className={`interactive-clearing-modal ${ledger ? "modal-wide" : ""}`}>
        <header className="modal-auth-header">
          <h1 className="brand-text-geo">BGNZN</h1>
          <p className="portal-sub-tag">Private Clearing Registry</p>
        </header>

        {!ledger ? (
          <form className="secure-auth-form" onSubmit={submit}>
            <div className="input-data-field">
              <label className="form-label-geo" htmlFor="portal-key">
                Sovereign Key Identifier
              </label>
              <input
                id="portal-key"
                type="text"
                value={keyId}
                onChange={(e) => setKeyId(e.target.value)}
                placeholder="Enter private ledger routing hash..."
                className="geo-terminal-input"
                data-testid="portal-key-input"
                required
              />
            </div>
            <div className="input-data-field">
              <label className="form-label-geo" htmlFor="portal-pass">
                Cryptographic Cipher Pass
              </label>
              <input
                id="portal-pass"
                type="password"
                value={passphrase}
                onChange={(e) => setPassphrase(e.target.value)}
                placeholder="••••••••••••••••"
                className="geo-terminal-input"
                data-testid="portal-pass-input"
                required
              />
            </div>
            <button
              type="submit"
              className="prime-clearing-btn"
              data-testid="portal-login-button"
              disabled={busy}
            >
              {busy ? "Opening Channel…" : "Initialize Settlement Session"}
            </button>
            {error && (
              <p className="portal-error" data-testid="portal-error">
                {error}
              </p>
            )}
          </form>
        ) : (
          <div data-testid="portal-ledger" className="space-y-5">
            <p className="portal-sub-tag" style={{ marginBottom: 0 }}>
              {ledger.label} · {ledger.key_id}
            </p>
            <div className="overflow-x-auto">
              <table className="portal-ledger-table" data-testid="ledger-assets-table">
                <thead>
                  <tr>
                    <th colSpan={3}>Assets</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.assets.map((row, i) => (
                    <tr key={i} data-testid={`ledger-asset-${i}`}>
                      <td>{row.name}</td>
                      <td style={{ color: "#8b94a0", fontSize: "0.75rem" }}>{row.detail}</td>
                      <td className="num">{fmt(row.value)}</td>
                    </tr>
                  ))}
                  <tr className="portal-ledger-total">
                    <td colSpan={2}>Total Assets</td>
                    <td className="num" data-testid="ledger-assets-total">
                      {fmt(total(ledger.assets))}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="overflow-x-auto">
              <table className="portal-ledger-table" data-testid="ledger-liabilities-table">
                <thead>
                  <tr>
                    <th colSpan={3}>Liabilities</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.liabilities.map((row, i) => (
                    <tr key={i} data-testid={`ledger-liability-${i}`}>
                      <td>{row.name}</td>
                      <td style={{ color: "#8b94a0", fontSize: "0.75rem" }}>{row.detail}</td>
                      <td className="num">{fmt(row.value)}</td>
                    </tr>
                  ))}
                  <tr className="portal-ledger-total">
                    <td colSpan={2}>Total Liabilities</td>
                    <td className="num" data-testid="ledger-liabilities-total">
                      {fmt(total(ledger.liabilities))}
                    </td>
                  </tr>
                  <tr className="portal-ledger-total">
                    <td colSpan={2}>Net Member Equity</td>
                    <td className="num" data-testid="ledger-equity">
                      {fmt(total(ledger.assets) - total(ledger.liabilities))}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            {ledger.demo && (
              <p className="portal-error" style={{ color: "#b0b7bd" }} data-testid="ledger-demo-note">
                {ledger.equity_note}
              </p>
            )}

            <form
              onSubmit={runAdvance}
              data-testid="advance-calculator"
              style={{
                borderTop: "1px solid rgba(176,183,189,0.2)",
                paddingTop: 20,
              }}
            >
              <p className="portal-sub-tag" style={{ marginBottom: 16, textAlign: "left" }}>
                Sovereign Up-Front Advance Terminal
              </p>
              <div className="input-data-field">
                <label className="form-label-geo" htmlFor="adv-face">
                  Note Face Value (USD)
                </label>
                <input
                  id="adv-face"
                  type="number"
                  min="1"
                  step="any"
                  value={faceValue}
                  onChange={(e) => setFaceValue(e.target.value)}
                  className="geo-terminal-input"
                  data-testid="advance-face-input"
                  required
                />
              </div>
              <div className="input-data-field">
                <label className="form-label-geo" htmlFor="adv-rate">
                  Fixed Up-Front Discount Rate (%)
                </label>
                <input
                  id="adv-rate"
                  type="number"
                  min="0"
                  max="100"
                  step="any"
                  value={ratePercent}
                  onChange={(e) => setRatePercent(e.target.value)}
                  className="geo-terminal-input"
                  data-testid="advance-rate-input"
                  required
                />
              </div>
              <div className="input-data-field">
                <label className="form-label-geo" htmlFor="adv-days">
                  Crop Cycle Duration (Days)
                </label>
                <input
                  id="adv-days"
                  type="number"
                  min="1"
                  max="3650"
                  value={durationDays}
                  onChange={(e) => setDurationDays(e.target.value)}
                  className="geo-terminal-input"
                  data-testid="advance-days-input"
                  required
                />
              </div>
              <button
                type="submit"
                className="prime-clearing-btn"
                data-testid="advance-compute-button"
              >
                Compute Advance
              </button>
              {advanceError && (
                <p className="portal-error" data-testid="advance-error">
                  {advanceError}
                </p>
              )}
            </form>

            {advance && (
              <table className="portal-ledger-table" data-testid="advance-result">
                <tbody>
                  <tr>
                    <td>Net Present Capital Advanced</td>
                    <td className="num" data-testid="advance-net">
                      {fmt(advance.Net_Present_Capital_Advanced)}
                    </td>
                  </tr>
                  <tr>
                    <td>Up-Front Yield Collected</td>
                    <td className="num" data-testid="advance-yield">
                      {fmt(advance.Upfront_Yield_Collected)}
                    </td>
                  </tr>
                  <tr className="portal-ledger-total">
                    <td>Trailing Interest Rate (Usury / Natural Law compliant)</td>
                    <td className="num" data-testid="advance-trailing">
                      {advance.Trailing_Interest_Rate.toFixed(2)}%
                    </td>
                  </tr>
                </tbody>
              </table>
            )}

            <form
              onSubmit={runEquilibrium}
              data-testid="equilibrium-monitor"
              style={{ borderTop: "1px solid rgba(176,183,189,0.2)", paddingTop: 20 }}
            >
              <p className="portal-sub-tag" style={{ marginBottom: 16, textAlign: "left" }}>
                Reserve Equilibrium Monitor
              </p>
              {[
                ["vault_specie", "Vault Specie (USD)", "eq-vault-input"],
                ["crop_value", "Living Crop Reserves (USD)", "eq-crop-input"],
                ["decay_percent", "Biological Decay (%)", "eq-decay-input"],
                ["netting_efficiency_percent", "Netting Efficiency (%)", "eq-netting-input"],
                ["liabilities", "Total Liabilities (USD)", "eq-liab-input"],
              ].map(([field, label, testid]) => (
                <div className="input-data-field" key={field}>
                  <label className="form-label-geo" htmlFor={testid}>
                    {label}
                  </label>
                  <input
                    id={testid}
                    type="number"
                    min="0"
                    step="any"
                    value={eqInputs[field]}
                    onChange={(e) => setEqInputs({ ...eqInputs, [field]: e.target.value })}
                    className="geo-terminal-input"
                    data-testid={testid}
                    required
                  />
                </div>
              ))}
              <button type="submit" className="prime-clearing-btn" data-testid="equilibrium-compute-button">
                Verify Reserve Equilibrium
              </button>
              {eqError && (
                <p className="portal-error" data-testid="equilibrium-error">
                  {eqError}
                </p>
              )}
            </form>

            {equilibrium && (
              <table className="portal-ledger-table" data-testid="equilibrium-result">
                <tbody>
                  <tr>
                    <td>Current Liquidity Ratio</td>
                    <td className="num" data-testid="equilibrium-ratio">
                      {equilibrium.Current_Liquidity_Ratio.toFixed(2)}×
                    </td>
                  </tr>
                  <tr className="portal-ledger-total">
                    <td>System Equilibrium Status (125% floor)</td>
                    <td
                      className="num"
                      data-testid="equilibrium-status"
                      style={{
                        color:
                          equilibrium.System_Equilibrium_Status === "STABLE" ? "#38e07c" : "#ff4455",
                      }}
                    >
                      {equilibrium.System_Equilibrium_Status}
                    </td>
                  </tr>
                </tbody>
              </table>
            )}

            <form
              onSubmit={runMojave}
              data-testid="mojave-simulator"
              style={{ borderTop: "1px solid rgba(176,183,189,0.2)", paddingTop: 20 }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
                <p className="portal-sub-tag" style={{ marginBottom: 0, textAlign: "left" }}>
                  Mojave Desert Reserves Simulator
                </p>
                {gold && (
                  <span
                    data-testid="gold-price-chip"
                    style={{
                      fontFamily: "ui-monospace, Menlo, monospace",
                      fontSize: "0.68rem",
                      color: gold.fallback ? "#ff6b76" : gold.stale ? "#e5c158" : "#38e07c",
                    }}
                  >
                    Au spot {fmt(gold.usd_per_gram)}/g · {gold.provider}
                    {gold.fallback ? " (static fallback)" : gold.stale ? " (stale)" : " (live)"}
                  </span>
                )}
              </div>
              <div className="input-data-field">
                <label className="form-label-geo" htmlFor="mojave-season">
                  Season Profile (Zone 9b)
                </label>
                <select
                  id="mojave-season"
                  value={mojaveSeason}
                  onChange={(e) => setMojaveSeason(e.target.value)}
                  className="geo-terminal-input"
                  data-testid="mojave-season-select"
                >
                  <option value="OPTIMAL_FALL_HARVEST">Optimal Fall Harvest — peak Medjool / pomegranate</option>
                  <option value="HIGH_SUMMER_HEAT_WAVE">High Summer Heat Wave — &gt;115°F stress</option>
                  <option value="WINTER_FROST_SHOCK">Winter Frost Shock — &lt;28°F citrus risk</option>
                </select>
              </div>
              <div className="input-data-field">
                <label className="form-label-geo" htmlFor="mojave-liab">
                  Target Liabilities (USD)
                </label>
                <input
                  id="mojave-liab"
                  type="number"
                  min="1"
                  step="any"
                  value={mojaveLiab}
                  onChange={(e) => setMojaveLiab(e.target.value)}
                  className="geo-terminal-input"
                  data-testid="mojave-liab-input"
                  required
                />
              </div>
              <button type="submit" className="prime-clearing-btn" data-testid="mojave-compute-button">
                Compute Vault Distribution
              </button>
              {mojaveError && (
                <p className="portal-error" data-testid="mojave-error">
                  {mojaveError}
                </p>
              )}
            </form>

            {mojave && (
              <table className="portal-ledger-table" data-testid="mojave-result">
                <tbody>
                  <tr>
                    <td>Gross Fruit Forest Value (Zone 9b matrix)</td>
                    <td className="num" data-testid="mojave-gross">
                      {fmt(mojave.Gross_Orchard_Value_USD)}
                    </td>
                  </tr>
                  <tr>
                    <td>Adjusted Ecological Reserve Cushion</td>
                    <td className="num" data-testid="mojave-adjusted">
                      {fmt(mojave.Adjusted_Agri_Reserves_USD)}
                    </td>
                  </tr>
                  <tr>
                    <td>Required Vault Gold (135% coverage floor)</td>
                    <td className="num" data-testid="mojave-gold-usd">
                      {fmt(mojave.Required_Vault_Gold_USD)}
                    </td>
                  </tr>
                  <tr className="portal-ledger-total">
                    <td>Optimal Vault Gold Allocation</td>
                    <td className="num" data-testid="mojave-gold-grams">
                      {mojave.Optimal_Vault_Gold_Grams.toLocaleString("en-US")} g Au
                    </td>
                  </tr>
                </tbody>
              </table>
            )}

            <form
              onSubmit={runOnboarding}
              data-testid="onboarding-engine"
              style={{ borderTop: "1px solid rgba(176,183,189,0.2)", paddingTop: 20 }}
            >
              <p className="portal-sub-tag" style={{ marginBottom: 16, textAlign: "left" }}>
                Onboarding Clearing Engine
              </p>
              <div className="input-data-field">
                <label className="form-label-geo" htmlFor="onb-client">
                  Client Sovereign ID
                </label>
                <input
                  id="onb-client"
                  type="text"
                  value={onb.client_id}
                  onChange={(e) => setOnb({ ...onb, client_id: e.target.value })}
                  className="geo-terminal-input"
                  data-testid="onb-client-input"
                  required
                />
              </div>
              <div className="input-data-field">
                <label className="form-label-geo" htmlFor="onb-face">
                  Promissory Note Face Value (USD)
                </label>
                <input
                  id="onb-face"
                  type="number"
                  min="1"
                  step="any"
                  value={onb.face}
                  onChange={(e) => setOnb({ ...onb, face: e.target.value })}
                  className="geo-terminal-input"
                  data-testid="onb-face-input"
                  required
                />
              </div>
              <div className="input-data-field">
                <label className="form-label-geo" htmlFor="onb-rate">
                  Up-Front Natural Clearing Rate (%)
                </label>
                <input
                  id="onb-rate"
                  type="number"
                  min="0"
                  max="100"
                  step="any"
                  value={onb.rate}
                  onChange={(e) => setOnb({ ...onb, rate: e.target.value })}
                  className="geo-terminal-input"
                  data-testid="onb-rate-input"
                  required
                />
              </div>
              <div className="input-data-field">
                <label className="form-label-geo" htmlFor="onb-days">
                  Facility Term (Days)
                </label>
                <input
                  id="onb-days"
                  type="number"
                  min="1"
                  max="3650"
                  value={onb.days}
                  onChange={(e) => setOnb({ ...onb, days: e.target.value })}
                  className="geo-terminal-input"
                  data-testid="onb-days-input"
                  required
                />
              </div>
              <button type="submit" className="prime-clearing-btn" data-testid="onb-execute-button">
                Execute Origination Block
              </button>
              {onbError && (
                <p className="portal-error" data-testid="onb-error">
                  {onbError}
                </p>
              )}
            </form>

            {onbResult && (
              <div
                data-testid="onb-result"
                style={{
                  background: "rgba(11,15,25,0.85)",
                  border: "1px solid rgba(56,224,124,0.35)",
                  borderLeft: "3px solid #38e07c",
                  borderRadius: 8,
                  padding: "14px 16px",
                  fontFamily: "ui-monospace, Menlo, monospace",
                  fontSize: "0.72rem",
                  lineHeight: 1.8,
                  color: "#b0b7bd",
                  overflowX: "auto",
                }}
              >
                <div style={{ color: "#38e07c" }}>
                  {onbResult.ledger_clearing_balances.settlement_finality_status}
                </div>
                <div>client: {onbResult.client_sovereign_id}</div>
                <div>
                  note face: {fmt(onbResult.contract_parameters.promissory_note_face_value_usd)} · rate{" "}
                  {onbResult.contract_parameters.upfront_discount_rate_annual} ·{" "}
                  {onbResult.contract_parameters.facility_term_days}d
                </div>
                <div>
                  cash advanced: {fmt(onbResult.ledger_clearing_balances.immediate_cash_advanced_usd)} ·
                  yield retained: {fmt(onbResult.ledger_clearing_balances.upfront_yield_retained_usd)} ·
                  trailing: {onbResult.ledger_clearing_balances.trailing_amortized_interest_rate.toFixed(4)}%
                </div>
                <div style={{ color: "#5f6b7a" }}>executed {onbResult.timestamp}</div>
              </div>
            )}

            <form
              onSubmit={runMargin}
              data-testid="margin-trigger"
              style={{ borderTop: "1px solid rgba(176,183,189,0.2)", paddingTop: 20 }}
            >
              <p className="portal-sub-tag" style={{ marginBottom: 16, textAlign: "left" }}>
                Yield Margin Fault Trigger
              </p>
              {[
                ["tree_id", "Tree / Token ID", "margin-tree-input", "text"],
                ["yield_lbs", "Audited Yield Per Tree (lbs)", "margin-yield-input", "number"],
                ["spot", "Baseline Spot Value (USD/lb)", "margin-spot-input", "number"],
              ].map(([field, label, testid, type]) => (
                <div className="input-data-field" key={field}>
                  <label className="form-label-geo" htmlFor={testid}>
                    {label}
                  </label>
                  <input
                    id={testid}
                    type={type}
                    min="0"
                    step="any"
                    value={margin[field]}
                    onChange={(e) => setMargin({ ...margin, [field]: e.target.value })}
                    className="geo-terminal-input"
                    data-testid={testid}
                    required
                  />
                </div>
              ))}
              <button type="submit" className="prime-clearing-btn" data-testid="margin-validate-button">
                Validate Margin Status
              </button>
            </form>

            {marginResult && (
              <div
                data-testid="margin-result"
                style={{
                  background: "rgba(11,15,25,0.85)",
                  border: `1px solid ${
                    marginResult.Validation_Status === "EQUILIBRIUM_VERIFIED"
                      ? "rgba(56,224,124,0.35)"
                      : "rgba(255,68,85,0.4)"
                  }`,
                  borderLeft: `3px solid ${
                    marginResult.Validation_Status === "EQUILIBRIUM_VERIFIED" ? "#38e07c" : "#ff4455"
                  }`,
                  borderRadius: 8,
                  padding: "14px 16px",
                  fontFamily: "ui-monospace, Menlo, monospace",
                  fontSize: "0.72rem",
                  lineHeight: 1.8,
                  color: "#b0b7bd",
                }}
              >
                <div
                  data-testid="margin-status"
                  style={{
                    color:
                      marginResult.Validation_Status === "EQUILIBRIUM_VERIFIED"
                        ? "#38e07c"
                        : "#ff4455",
                  }}
                >
                  {marginResult.Validation_Status}
                </div>
                {marginResult.Validation_Status === "COLLATERAL_FAULT_MARGIN_CALL" && (
                  <>
                    <div>audited: {marginResult.Audited_Yield_Per_Tree_LBS} lbs/tree · floor 150 lbs</div>
                    <div>deficit: {marginResult.Yield_Deficit_Per_Tree_LBS} lbs/tree</div>
                    <div style={{ color: "#e5c158" }}>
                      margin call: {fmt(marginResult.Required_Margin_Call_USD)}
                    </div>
                  </>
                )}
                <div>action: {marginResult.Action_Required}</div>
              </div>
            )}

            <form
              onSubmit={runSwap}
              data-testid="swap-clearing"
              style={{ borderTop: "1px solid rgba(176,183,189,0.2)", paddingTop: 20 }}
            >
              <p className="portal-sub-tag" style={{ marginBottom: 16, textAlign: "left" }}>
                Cross-Commodity Swap Clearing
              </p>
              <div className="input-data-field">
                <label className="form-label-geo" htmlFor="swap-asset">
                  Commodity Asset Class
                </label>
                <select
                  id="swap-asset"
                  value={swap.asset}
                  onChange={(e) => setSwap({ ...swap, asset: e.target.value })}
                  className="geo-terminal-input"
                  data-testid="swap-asset-select"
                >
                  <option value="Desert_Fruit_Yield">Desert Fruit Yield — 35% haircut</option>
                  <option value="Cacao_Bulk_Inventory">Cacao Bulk Inventory — 22% haircut</option>
                  <option value="Sugar_Refined_Bulk">Sugar Refined Bulk — 15% haircut</option>
                  <option value="Other_Commodity">Other Commodity — 25% default haircut</option>
                </select>
              </div>
              <div className="input-data-field">
                <label className="form-label-geo" htmlFor="swap-buyer">
                  Buyer Specie Tokens
                </label>
                <input
                  id="swap-buyer"
                  type="number"
                  min="0"
                  step="any"
                  value={swap.buyer}
                  onChange={(e) => setSwap({ ...swap, buyer: e.target.value })}
                  className="geo-terminal-input"
                  data-testid="swap-buyer-input"
                  required
                />
              </div>
              <div className="input-data-field">
                <label className="form-label-geo" htmlFor="swap-seller">
                  Seller Agri Tokens
                </label>
                <input
                  id="swap-seller"
                  type="number"
                  min="0"
                  step="any"
                  value={swap.seller}
                  onChange={(e) => setSwap({ ...swap, seller: e.target.value })}
                  className="geo-terminal-input"
                  data-testid="swap-seller-input"
                  required
                />
              </div>
              <button type="submit" className="prime-clearing-btn" data-testid="swap-execute-button">
                Execute Atomic Swap
              </button>
            </form>

            {swapResult && (
              <div
                data-testid="swap-result"
                style={{
                  background: "rgba(11,15,25,0.85)",
                  border: `1px solid ${
                    swapResult.Settlement_Status === "ATOMIC_SWAP_COMPLETE"
                      ? "rgba(56,224,124,0.35)"
                      : "rgba(255,68,85,0.4)"
                  }`,
                  borderLeft: `3px solid ${
                    swapResult.Settlement_Status === "ATOMIC_SWAP_COMPLETE" ? "#38e07c" : "#ff4455"
                  }`,
                  borderRadius: 8,
                  padding: "14px 16px",
                  fontFamily: "ui-monospace, Menlo, monospace",
                  fontSize: "0.72rem",
                  lineHeight: 1.8,
                  color: "#b0b7bd",
                }}
              >
                <div
                  data-testid="swap-status"
                  style={{
                    color:
                      swapResult.Settlement_Status === "ATOMIC_SWAP_COMPLETE"
                        ? "#38e07c"
                        : "#ff4455",
                  }}
                >
                  {swapResult.Settlement_Status}
                </div>
                {swapResult.Settlement_Status === "ATOMIC_SWAP_COMPLETE" && (
                  <>
                    <div>netted value exchanged: {fmt(swapResult.Netted_Value_Exchanged)}</div>
                    <div>
                      systemic counterparty risk:{" "}
                      {swapResult.Systemic_Counterparty_Risk.toFixed(4)}
                    </div>
                  </>
                )}
              </div>
            )}

            <form
              onSubmit={runNetting}
              data-testid="cross-netting-form"
              style={{ borderTop: "1px solid rgba(176,183,189,0.2)", paddingTop: 20 }}
            >
              <p className="portal-sub-tag" style={{ marginBottom: 16, textAlign: "left" }}>
                Multi-Commodity Cross-Netting Engine
              </p>
              <div className="input-data-field">
                <label className="form-label-geo" htmlFor="netting-counterparty">
                  Counterparty Sovereign ID
                </label>
                <input
                  id="netting-counterparty"
                  value={netting.counterparty}
                  onChange={(e) => setNetting({ ...netting, counterparty: e.target.value })}
                  className="geo-terminal-input"
                  data-testid="netting-counterparty-input"
                  required
                />
              </div>
              <div className="input-data-field">
                <label className="form-label-geo" htmlFor="netting-asset">
                  Source Commodity Class
                </label>
                <select
                  id="netting-asset"
                  value={netting.asset}
                  onChange={(e) => setNetting({ ...netting, asset: e.target.value })}
                  className="geo-terminal-input"
                  data-testid="netting-asset-select"
                >
                  <option value="Cacao_Bulk_MT">Cacao Bulk — $6,800/MT · 22% moisture haircut</option>
                  <option value="Sugar_Refined_MT">Sugar Refined — $540/MT · 15% storage haircut</option>
                </select>
              </div>
              <div className="input-data-field">
                <label className="form-label-geo" htmlFor="netting-tons">
                  Inventory Volume (Metric Tons)
                </label>
                <input
                  id="netting-tons"
                  type="number"
                  min="0"
                  step="any"
                  value={netting.tons}
                  onChange={(e) => setNetting({ ...netting, tons: e.target.value })}
                  className="geo-terminal-input"
                  data-testid="netting-tons-input"
                  required
                />
              </div>
              <div className="input-data-field">
                <label className="form-label-geo" htmlFor="netting-vault">
                  Target Vault Reserve Line
                </label>
                <input
                  id="netting-vault"
                  value={netting.vault}
                  onChange={(e) => setNetting({ ...netting, vault: e.target.value })}
                  className="geo-terminal-input"
                  data-testid="netting-vault-input"
                  required
                />
              </div>
              <button type="submit" className="prime-clearing-btn" data-testid="netting-execute-button">
                Execute Cross-Netting Trigger
              </button>
            </form>

            {nettingResult && (
              <div
                data-testid="netting-result"
                style={{
                  background: "rgba(11,15,25,0.85)",
                  border: `1px solid ${
                    nettingResult.status === "REJECTED" || nettingResult.status === "TERMINAL_UNAVAILABLE"
                      ? "rgba(255,68,85,0.4)"
                      : "rgba(56,224,124,0.35)"
                  }`,
                  borderLeft: `3px solid ${
                    nettingResult.status === "REJECTED" || nettingResult.status === "TERMINAL_UNAVAILABLE"
                      ? "#ff4455"
                      : "#38e07c"
                  }`,
                  borderRadius: 8,
                  padding: "14px 16px",
                  fontFamily: "ui-monospace, Menlo, monospace",
                  fontSize: "0.72rem",
                  lineHeight: 1.8,
                  color: "#b0b7bd",
                  overflowX: "auto",
                }}
              >
                {nettingResult.status === "REJECTED" || nettingResult.status === "TERMINAL_UNAVAILABLE" ? (
                  <div data-testid="netting-status" style={{ color: "#ff4455" }}>
                    {nettingResult.status}
                    {nettingResult.reason ? ` — ${nettingResult.reason}` : ""}
                  </div>
                ) : (
                  <>
                    <div data-testid="netting-status" style={{ color: "#38e07c" }}>
                      {nettingResult.atomic_settlement_instructions.clearing_status}
                    </div>
                    <div style={{ wordBreak: "break-all", color: "#5bc6e9" }}>
                      tx: {nettingResult.transaction_metadata.ledger_tx_hash}
                    </div>
                    <div>
                      counterparty: {nettingResult.transaction_metadata.counterparty_sovereign_id} ·
                      cleared: {fmt(nettingResult.netting_computations.net_settlement_value_usd)} (gross{" "}
                      {fmt(nettingResult.netting_computations.gross_inventory_value_usd)} · haircut{" "}
                      {nettingResult.netting_computations.applied_risk_haircut})
                    </div>
                    {nettingResult.netting_computations.rate_source && (
                      <div data-testid="netting-rate-source">
                        rates: {nettingResult.netting_computations.rate_source} · commodity @ $
                        {Number(nettingResult.netting_computations.commodity_rate_usd).toLocaleString("en-US")}
                        {" "}· gold @ ${nettingResult.netting_computations.gold_spot_usd_per_gram}/g
                      </div>
                    )}
                    <div style={{ color: "#e5c158" }}>
                      gold specie settlement:{" "}
                      {nettingResult.atomic_settlement_instructions.gold_specie_to_mint_or_release_grams}{" "}
                      g $gAu → {nettingResult.atomic_settlement_instructions.target_vault_reserve_line}
                    </div>
                    <div>
                      escrow: {nettingResult.atomic_settlement_instructions.asset_to_burn_or_escrow} ·
                      finality: {nettingResult.atomic_settlement_instructions.settlement_lateness_window}
                    </div>
                  </>
                )}
              </div>
            )}

            <div
              data-testid="oracle-core-panel"
              style={{ borderTop: "1px solid rgba(176,183,189,0.2)", paddingTop: 20 }}
            >
              <p className="portal-sub-tag" style={{ marginBottom: 16, textAlign: "left" }}>
                Sovereign Private Oracle Core
              </p>
              {oracleMatrix && (
                <div
                  data-testid="oracle-matrix"
                  style={{
                    background: "rgba(11,15,25,0.85)",
                    border: "1px solid rgba(176,183,189,0.25)",
                    borderRadius: 8,
                    padding: "14px 16px",
                    fontFamily: "ui-monospace, Menlo, monospace",
                    fontSize: "0.72rem",
                    lineHeight: 1.8,
                    color: "#b0b7bd",
                    overflowX: "auto",
                    marginBottom: 16,
                  }}
                >
                  <div style={{ color: "#e5c158" }}>
                    master spot matrix · {oracleMatrix.validation_method}
                  </div>
                  <div>
                    desks: {oracleMatrix.authorized_broker_desks.join(" · ")}
                  </div>
                  <div style={{ marginTop: 6 }}>
                    {Object.entries(oracleMatrix.commodity_market_rates).map(([ticker, rate]) => (
                      <div key={ticker}>
                        {ticker}: <span style={{ color: "#5bc6e9" }}>${Number(rate).toLocaleString("en-US")}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <form onSubmit={runOracle} data-testid="oracle-feed-form">
                <div className="input-data-field">
                  <label className="form-label-geo" htmlFor="oracle-desk">
                    Authorized Broker Desk
                  </label>
                  <select
                    id="oracle-desk"
                    value={oracle.desk}
                    onChange={(e) => setOracle({ ...oracle, desk: e.target.value })}
                    className="geo-terminal-input"
                    data-testid="oracle-desk-select"
                  >
                    <option value="DESK_01_LONDON_OTC">DESK_01_LONDON_OTC</option>
                    <option value="DESK_02_ZURICH_METALS">DESK_02_ZURICH_METALS</option>
                  </select>
                </div>
                <div className="input-data-field">
                  <label className="form-label-geo" htmlFor="oracle-ticker">
                    Asset Ticker
                  </label>
                  <select
                    id="oracle-ticker"
                    value={oracle.ticker}
                    onChange={(e) => setOracle({ ...oracle, ticker: e.target.value })}
                    className="geo-terminal-input"
                    data-testid="oracle-ticker-select"
                  >
                    {oracleMatrix &&
                      Object.keys(oracleMatrix.commodity_market_rates).map((ticker) => (
                        <option key={ticker} value={ticker}>
                          {ticker}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="input-data-field">
                  <label className="form-label-geo" htmlFor="oracle-rate">
                    New Spot Rate (USD)
                  </label>
                  <input
                    id="oracle-rate"
                    type="number"
                    min="0"
                    step="any"
                    value={oracle.rate}
                    onChange={(e) => setOracle({ ...oracle, rate: e.target.value })}
                    className="geo-terminal-input"
                    data-testid="oracle-rate-input"
                    required
                  />
                </div>
                <button type="submit" className="prime-clearing-btn" data-testid="oracle-submit-button">
                  Sign & Submit Oracle Feed
                </button>
              </form>
              {oracleResult && (
                <div
                  data-testid="oracle-result"
                  style={{
                    background: "rgba(11,15,25,0.85)",
                    border: `1px solid ${
                      oracleResult.status === "ORACLE_MATRIX_UPDATED"
                        ? "rgba(56,224,124,0.35)"
                        : "rgba(255,68,85,0.4)"
                    }`,
                    borderLeft: `3px solid ${
                      oracleResult.status === "ORACLE_MATRIX_UPDATED" ? "#38e07c" : "#ff4455"
                    }`,
                    borderRadius: 8,
                    padding: "14px 16px",
                    fontFamily: "ui-monospace, Menlo, monospace",
                    fontSize: "0.72rem",
                    lineHeight: 1.8,
                    color: "#b0b7bd",
                    overflowX: "auto",
                    marginTop: 16,
                  }}
                >
                  <div
                    data-testid="oracle-status"
                    style={{
                      color: oracleResult.status === "ORACLE_MATRIX_UPDATED" ? "#38e07c" : "#ff4455",
                    }}
                  >
                    {oracleResult.status}
                    {oracleResult.reason ? ` — ${oracleResult.reason}` : ""}
                  </div>
                  {oracleResult.status === "ORACLE_MATRIX_UPDATED" && (
                    <>
                      <div>
                        {oracleResult.asset_ticker} synchronized @ $
                        {Number(oracleResult.synchronized_rate_usd).toLocaleString("en-US")}
                      </div>
                      <div style={{ color: "#5bc6e9", wordBreak: "break-all" }}>
                        hmac: {oracleResult.signature}
                      </div>
                      <div>{oracleResult.clearing_condition}</div>
                    </>
                  )}
                </div>
              )}
            </div>

            <div
              data-testid="receipt-registry-panel"
              style={{ borderTop: "1px solid rgba(176,183,189,0.2)", paddingTop: 20 }}
            >
              <p className="portal-sub-tag" style={{ marginBottom: 16, textAlign: "left" }}>
                Warehouse Receipt Repository · BOURG MERCHANT EXCHANGE Ledger
              </p>
              <form onSubmit={runReceiptRegister} data-testid="receipt-register-form">
                <div className="input-data-field">
                  <label className="form-label-geo" htmlFor="receipt-asset">
                    Asset Class Code
                  </label>
                  <select
                    id="receipt-asset"
                    value={receiptForm.asset}
                    onChange={(e) => setReceiptForm({ ...receiptForm, asset: e.target.value })}
                    className="geo-terminal-input"
                    data-testid="receipt-asset-select"
                  >
                    {["AU", "AG", "CU", "PD", "RH", "DT", "CC", "SG", "ABDP"].map((code) => (
                      <option key={code} value={code}>
                        {code}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="input-data-field">
                  <label className="form-label-geo" htmlFor="receipt-units">
                    Fractional Units Nominal
                  </label>
                  <input
                    id="receipt-units"
                    type="number"
                    min="0"
                    step="any"
                    value={receiptForm.units}
                    onChange={(e) => setReceiptForm({ ...receiptForm, units: e.target.value })}
                    className="geo-terminal-input"
                    data-testid="receipt-units-input"
                    required
                  />
                </div>
                <div className="input-data-field">
                  <label className="form-label-geo" htmlFor="receipt-cost">
                    Acquisition Cost (USD)
                  </label>
                  <input
                    id="receipt-cost"
                    type="number"
                    min="0"
                    step="any"
                    value={receiptForm.cost}
                    onChange={(e) => setReceiptForm({ ...receiptForm, cost: e.target.value })}
                    className="geo-terminal-input"
                    data-testid="receipt-cost-input"
                    required
                  />
                </div>
                <div className="input-data-field">
                  <label className="form-label-geo" htmlFor="receipt-haircut">
                    Degradation Haircut (bps)
                  </label>
                  <input
                    id="receipt-haircut"
                    type="number"
                    min="0"
                    max="10000"
                    value={receiptForm.haircut}
                    onChange={(e) => setReceiptForm({ ...receiptForm, haircut: e.target.value })}
                    className="geo-terminal-input"
                    data-testid="receipt-haircut-input"
                    required
                  />
                </div>
                <div className="input-data-field">
                  <label className="form-label-geo" htmlFor="receipt-vault">
                    Escrow Vault Identifier
                  </label>
                  <input
                    id="receipt-vault"
                    value={receiptForm.vault}
                    onChange={(e) => setReceiptForm({ ...receiptForm, vault: e.target.value })}
                    className="geo-terminal-input"
                    data-testid="receipt-vault-input"
                    required
                  />
                </div>
                <div className="input-data-field">
                  <label className="form-label-geo" htmlFor="receipt-params">
                    Custom Field Parameters (AES-256 sealed)
                  </label>
                  <textarea
                    id="receipt-params"
                    rows={2}
                    value={receiptForm.params}
                    onChange={(e) => setReceiptForm({ ...receiptForm, params: e.target.value })}
                    className="geo-terminal-input"
                    data-testid="receipt-params-input"
                    required
                  />
                </div>
                <button type="submit" className="prime-clearing-btn" data-testid="receipt-register-button">
                  Register Warehouse Receipt
                </button>
              </form>
              {receiptError && (
                <p className="portal-error" data-testid="receipt-error">
                  {receiptError}
                </p>
              )}
              {receiptResult && (
                <div
                  data-testid="receipt-result"
                  style={{
                    background: "rgba(11,15,25,0.85)",
                    border: "1px solid rgba(56,224,124,0.35)",
                    borderLeft: "3px solid #38e07c",
                    borderRadius: 8,
                    padding: "14px 16px",
                    fontFamily: "ui-monospace, Menlo, monospace",
                    fontSize: "0.72rem",
                    lineHeight: 1.8,
                    color: "#b0b7bd",
                    overflowX: "auto",
                    marginTop: 16,
                  }}
                >
                  <div data-testid="receipt-status" style={{ color: "#38e07c" }}>
                    {receiptResult.registry_status}
                  </div>
                  <div style={{ wordBreak: "break-all", color: "#5bc6e9" }}>
                    blind hash: {receiptResult.receipt_blind_hash}
                  </div>
                  <div>
                    class: {receiptResult.asset_class_code} · units:{" "}
                    {Number(receiptResult.fractional_units_nominal).toLocaleString("en-US")} · cost:{" "}
                    {fmt(receiptResult.asset_acquisition_cost_usd)} · haircut:{" "}
                    {receiptResult.degradation_haircut_bps} bps · unhypothecated:{" "}
                    {receiptResult.is_unhypothecated ? "yes" : "no"}
                  </div>
                  <div>ledger state block: #{receiptResult.ledger_state_block_height}</div>
                </div>
              )}
              <form
                onSubmit={runReceiptLookup}
                data-testid="receipt-lookup-form"
                style={{ marginTop: 20 }}
              >
                <div className="input-data-field">
                  <label className="form-label-geo" htmlFor="receipt-lookup-hash">
                    Blind Index Hash Query (no sequence timelines leaked)
                  </label>
                  <input
                    id="receipt-lookup-hash"
                    value={lookupHash}
                    onChange={(e) => setLookupHash(e.target.value)}
                    className="geo-terminal-input"
                    data-testid="receipt-lookup-input"
                    placeholder="0x… or 64-char hex"
                    spellCheck={false}
                    required
                  />
                </div>
                <button type="submit" className="prime-clearing-btn" data-testid="receipt-lookup-button">
                  Query Blind Index & Unseal Payload
                </button>
              </form>
              {lookupError && (
                <p className="portal-error" data-testid="receipt-lookup-error">
                  {lookupError}
                </p>
              )}
              {lookupResult && (
                <div
                  data-testid="receipt-lookup-result"
                  style={{
                    background: "rgba(11,15,25,0.85)",
                    border: "1px solid rgba(91,198,233,0.3)",
                    borderLeft: "3px solid #5bc6e9",
                    borderRadius: 8,
                    padding: "14px 16px",
                    fontFamily: "ui-monospace, Menlo, monospace",
                    fontSize: "0.72rem",
                    lineHeight: 1.8,
                    color: "#b0b7bd",
                    overflowX: "auto",
                    marginTop: 16,
                  }}
                >
                  <div data-testid="receipt-lookup-integrity" style={{ color: "#5bc6e9" }}>
                    {lookupResult.payload_integrity}
                  </div>
                  <div>
                    class: {lookupResult.asset_class_code} · units:{" "}
                    {Number(lookupResult.fractional_units_nominal).toLocaleString("en-US")} · block: #
                    {lookupResult.ledger_state_block_height}
                  </div>
                  <div style={{ color: "#e5c158" }}>
                    unsealed parameters: {lookupResult.decrypted_parameters.custom_parameters}
                  </div>
                  <div style={{ color: "#8b94a0" }}>
                    registered by {lookupResult.registered_by} · vault hash:{" "}
                    {lookupResult.escrow_vault_id_hash.slice(0, 24)}…
                  </div>
                </div>
              )}
              {receipts.length > 0 && (
                <div
                  data-testid="receipt-list"
                  style={{
                    marginTop: 20,
                    background: "rgba(11,15,25,0.85)",
                    border: "1px solid rgba(176,183,189,0.25)",
                    borderRadius: 8,
                    padding: "14px 16px",
                    fontFamily: "ui-monospace, Menlo, monospace",
                    fontSize: "0.72rem",
                    lineHeight: 1.8,
                    color: "#b0b7bd",
                    overflowX: "auto",
                  }}
                >
                  <div style={{ color: "#e5c158" }}>
                    registered receipts: {receipts.length} (payloads sealed)
                  </div>
                  {receipts.map((r) => (
                    <div key={r.receipt_blind_hash} style={{ wordBreak: "break-all" }}>
                      <span style={{ color: "#5bc6e9" }}>{r.receipt_blind_hash.slice(0, 20)}…</span>{" "}
                      {r.asset_class_code} · {Number(r.fractional_units_nominal).toLocaleString("en-US")}{" "}
                      units · {fmt(r.asset_acquisition_cost_usd)} · block #{r.ledger_state_block_height}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div
              data-testid="fiat-wallet-panel"
              style={{ borderTop: "1px solid rgba(176,183,189,0.2)", paddingTop: 20 }}
            >
              <p className="portal-sub-tag" style={{ marginBottom: 16, textAlign: "left" }}>
                Private Fiat Wallet · $sUSD SMS Clearing
              </p>
              {fiatWallet && (
                <div
                  data-testid="fiat-wallet-balances"
                  style={{
                    background: "rgba(11,15,25,0.85)",
                    border: "1px solid rgba(176,183,189,0.25)",
                    borderRadius: 8,
                    padding: "14px 16px",
                    fontFamily: "ui-monospace, Menlo, monospace",
                    fontSize: "0.72rem",
                    lineHeight: 1.8,
                    color: "#b0b7bd",
                    overflowX: "auto",
                    marginBottom: 16,
                  }}
                >
                  <div style={{ color: "#e5c158" }}>
                    {fiatWallet.token_pair} · {fiatWallet.settlement_standard}
                  </div>
                  {Object.entries(fiatWallet.ledger_balances_susd).map(([entity, balance]) => (
                    <div key={entity}>
                      {entity}:{" "}
                      <span style={{ color: "#38e07c" }}>
                        ${Number(balance).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                      </span>{" "}
                      sUSD
                    </div>
                  ))}
                </div>
              )}
              <form onSubmit={runSmsPayment} data-testid="sms-payment-form">
                <div className="input-data-field">
                  <label className="form-label-geo" htmlFor="sms-sender">
                    Sender Member Key
                  </label>
                  <input
                    id="sms-sender"
                    value={smsForm.sender}
                    onChange={(e) => setSmsForm({ ...smsForm, sender: e.target.value })}
                    className="geo-terminal-input"
                    data-testid="sms-sender-input"
                    required
                  />
                </div>
                <div className="input-data-field">
                  <label className="form-label-geo" htmlFor="sms-recipient">
                    Recipient Member Key
                  </label>
                  <input
                    id="sms-recipient"
                    value={smsForm.recipient}
                    onChange={(e) => setSmsForm({ ...smsForm, recipient: e.target.value })}
                    className="geo-terminal-input"
                    data-testid="sms-recipient-input"
                    required
                  />
                </div>
                <div className="input-data-field">
                  <label className="form-label-geo" htmlFor="sms-amount">
                    Transfer Volume (USD)
                  </label>
                  <input
                    id="sms-amount"
                    type="number"
                    min="0"
                    step="any"
                    value={smsForm.amount}
                    onChange={(e) => setSmsForm({ ...smsForm, amount: e.target.value })}
                    className="geo-terminal-input"
                    data-testid="sms-amount-input"
                    required
                  />
                </div>
                <button type="submit" className="prime-clearing-btn" data-testid="sms-execute-button">
                  Execute SMS Text Payment
                </button>
                <a
                  href={SMS_PAY_URL}
                  data-testid="sms-ios-cta"
                  className="prime-clearing-btn"
                  style={{ marginTop: 12, display: "block", textAlign: "center", textDecoration: "none" }}
                >
                  Text a Payment from iPhone (iOS P2P)
                </a>
              </form>
              {smsResult && (
                <div
                  data-testid="sms-result"
                  style={{
                    background: "rgba(11,15,25,0.85)",
                    border: `1px solid ${
                      smsResult.ledger_event ? "rgba(56,224,124,0.35)" : "rgba(255,68,85,0.4)"
                    }`,
                    borderLeft: `3px solid ${smsResult.ledger_event ? "#38e07c" : "#ff4455"}`,
                    borderRadius: 8,
                    padding: "14px 16px",
                    fontFamily: "ui-monospace, Menlo, monospace",
                    fontSize: "0.72rem",
                    lineHeight: 1.8,
                    color: "#b0b7bd",
                    overflowX: "auto",
                    marginTop: 16,
                  }}
                >
                  {smsResult.ledger_event ? (
                    <>
                      <div data-testid="sms-status" style={{ color: "#38e07c" }}>
                        {smsResult.ledger_event.status}
                      </div>
                      <div style={{ wordBreak: "break-all", color: "#5bc6e9" }}>
                        tx: {smsResult.ledger_event.transaction_hash}
                      </div>
                      <div>
                        sender remaining: $
                        {Number(
                          smsResult.balance_sheet_updates.sender_remaining_balance_susd
                        ).toLocaleString("en-US", { minimumFractionDigits: 2 })}{" "}
                        · recipient new: $
                        {Number(
                          smsResult.balance_sheet_updates.recipient_new_balance_susd
                        ).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                      </div>
                      <div>
                        trailing usury fee:{" "}
                        {smsResult.balance_sheet_updates.trailing_usury_fee.toFixed(4)}
                      </div>
                    </>
                  ) : (
                    <div data-testid="sms-status" style={{ color: "#ff4455" }}>
                      {smsResult.status}
                      {smsResult.reason ? ` — ${smsResult.reason}` : ""}
                    </div>
                  )}
                </div>
              )}
            </div>

            {recon && (
              <div
                data-testid="recon-card"
                style={{ borderTop: "1px solid rgba(176,183,189,0.2)", paddingTop: 20 }}
              >
                <p className="portal-sub-tag" style={{ marginBottom: 16, textAlign: "left" }}>
                  Proof-of-Reserve Reconciliation
                </p>
                <div
                  style={{
                    background: "rgba(11,15,25,0.85)",
                    border: "1px solid rgba(176,183,189,0.25)",
                    borderRadius: 8,
                    padding: "14px 16px",
                    fontFamily: "ui-monospace, Menlo, monospace",
                    fontSize: "0.72rem",
                    lineHeight: 1.8,
                    color: "#b0b7bd",
                    overflowX: "auto",
                  }}
                >
                  <div style={{ color: "#e5c158" }}>{recon.repository_identifier}</div>
                  <div>
                    cycle: {recon.accounting_cycle_period} · node:{" "}
                    {recon.proof_of_reserve_validation.telemetry_source_node}
                  </div>
                  <div>method: {recon.proof_of_reserve_validation.verification_method}</div>
                  <div style={{ wordBreak: "break-all" }}>
                    ecdsa: {recon.proof_of_reserve_validation.signature_block_ecdsa.slice(0, 34)}…
                  </div>
                  <div style={{ marginTop: 6, color: "#8b94a0" }}>
                    {Object.entries(recon.earthen_asset_inventory).map(([key, asset]) => (
                      <div key={key}>
                        {asset.variety_profile || key}: {asset.tree_count ?? "—"} trees ·{" "}
                        {asset.audited_harvest_weight_lbs ??
                          (asset.fragaria_ananassa_lbs || 0) + (asset.solanum_lycopersicum_lbs || 0)}{" "}
                        lbs audited · token {asset.assigned_unit_token}
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: 6 }}>
                    capex: {fmt(recon.ledger_rebalancing_parameters.total_capital_expenditure_usd)} ·
                    intrinsic value:{" "}
                    {fmt(recon.ledger_rebalancing_parameters.calculated_current_intrinsic_value_usd)}
                  </div>
                  <div style={{ color: "#38e07c" }}>
                    {recon.ledger_rebalancing_parameters.clearing_status}
                  </div>
                </div>
              </div>
            )}

            <div
              data-testid="placement-memorandum"
              style={{ borderTop: "1px solid rgba(176,183,189,0.2)", paddingTop: 20 }}
            >
              <p className="portal-sub-tag" style={{ marginBottom: 16, textAlign: "left" }}>
                Private Sovereign Placement Memorandum · $abDP
              </p>
              <div className="space-y-4">
                {PLACEMENT_ARTICLES.map((article) => (
                  <div
                    key={article.id}
                    data-testid={`article-${article.id}`}
                    style={{
                      background: "rgba(11,15,25,0.85)",
                      border: "1px solid rgba(91,198,233,0.25)",
                      borderLeft: "3px solid #5bc6e9",
                      borderRadius: 8,
                      padding: "14px 16px",
                    }}
                  >
                    <div
                      style={{
                        color: "#5bc6e9",
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "1px",
                        marginBottom: 6,
                      }}
                    >
                      Article {article.id} — {article.title}
                    </div>
                    <p style={{ color: "#b0b7bd", fontSize: "0.78rem", lineHeight: 1.7, margin: 0 }}>
                      {article.body}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {genesis && (
              <div
                data-testid="genesis-card"
                style={{ borderTop: "1px solid rgba(176,183,189,0.2)", paddingTop: 20 }}
              >
                <p className="portal-sub-tag" style={{ marginBottom: 16, textAlign: "left" }}>
                  Sovereign Node Genesis · Chain {genesis.config.chainId}
                </p>
                <div
                  style={{
                    background: "rgba(11,15,25,0.85)",
                    border: "1px solid rgba(176,183,189,0.25)",
                    borderRadius: 8,
                    padding: "14px 16px",
                    fontFamily: "ui-monospace, Menlo, monospace",
                    fontSize: "0.72rem",
                    lineHeight: 1.8,
                    color: "#b0b7bd",
                    overflowX: "auto",
                  }}
                >
                  <div style={{ color: "#e5c158" }}>
                    clique PoA · {genesis.config.clique.period}s block period · epoch{" "}
                    {genesis.config.clique.epoch}
                  </div>
                  <div style={{ wordBreak: "break-all" }}>
                    node: bgnzn_private_repository_sovereign_node_01_mojave_9b
                  </div>
                  <div style={{ marginTop: 6 }}>
                    {Object.entries(genesis.alloc).map(([address, entry]) => (
                      <div key={address} style={{ wordBreak: "break-all" }}>
                        <span style={{ color: "#5bc6e9" }}>{address.slice(0, 12)}…</span>{" "}
                        {entry.comment}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {contract && (
              <div
                data-testid="clearing-contract-card"
                style={{ borderTop: "1px solid rgba(176,183,189,0.2)", paddingTop: 20 }}
              >
                <p className="portal-sub-tag" style={{ marginBottom: 16, textAlign: "left" }}>
                  Clearing Engine Contract · Solidity ^0.8.20
                </p>
                <div
                  style={{
                    background: "rgba(11,15,25,0.85)",
                    border: "1px solid rgba(176,183,189,0.25)",
                    borderRadius: 8,
                    padding: "14px 16px",
                    fontFamily: "ui-monospace, Menlo, monospace",
                    fontSize: "0.72rem",
                    lineHeight: 1.8,
                    color: "#b0b7bd",
                    overflowX: "auto",
                  }}
                >
                  <div style={{ color: "#e5c158" }}>{contract.contract_name}</div>
                  <div style={{ wordBreak: "break-all" }}>
                    binding: {contract.genesis_binding}
                  </div>
                  <div>{contract.genesis_binding_role}</div>
                  <div>collateral accepted: {contract.accepted_collateral_tokens.join(" · ")}</div>
                  <div>entrypoint: {contract.entrypoint}</div>
                  <div
                    data-testid="contract-compile-status"
                    style={{ color: "#38e07c", marginTop: 4 }}
                  >
                    {contract.compile_status}
                  </div>
                  {contract.deployment && (
                    <div
                      data-testid="contract-deployment"
                      style={{ color: "#5bc6e9", wordBreak: "break-all" }}
                    >
                      deployed: {contract.deployment.address} · block{" "}
                      {contract.deployment.deploy_block} · settlement ${contract.deployment.settlement_token_symbol}{" "}
                      @ {contract.deployment.settlement_token}
                    </div>
                  )}
                  {contract.compile_notes.map((note, i) => (
                    <div key={i} style={{ color: "#8b94a0", marginTop: 4 }}>
                      note: {note}
                    </div>
                  ))}
                  <button
                    onClick={() => setContractOpen(!contractOpen)}
                    data-testid="contract-source-toggle"
                    className="prime-clearing-btn"
                    style={{ marginTop: 12, padding: "10px" }}
                  >
                    {contractOpen ? "Seal Contract Source" : "View Contract Source"}
                  </button>
                  {contractOpen && (
                    <pre
                      data-testid="contract-source"
                      style={{
                        marginTop: 12,
                        maxHeight: 320,
                        overflowY: "auto",
                        background: "rgba(6,13,22,0.9)",
                        border: "1px solid rgba(229,193,88,0.2)",
                        borderRadius: 6,
                        padding: 12,
                        fontSize: "0.68rem",
                        lineHeight: 1.6,
                        whiteSpace: "pre",
                      }}
                    >
                      {contract.source}
                    </pre>
                  )}
                </div>
              </div>
            )}

            {ledgerSchema && (
              <div
                data-testid="ledger-schema-card"
                style={{ borderTop: "1px solid rgba(176,183,189,0.2)", paddingTop: 20 }}
              >
                <p className="portal-sub-tag" style={{ marginBottom: 16, textAlign: "left" }}>
                  Private Ledger Schema · {ledgerSchema.schema_name}
                </p>
                <div
                  style={{
                    background: "rgba(11,15,25,0.85)",
                    border: "1px solid rgba(176,183,189,0.25)",
                    borderRadius: 8,
                    padding: "14px 16px",
                    fontFamily: "ui-monospace, Menlo, monospace",
                    fontSize: "0.72rem",
                    lineHeight: 1.8,
                    color: "#b0b7bd",
                    overflowX: "auto",
                  }}
                >
                  <div style={{ color: "#e5c158" }}>
                    tables: {ledgerSchema.tables.join(" · ")}
                  </div>
                  <div>
                    asset classes: {ledgerSchema.asset_class_codes.join(" · ")}
                  </div>
                  <div style={{ color: "#8b94a0" }}>{ledgerSchema.engine_target}</div>
                  <button
                    onClick={() => setSchemaOpen(!schemaOpen)}
                    data-testid="ledger-schema-toggle"
                    className="prime-clearing-btn"
                    style={{ marginTop: 12, padding: "10px" }}
                  >
                    {schemaOpen ? "Seal Schema DDL" : "View Schema DDL"}
                  </button>
                  {schemaOpen && (
                    <pre
                      data-testid="ledger-schema-ddl"
                      style={{
                        marginTop: 12,
                        maxHeight: 320,
                        overflowY: "auto",
                        background: "rgba(6,13,22,0.9)",
                        border: "1px solid rgba(229,193,88,0.2)",
                        borderRadius: 6,
                        padding: 12,
                        fontSize: "0.68rem",
                        lineHeight: 1.6,
                        whiteSpace: "pre",
                      }}
                    >
                      {ledgerSchema.ddl}
                    </pre>
                  )}
                </div>
              </div>
            )}

            {nodeStatus && (
              <div
                data-testid="node-bootstrap-card"
                style={{
                  background: "rgba(11,15,25,0.85)",
                  border: "1px solid rgba(176,183,189,0.25)",
                  borderRadius: 8,
                  padding: "14px 16px",
                  fontFamily: "ui-monospace, Menlo, monospace",
                  fontSize: "0.72rem",
                  lineHeight: 1.8,
                  color: "#b0b7bd",
                  overflowX: "auto",
                  marginTop: 12,
                }}
              >
                <div
                  data-testid="node-status-badge"
                  style={{
                    color: nodeStatus.rpc_8545_open ? "#38e07c" : "#e5c158",
                  }}
                >
                  {nodeStatus.status}
                </div>
                <div>
                  network id: {nodeStatus.network_id} · datadir: {nodeStatus.data_dir}
                </div>
                <div>
                  geth binary: {nodeStatus.geth_binary_present ? "present" : "not installed"} · rpc
                  8545: {nodeStatus.rpc_8545_open ? "open" : "closed"}
                </div>
                {nodeStatus.live_chain && (
                  <div style={{ color: "#38e07c" }} data-testid="node-live-chain">
                    live: block #{nodeStatus.live_chain.block_height} · mining:{" "}
                    {nodeStatus.live_chain.mining ? "active" : "idle"}
                  </div>
                )}
                {nodeStatus.deployment && (
                  <div data-testid="node-deployment" style={{ color: "#5bc6e9", wordBreak: "break-all" }}>
                    engine: {nodeStatus.deployment.address} · $gAu:{" "}
                    {nodeStatus.deployment.settlement_token}
                  </div>
                )}
                {nodeStatus.genesis_note && (
                  <div style={{ color: "#8b94a0" }}>genesis note: {nodeStatus.genesis_note}</div>
                )}
                <div style={{ marginTop: 6, color: "#8b94a0" }}>
                  <div>init: {nodeStatus.bootstrap_commands.init}</div>
                  <div style={{ wordBreak: "break-all" }}>
                    run: {nodeStatus.bootstrap_commands.run}
                  </div>
                </div>
                {nodeStatus.secure_transport && (
                  <div
                    data-testid="secure-transport"
                    style={{
                      marginTop: 8,
                      paddingTop: 8,
                      borderTop: "1px solid rgba(176,183,189,0.15)",
                    }}
                  >
                    <div style={{ wordBreak: "break-all" }}>
                      rpc endpoint in use:{" "}
                      <span style={{ color: "#38e07c" }}>{nodeStatus.rpc_endpoint_in_use}</span>
                    </div>
                    <div>
                      wireguard {nodeStatus.secure_transport.interface}:{" "}
                      {nodeStatus.secure_transport.tunnel_network} · port{" "}
                      {nodeStatus.secure_transport.listen_port} · keepalive{" "}
                      {nodeStatus.secure_transport.persistent_keepalive_seconds}s
                    </div>
                    <div data-testid="secure-transport-status" style={{ color: "#e5c158" }}>
                      {nodeStatus.secure_transport.status}
                    </div>
                    <div style={{ color: "#8b94a0" }}>
                      staged: {nodeStatus.secure_transport.config_paths.join(" · ")} (0600)
                    </div>
                    <div style={{ color: "#8b94a0", wordBreak: "break-all" }}>
                      tunnel rpc target: {nodeStatus.secure_transport.rpc_endpoint_over_tunnel} — set
                      BGNZN_RPC_ENDPOINT to this once wg0 is live on both hosts
                    </div>
                    {nodeStatus.hardening && (
                      <>
                        <div style={{ wordBreak: "break-all" }}>
                          recovery daemon: {nodeStatus.hardening.systemd_unit.path} ·{" "}
                          {nodeStatus.hardening.systemd_unit.restart_policy}
                        </div>
                        <div style={{ wordBreak: "break-all" }}>
                          firewall: {nodeStatus.hardening.firewall_script.path} (
                          {nodeStatus.hardening.firewall_script.permissions}) ·{" "}
                          {nodeStatus.hardening.firewall_script.posture}
                        </div>
                        {nodeStatus.hardening.hardware_firewall_script && (
                          <div style={{ wordBreak: "break-all" }}>
                            hardware netfilter: {nodeStatus.hardening.hardware_firewall_script.path}{" "}
                            ({nodeStatus.hardening.hardware_firewall_script.permissions}) · INPUT/FORWARD
                            DROP · 51820/udp + 3000/8001 tcp only
                          </div>
                        )}
                        {nodeStatus.hardening.supervisor_programs && (
                          <div style={{ wordBreak: "break-all" }}>
                            dual-host daemons:{" "}
                            {nodeStatus.hardening.supervisor_programs.programs.join(" · ")}
                          </div>
                        )}
                        <div data-testid="hardening-status" style={{ color: "#e5c158" }}>
                          hardening: {nodeStatus.hardening.status}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {handshake && (
              <div
                data-testid="node-handshake-card"
                style={{ borderTop: "1px solid rgba(176,183,189,0.2)", paddingTop: 20 }}
              >
                <p className="portal-sub-tag" style={{ marginBottom: 16, textAlign: "left" }}>
                  Private Node Connection Bridge
                </p>
                <div
                  style={{
                    background: "rgba(11,15,25,0.85)",
                    border: "1px solid rgba(176,183,189,0.25)",
                    borderRadius: 8,
                    padding: "14px 16px",
                    fontFamily: "ui-monospace, Menlo, monospace",
                    fontSize: "0.72rem",
                    lineHeight: 1.8,
                    color: "#b0b7bd",
                    overflowX: "auto",
                  }}
                >
                  <div
                    data-testid="handshake-status"
                    style={{ color: handshake.status === "CONNECTED" ? "#38e07c" : "#ff4455" }}
                  >
                    {handshake.status === "CONNECTED"
                      ? `[+] BGNZN Node Securely Handshaked. Network ID: ${handshake.network} | Current Ledger Block: ${handshake.block}`
                      : `[-] Secure Connection Refused: ${handshake.error || "local node offline"}`}
                  </div>
                  {handshake.clearing_engine && (
                    <div style={{ color: "#5bc6e9", wordBreak: "break-all" }}>
                      engine: {handshake.clearing_engine} · bytecode:{" "}
                      {handshake.engine_code_present ? "deployed" : "MISSING"}
                    </div>
                  )}
                </div>
                <form
                  onSubmit={runWarehouseQuery}
                  data-testid="warehouse-line-form"
                  style={{ marginTop: 16 }}
                >
                  <div className="input-data-field">
                    <label className="form-label-geo" htmlFor="warehouse-line-id">
                      Warehouse Line Identifier (bytes32 hex)
                    </label>
                    <input
                      id="warehouse-line-id"
                      value={lineId}
                      onChange={(e) => setLineId(e.target.value)}
                      className="geo-terminal-input"
                      data-testid="warehouse-line-input"
                      spellCheck={false}
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    className="prime-clearing-btn"
                    data-testid="warehouse-line-query-button"
                  >
                    Query On-Chain Warehouse Registry
                  </button>
                </form>
                {lineError && (
                  <p className="portal-error" data-testid="warehouse-line-error">
                    {lineError}
                  </p>
                )}
                {lineResult && (
                  <div
                    data-testid="warehouse-line-result"
                    style={{
                      background: "rgba(11,15,25,0.85)",
                      border: `1px solid ${
                        lineResult.isActive ? "rgba(56,224,124,0.35)" : "rgba(176,183,189,0.25)"
                      }`,
                      borderLeft: `3px solid ${lineResult.isActive ? "#38e07c" : "#8b94a0"}`,
                      borderRadius: 8,
                      padding: "14px 16px",
                      fontFamily: "ui-monospace, Menlo, monospace",
                      fontSize: "0.72rem",
                      lineHeight: 1.8,
                      color: "#b0b7bd",
                      overflowX: "auto",
                    }}
                  >
                    <div
                      data-testid="warehouse-line-status"
                      style={{ color: lineResult.isActive ? "#38e07c" : "#8b94a0" }}
                    >
                      {lineResult.isActive
                        ? "WAREHOUSE_LINE_ACTIVE"
                        : lineResult.isSettled
                          ? "WAREHOUSE_LINE_SETTLED"
                          : "NO_ACTIVE_LINE_AT_IDENTIFIER"}
                    </div>
                    <div style={{ wordBreak: "break-all" }}>line: {lineResult.line_id}</div>
                    <div>
                      face value: {fmt(lineResult.faceValueUSD)} · net advanced:{" "}
                      {fmt(lineResult.netAdvancedUSD)}
                    </div>
                    <div style={{ color: "#e5c158" }}>
                      upfront discount retained: {fmt(lineResult.upfrontDiscountUSD)} · trailing
                      usury: 0.0000%
                    </div>
                    {lineResult.expirationTimestamp > 0 && (
                      <div>
                        expiration:{" "}
                        {new Date(lineResult.expirationTimestamp * 1000).toUTCString()}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div
              data-testid="settle-lifecycle-card"
              style={{ borderTop: "1px solid rgba(176,183,189,0.2)", paddingTop: 20 }}
            >
              <p className="portal-sub-tag" style={{ marginBottom: 16, textAlign: "left" }}>
                T+0 Settlement Finality · On-Chain Recapture
              </p>
              <button
                onClick={runSettleLifecycle}
                disabled={settleBusy}
                className="prime-clearing-btn"
                data-testid="settle-lifecycle-button"
              >
                {settleBusy ? "Executing On-Chain Lifecycle…" : "Execute Full Settlement Lifecycle"}
              </button>
              {settleError && (
                <p className="portal-error" data-testid="settle-error">
                  {settleError}
                </p>
              )}
              {settleResult && (
                <div
                  data-testid="settle-result"
                  style={{
                    background: "rgba(11,15,25,0.85)",
                    border: "1px solid rgba(56,224,124,0.35)",
                    borderLeft: "3px solid #38e07c",
                    borderRadius: 8,
                    padding: "14px 16px",
                    fontFamily: "ui-monospace, Menlo, monospace",
                    fontSize: "0.72rem",
                    lineHeight: 1.8,
                    color: "#b0b7bd",
                    overflowX: "auto",
                    marginTop: 16,
                  }}
                >
                  <div data-testid="settle-status" style={{ color: "#38e07c" }}>
                    {settleResult.lifecycle_status}
                  </div>
                  <div style={{ wordBreak: "break-all" }}>line: {settleResult.line_id}</div>
                  {Object.entries(settleResult.transactions).map(([step, tx]) => (
                    <div key={step} style={{ wordBreak: "break-all" }}>
                      <span style={{ color: "#e5c158" }}>{step}</span>:{" "}
                      <span style={{ color: "#5bc6e9" }}>{tx.tx.slice(0, 22)}…</span> block #
                      {tx.block}
                    </div>
                  ))}
                  <div>
                    registry: face {fmt(settleResult.registry_after_settlement.faceValueUSD)} · net{" "}
                    {fmt(settleResult.registry_after_settlement.netAdvancedUSD)} · active:{" "}
                    {settleResult.registry_after_settlement.isActive ? "yes" : "no"} · settled:{" "}
                    {settleResult.registry_after_settlement.isSettled ? "yes" : "no"}
                  </div>
                  <div>
                    engine vault:{" "}
                    {Number(settleResult.engine_vault_balance_gAu).toLocaleString("en-US")} gAu ·
                    finality: {settleResult.settlement_finality_seconds}s · trailing usury:{" "}
                    {settleResult.trailing_usury_rate.toFixed(4)}%
                  </div>
                  <div style={{ color: "#8b94a0" }}>{settleResult.demo_line_note}</div>
                </div>
              )}
            </div>

            {nodeStatus?.deployment?.escrow_engine && (
              <div
                data-testid="escrow-engine-card"
                style={{ borderTop: "1px solid rgba(176,183,189,0.2)", paddingTop: 20 }}
              >
                <p className="portal-sub-tag" style={{ marginBottom: 16, textAlign: "left" }}>
                  Smart Escrow Engine · $sUSD Forward Settlement
                </p>
                <div
                  style={{
                    background: "rgba(11,15,25,0.85)",
                    border: "1px solid rgba(176,183,189,0.25)",
                    borderRadius: 8,
                    padding: "14px 16px",
                    fontFamily: "ui-monospace, Menlo, monospace",
                    fontSize: "0.72rem",
                    lineHeight: 1.8,
                    color: "#b0b7bd",
                    overflowX: "auto",
                    marginBottom: 16,
                  }}
                >
                  <div style={{ wordBreak: "break-all", color: "#5bc6e9" }}>
                    engine: {nodeStatus.deployment.escrow_engine.address}
                  </div>
                  <div style={{ wordBreak: "break-all" }}>
                    $sUSD: {nodeStatus.deployment.escrow_engine.susd_token.address} · supply{" "}
                    {Number(nodeStatus.deployment.escrow_engine.susd_token.supply).toLocaleString("en-US")}
                  </div>
                  <div>100% cash-backed · capital locked on-chain · non-delivery fault circuit</div>
                </div>
                <form onSubmit={runEscrowLookup} data-testid="escrow-lookup-form">
                  <div className="input-data-field">
                    <label className="form-label-geo" htmlFor="escrow-agreement-id">
                      Forward Agreement Identifier (bytes32 hex)
                    </label>
                    <input
                      id="escrow-agreement-id"
                      value={escrowId}
                      onChange={(e) => setEscrowId(e.target.value)}
                      className="geo-terminal-input"
                      data-testid="escrow-lookup-input"
                      spellCheck={false}
                      required
                    />
                  </div>
                  <button type="submit" className="prime-clearing-btn" data-testid="escrow-lookup-button">
                    Query Escrow Registry
                  </button>
                </form>
                <button
                  onClick={runEscrowLifecycle}
                  disabled={escrowBusy}
                  className="prime-clearing-btn"
                  data-testid="escrow-lifecycle-button"
                  style={{ marginTop: 12 }}
                >
                  {escrowBusy ? "Executing Escrow Cycle…" : "Execute Forward Escrow Cycle"}
                </button>
                {escrowError && (
                  <p className="portal-error" data-testid="escrow-error">
                    {escrowError}
                  </p>
                )}
                {(escrowLifecycle || escrowResult) && (
                  <div
                    data-testid="escrow-result"
                    style={{
                      background: "rgba(11,15,25,0.85)",
                      border: "1px solid rgba(56,224,124,0.35)",
                      borderLeft: "3px solid #38e07c",
                      borderRadius: 8,
                      padding: "14px 16px",
                      fontFamily: "ui-monospace, Menlo, monospace",
                      fontSize: "0.72rem",
                      lineHeight: 1.8,
                      color: "#b0b7bd",
                      overflowX: "auto",
                      marginTop: 16,
                    }}
                  >
                    {escrowLifecycle ? (
                      <>
                        <div data-testid="escrow-status" style={{ color: "#38e07c" }}>
                          {escrowLifecycle.lifecycle_status}
                        </div>
                        <div style={{ wordBreak: "break-all" }}>
                          agreement: {escrowLifecycle.agreement_id}
                        </div>
                        {Object.entries(escrowLifecycle.transactions).map(([step, tx]) => (
                          <div key={step} style={{ wordBreak: "break-all" }}>
                            <span style={{ color: "#e5c158" }}>{step}</span>:{" "}
                            <span style={{ color: "#5bc6e9" }}>{tx.tx.slice(0, 22)}…</span> block #
                            {tx.block}
                          </div>
                        ))}
                        <div>
                          {escrowLifecycle.asset_class} · cash {fmt(escrowLifecycle.cash_volume_usd)} ·{" "}
                          {escrowLifecycle.commodity_qty_lbs} lbs · seller balance:{" "}
                          {Number(escrowLifecycle.seller_susd_balance).toLocaleString("en-US")} sUSD ·
                          finality: {escrowLifecycle.settlement_finality_seconds}s · usury:{" "}
                          {escrowLifecycle.trailing_usury_rate.toFixed(4)}%
                        </div>
                      </>
                    ) : (
                      <>
                        <div
                          data-testid="escrow-status"
                          style={{
                            color: escrowResult.status === "Fulfilled" ? "#38e07c" : "#e5c158",
                          }}
                        >
                          ESCROW_{escrowResult.status}
                        </div>
                        <div style={{ wordBreak: "break-all" }}>
                          agreement: {escrowResult.agreement_id}
                        </div>
                        <div>
                          {escrowResult.asset_class} · locked {fmt(escrowResult.locked_cash_volume_usd)}{" "}
                          · qty {escrowResult.expected_commodity_quantity} · buyer{" "}
                          {escrowResult.buyer.slice(0, 12)}… · seller {escrowResult.seller.slice(0, 12)}…
                        </div>
                        {escrowResult.delivery_deadline > 0 && (
                          <div>
                            deadline: {new Date(escrowResult.delivery_deadline * 1000).toUTCString()}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            <div
              data-testid="governing-clauses"
              style={{ borderTop: "1px solid rgba(176,183,189,0.2)", paddingTop: 20 }}
            >
              <p className="portal-sub-tag" style={{ marginBottom: 16, textAlign: "left" }}>
                Governing Settlement Clauses · Section 4
              </p>
              <div className="space-y-4">
                {GOVERNING_CLAUSES.map((clause) => (
                  <div
                    key={clause.id}
                    data-testid={`clause-${clause.id.replace(".", "-")}`}
                    style={{
                      background: "rgba(11,15,25,0.85)",
                      border: "1px solid rgba(229,193,88,0.25)",
                      borderLeft: "3px solid #e5c158",
                      borderRadius: 8,
                      padding: "14px 16px",
                    }}
                  >
                    <div
                      style={{
                        color: "#e5c158",
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "1px",
                        marginBottom: 6,
                      }}
                    >
                      Clause {clause.id} — {clause.title}
                    </div>
                    <p style={{ color: "#b0b7bd", fontSize: "0.78rem", lineHeight: 1.7, margin: 0 }}>
                      {clause.body}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {contracts.length > 0 && (
              <div
                data-testid="transit-registry"
                style={{ borderTop: "1px solid rgba(176,183,189,0.2)", paddingTop: 20 }}
              >
                <p className="portal-sub-tag" style={{ marginBottom: 16, textAlign: "left" }}>
                  Specie Transit Registry
                </p>
                {contracts.map((c, i) => (
                  <div
                    key={i}
                    data-testid={`transit-contract-${i}`}
                    style={{
                      background: "rgba(11,15,25,0.85)",
                      border: "1px solid rgba(176,183,189,0.25)",
                      borderRadius: 8,
                      padding: "14px 16px",
                      fontFamily: "ui-monospace, Menlo, monospace",
                      fontSize: "0.72rem",
                      lineHeight: 1.7,
                      color: "#b0b7bd",
                      overflowX: "auto",
                    }}
                  >
                    <div style={{ color: "#e5c158" }}>{c.contract_type}</div>
                    <div>exemption: {c.regulatory_exemption_profile}</div>
                    <div>
                      payload: {c.consignment_parameters.transit_payload.audited_mass_ounces} oz{" "}
                      {c.consignment_parameters.transit_payload.metal_type} @{" "}
                      {c.consignment_parameters.transit_payload.purity_threshold} purity
                    </div>
                    <div>
                      carrier bond: {c.consignment_parameters.carrier_good_faith_bond_grams_gAg.toLocaleString()} gAg
                    </div>
                    <div>
                      release: {c.settlement_verification_protocol.origin_vault_release_trigger}
                    </div>
                    <div>
                      telemetry: laser scan{" "}
                      {c.settlement_verification_protocol.destination_dock_telemetry.volumetric_laser_scan_match ? "✓" : "✗"}{" "}
                      · spectrographic purity{" "}
                      {c.settlement_verification_protocol.destination_dock_telemetry.spectrographic_purity_verification ? "✓" : "✗"}
                    </div>
                    <div style={{ color: "#ff6b76" }}>
                      fault circuit: {c.settlement_verification_protocol.fault_circuit_parameters.grace_period_hours}h
                      grace → {c.settlement_verification_protocol.fault_circuit_parameters.breach_remedy}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div
              data-testid="por-panel"
              style={{ borderTop: "1px solid rgba(176,183,189,0.2)", paddingTop: 20 }}
            >
              <p className="portal-sub-tag" style={{ marginBottom: 16, textAlign: "left" }}>
                Proof-of-Reserve · BMEX Live Repository Audit
              </p>
              <PorWidget />
            </div>

            <div
              data-testid="liquidation-panel"
              style={{ borderTop: "1px solid rgba(176,183,189,0.2)", paddingTop: 20 }}
            >
              <p className="portal-sub-tag" style={{ marginBottom: 16, textAlign: "left" }}>
                Forward Settlement &amp; Liquidation Engine · Chain 9940
              </p>
              <div
                style={{
                  fontSize: "0.72rem",
                  color: "#b0b7bd",
                  lineHeight: 1.7,
                  marginBottom: 14,
                }}
              >
                Arm&rsquo;s-length forward purchase agreements lock the buyer&rsquo;s security
                deposit at registration. Cleared inside the database timeline → T+0 finality.
                Window lapses → the default circuit voids the contract and forfeits 100% of the
                deposit to the BOURG MERCHANT EXCHANGE vault. The breach audit watchdog scans every
                60 seconds.
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
                <button
                  className="prime-clearing-btn"
                  data-testid="liquidation-clear-button"
                  onClick={() => runLiquidationLifecycle("clear")}
                  disabled={liqBusy}
                  style={{ flex: 1, minWidth: 200 }}
                >
                  {liqBusy ? "EXECUTING…" : "EXECUTE CLEAN T+0 SETTLEMENT"}
                </button>
                <button
                  className="prime-clearing-btn"
                  data-testid="liquidation-breach-button"
                  onClick={() => runLiquidationLifecycle("breach")}
                  disabled={liqBusy}
                  style={{ flex: 1, minWidth: 200 }}
                >
                  {liqBusy ? "EXECUTING…" : "TRIGGER DEFAULT LIQUIDATION"}
                </button>
              </div>
              {liqError && (
                <p className="portal-error" data-testid="liquidation-error">
                  {liqError}
                </p>
              )}
              {liqResult && (
                <div
                  data-testid="liquidation-result"
                  style={{
                    background: "rgba(11,15,25,0.85)",
                    border: "1px solid rgba(176,183,189,0.25)",
                    borderRadius: 8,
                    padding: "14px 16px",
                    fontFamily: "ui-monospace, Menlo, monospace",
                    fontSize: "0.72rem",
                    lineHeight: 1.8,
                    color: "#b0b7bd",
                    overflowX: "auto",
                    marginBottom: 16,
                  }}
                >
                  <div
                    style={{
                      color:
                        liqResult.contract_status === "CLEARED_WITH_FINALITY"
                          ? "#38e07c"
                          : "#ff6b76",
                    }}
                  >
                    {liqResult.agreement_label} → {liqResult.contract_status}
                  </div>
                  <div style={{ wordBreak: "break-all" }}>
                    agreement:{" "}
                    <span style={{ color: "#5bc6e9" }}>{liqResult.agreement_id}</span>
                  </div>
                  <div>
                    tranche: ${liqResult.asset_tranche} · price {fmt(liqResult.purchase_price_usd)}{" "}
                    · deposit {fmt(liqResult.security_deposit_usd)}
                  </div>
                  <div>
                    finality: {liqResult.settlement_finality_seconds}s · final tx block #
                    {liqResult.transactions?.final?.block}
                  </div>
                  <div>
                    registry state: {liqResult.registry_final_state?.state} · deadline epoch{" "}
                    {liqResult.settlement_deadline_epoch}
                  </div>
                </div>
              )}
              <form onSubmit={runLiquidationLookup} data-testid="liquidation-lookup-form">
                <div className="input-data-field">
                  <label className="form-label-geo" htmlFor="liq-lookup">
                    Agreement Registry Lookup (bytes32 id)
                  </label>
                  <input
                    id="liq-lookup"
                    value={liqLookupId}
                    onChange={(e) => setLiqLookupId(e.target.value)}
                    className="geo-terminal-input"
                    data-testid="liquidation-lookup-input"
                    placeholder="0x…"
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="prime-clearing-btn"
                  data-testid="liquidation-lookup-button"
                >
                  QUERY AGREEMENT STATE
                </button>
              </form>
              {liqLookupError && (
                <p className="portal-error" data-testid="liquidation-lookup-error">
                  {liqLookupError}
                </p>
              )}
              {liqLookupResult && (
                <div
                  data-testid="liquidation-lookup-result"
                  style={{
                    background: "rgba(11,15,25,0.85)",
                    border: "1px solid rgba(176,183,189,0.25)",
                    borderRadius: 8,
                    padding: "14px 16px",
                    fontFamily: "ui-monospace, Menlo, monospace",
                    fontSize: "0.72rem",
                    lineHeight: 1.8,
                    color: "#b0b7bd",
                    overflowX: "auto",
                    marginTop: 12,
                  }}
                >
                  <div style={{ color: "#e5c158" }}>
                    state: {liqLookupResult.state} · tranche: $
                    {liqLookupResult.asset_tranche_ticker}
                  </div>
                  <div>buyer: {liqLookupResult.buyer}</div>
                  <div>seller (vault): {liqLookupResult.seller}</div>
                  <div>
                    price {fmt(liqLookupResult.purchase_price_usd)} · deposit{" "}
                    {fmt(liqLookupResult.required_security_deposit_usd)} · deadline{" "}
                    {liqLookupResult.settlement_deadline_timestamp}
                  </div>
                </div>
              )}
              <div style={{ marginTop: 14 }}>
                <button
                  className="prime-clearing-btn"
                  data-testid="breach-audit-button"
                  onClick={runBreachAudit}
                >
                  RUN BREACH AUDIT CYCLE
                </button>
                {auditResult && (
                  <div
                    data-testid="breach-audit-result"
                    style={{
                      fontFamily: "ui-monospace, Menlo, monospace",
                      fontSize: "0.72rem",
                      color: "#b0b7bd",
                      marginTop: 10,
                      lineHeight: 1.8,
                    }}
                  >
                    breaches found: {auditResult.breaches_found} · liquidated:{" "}
                    {auditResult.liquidated?.length || 0} · faults:{" "}
                    {auditResult.faults?.length || 0}
                  </div>
                )}
              </div>
            </div>

            <div
              data-testid="rollback-panel"
              style={{ borderTop: "1px solid rgba(176,183,189,0.2)", paddingTop: 20 }}
            >
              <p className="portal-sub-tag" style={{ marginBottom: 16, textAlign: "left" }}>
                Telemetry Rollback Circuit · Dual-Signature Emergency Brake
              </p>
              <div
                style={{
                  fontSize: "0.72rem",
                  color: "#b0b7bd",
                  lineHeight: 1.7,
                  marginBottom: 14,
                }}
              >
                The administrative circuit breaker for the BOURG MERCHANT EXCHANGE footprint.
                Reverses erroneous margin events triggered by faulty telemetry: the root HSM desk
                opens the ticket, the secondary auditor desk co-signs, and only then does the
                circuit force the clearing engine to restore the line&rsquo;s original drawdown
                parameters.
              </div>
              <form onSubmit={runRollbackInitiate} data-testid="rollback-initiate-form">
                <div className="input-data-field">
                  <label className="form-label-geo" htmlFor="rb-line">
                    Target Warehouse Line (bytes32 id)
                  </label>
                  <input
                    id="rb-line"
                    value={rbLineId}
                    onChange={(e) => setRbLineId(e.target.value)}
                    className="geo-terminal-input"
                    data-testid="rollback-line-input"
                    required
                  />
                </div>
                <div className="input-data-field">
                  <label className="form-label-geo" htmlFor="rb-reason">
                    Telemetry Fault Reason
                  </label>
                  <input
                    id="rb-reason"
                    value={rbReason}
                    onChange={(e) => setRbReason(e.target.value)}
                    className="geo-terminal-input"
                    data-testid="rollback-reason-input"
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="prime-clearing-btn"
                  data-testid="rollback-initiate-button"
                  disabled={rbBusy}
                >
                  {rbBusy ? "SIGNING…" : "INITIATE EMERGENCY ROLLBACK"}
                </button>
              </form>
              {rbError && (
                <p className="portal-error" data-testid="rollback-error">
                  {rbError}
                </p>
              )}
              {rbEvent && (
                <div
                  data-testid="rollback-event-card"
                  style={{
                    background: "rgba(11,15,25,0.85)",
                    border: "1px solid rgba(176,183,189,0.25)",
                    borderRadius: 8,
                    padding: "14px 16px",
                    fontFamily: "ui-monospace, Menlo, monospace",
                    fontSize: "0.72rem",
                    lineHeight: 1.8,
                    color: "#b0b7bd",
                    overflowX: "auto",
                    marginTop: 12,
                  }}
                >
                  <div style={{ wordBreak: "break-all" }}>
                    event: <span style={{ color: "#5bc6e9" }}>{rbEvent.event_id}</span>
                  </div>
                  <div>
                    root desk:{" "}
                    <span style={{ color: "#38e07c" }}>
                      {rbEvent.registry?.approved_by_root ? "SIGNED" : "PENDING"}
                    </span>{" "}
                    · auditor desk:{" "}
                    <span
                      style={{
                        color: rbEvent.registry?.approved_by_auditor ? "#38e07c" : "#e5c158",
                      }}
                    >
                      {rbEvent.registry?.approved_by_auditor ? "SIGNED" : "PENDING"}
                    </span>{" "}
                    ·{" "}
                    <span
                      data-testid="rollback-circuit-status"
                      style={{ color: rbEvent.registry?.finalized ? "#38e07c" : "#e5c158" }}
                    >
                      {rbEvent.circuit_status ||
                        (rbEvent.registry?.finalized
                          ? "EMERGENCY_CIRCUIT_EXECUTED"
                          : "AWAITING_DUAL_SIGNATURES")}
                    </span>
                  </div>
                  {rbEvent.line_state_after && rbEvent.line_state_after.line_active != null && (
                    <div style={{ color: "#e5c158" }}>
                      line restored → active: {String(rbEvent.line_state_after.line_active)} ·
                      settled: {String(rbEvent.line_state_after.line_settled)}
                    </div>
                  )}
                </div>
              )}
              <div style={{ marginTop: 12 }}>
                <button
                  className="prime-clearing-btn"
                  data-testid="rollback-cosign-button"
                  onClick={runRollbackCosign}
                  disabled={rbBusy || !rbEventId}
                >
                  {rbBusy ? "SIGNING…" : "CO-SIGN & EXECUTE (AUDITOR DESK)"}
                </button>
              </div>
            </div>

            <div
              data-testid="solvency-console"
              style={{ borderTop: "1px solid rgba(176,183,189,0.2)", paddingTop: 20 }}
            >
              <p className="portal-sub-tag" style={{ marginBottom: 16, textAlign: "left" }}>
                Executive Solvency Console · True-Circulation Audit
              </p>
              {solvency ? (
                <div
                  data-testid="solvency-board"
                  style={{
                    background: "rgba(11,15,25,0.85)",
                    border: "1px solid rgba(176,183,189,0.25)",
                    borderRadius: 8,
                    padding: "14px 16px",
                    fontFamily: "ui-monospace, Menlo, monospace",
                    fontSize: "0.72rem",
                    lineHeight: 1.9,
                    color: "#b0b7bd",
                    overflowX: "auto",
                    marginBottom: 16,
                  }}
                >
                  <div style={{ color: "#e5c158" }}>
                    live reserve audit · chain 9940 · block{" "}
                    <span data-testid="solvency-block">
                      {solvency.chain?.block_height ?? "—"}
                    </span>{" "}
                    · refreshes on the 2.00s block cycle
                  </div>
                  <div style={{ marginTop: 6, color: "#5bc6e9" }}>
                    off-chain correspondent liquidity (M&amp;T nostro vaults)
                  </div>
                  <div>
                    usd settled:{" "}
                    <span data-testid="solvency-usd" style={{ color: "#38e07c" }}>
                      {fmt(solvency.usd_nostro_settled)}
                    </span>
                    {"  ·  eur settled: €"}
                    {Number(solvency.eur_nostro_settled).toLocaleString("en-US")}
                    {"  ·  pending wires: "}
                    {solvency.pending_declarations_count}
                  </div>
                  <div style={{ marginTop: 6, color: "#5bc6e9" }}>
                    native circulation (minted − treasury float)
                  </div>
                  <div>
                    minted: {Number(solvency.chain?.total_minted_susd ?? 0).toLocaleString("en-US")} sUSD
                    {"  ·  treasury float: "}
                    {Number(solvency.chain?.treasury_float_susd ?? 0).toLocaleString("en-US")} sUSD
                  </div>
                  <div>
                    true circulating:{" "}
                    <span data-testid="solvency-circulating" style={{ color: "#38e07c" }}>
                      {Number(solvency.chain?.circulating_susd ?? 0).toLocaleString("en-US")} sUSD
                    </span>
                  </div>
                  <div style={{ marginTop: 6 }}>
                    consolidated vault backing:{" "}
                    {fmt(solvency.audit?.consolidated_vault_backing_usd ?? 0)} equivalent
                  </div>
                  <div>
                    system reserve audit state:{" "}
                    <span
                      data-testid="solvency-state"
                      style={{
                        color:
                          solvency.audit?.state === "PERFECT_EQUILIBRIUM_1_TO_1"
                            ? "#38e07c"
                            : "#e5c158",
                      }}
                    >
                      {solvency.audit?.state === "PERFECT_EQUILIBRIUM_1_TO_1"
                        ? "PERFECT EQUILIBRIUM (1:1 SECURED)"
                        : `DEVIATION DETECTED (${fmt(solvency.audit?.delta_usd ?? 0)})`}
                    </span>
                  </div>
                </div>
              ) : (
                <p
                  data-testid="solvency-loading"
                  style={{ color: "#b0b7bd", fontSize: "0.75rem", marginBottom: 16 }}
                >
                  Linking to the settlement grid…
                </p>
              )}
            </div>

            <div
              data-testid="treasury-panel"
              style={{ borderTop: "1px solid rgba(176,183,189,0.2)", paddingTop: 20 }}
            >
              <p className="portal-sub-tag" style={{ marginBottom: 16, textAlign: "left" }}>
                BMEX Treasury · $sUSD Web3 Withdrawal Rail
              </p>
              {treasury && (
                <div
                  data-testid="treasury-book"
                  style={{
                    background: "rgba(11,15,25,0.85)",
                    border: "1px solid rgba(176,183,189,0.25)",
                    borderRadius: 8,
                    padding: "14px 16px",
                    fontFamily: "ui-monospace, Menlo, monospace",
                    fontSize: "0.72rem",
                    lineHeight: 1.9,
                    color: "#b0b7bd",
                    overflowX: "auto",
                    marginBottom: 16,
                  }}
                >
                  <div style={{ color: "#e5c158" }}>treasury address book · chain 9940</div>
                  <div style={{ wordBreak: "break-all" }}>
                    root authority wallet:{" "}
                    <span style={{ color: "#5bc6e9" }}>{treasury.root_authority_wallet}</span>
                  </div>
                  <div style={{ wordBreak: "break-all" }}>
                    $sUSD token contract:{" "}
                    <span style={{ color: "#5bc6e9" }}>{treasury.susd_token}</span>
                  </div>
                  <div style={{ wordBreak: "break-all" }}>
                    engine vault: <span style={{ color: "#5bc6e9" }}>{treasury.engine_vault}</span>
                  </div>
                  <div>
                    treasury $sUSD:{" "}
                    <span data-testid="treasury-balance" style={{ color: "#38e07c" }}>
                      {fmt(treasury.root_susd_balance)}
                    </span>{" "}
                    · engine vault: {Number(treasury.engine_vault_gAu_balance).toLocaleString("en-US")} gAu
                    · escrow pool: {fmt(treasury.escrow_pool_susd_balance)}
                  </div>
                  {treasury.fiat_load_inflows && (
                    <div
                      data-testid="treasury-inflows"
                      style={{ borderTop: "1px solid rgba(176,183,189,0.15)", marginTop: 8, paddingTop: 8 }}
                    >
                      <div>
                        <span style={{ color: "#e5c158" }}>fiat loads landed at the treasury desk: </span>
                        <span data-testid="treasury-inflow-total" style={{ color: "#38e07c" }}>
                          ${treasury.fiat_load_inflows.total_usd.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                        </span>{" "}
                        across {treasury.fiat_load_inflows.count} settlement{treasury.fiat_load_inflows.count === 1 ? "" : "s"}
                        {" "}— every dollar loaded onto member cards accounted under the treasury director's authority
                      </div>
                      {treasury.fiat_load_inflows.recent.map((f) => (
                        <div key={f.intention_id} style={{ wordBreak: "break-all" }} data-testid={`inflow-${f.intention_id}`}>
                          <span style={{ color: "#5bc6e9" }}>{(f.settled_at || "").slice(0, 10)}</span>{" "}
                          {f.member_label} · {f.rail} · ${Number(f.amount_usd).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                          {f.fiat_destination ? ` → desk ${f.fiat_destination}` : ""}
                          {f.settlement_tx?.block ? ` · block #${f.settlement_tx.block}` : ""}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <form onSubmit={runTreasuryWithdraw} data-testid="treasury-withdraw-form">
                <div className="input-data-field">
                  <label className="form-label-geo" htmlFor="wd-address">
                    Destination Web3 Wallet Address (0x…)
                  </label>
                  <input
                    id="wd-address"
                    value={wdAddress}
                    onChange={(e) => setWdAddress(e.target.value)}
                    className="geo-terminal-input"
                    data-testid="treasury-address-input"
                    placeholder="0x…"
                    required
                  />
                </div>
                <div className="input-data-field">
                  <label className="form-label-geo" htmlFor="wd-amount">
                    Withdrawal Volume ($sUSD)
                  </label>
                  <input
                    id="wd-amount"
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={wdAmount}
                    onChange={(e) => setWdAmount(e.target.value)}
                    className="geo-terminal-input"
                    data-testid="treasury-amount-input"
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="prime-clearing-btn"
                  data-testid="treasury-withdraw-button"
                  disabled={wdBusy}
                >
                  {wdBusy ? "SETTLING…" : "SETTLE WITHDRAWAL T+0"}
                </button>
              </form>
              {wdError && (
                <p className="portal-error" data-testid="treasury-error">
                  {wdError}
                </p>
              )}
              {wdResult && (
                <div
                  data-testid="treasury-result"
                  style={{
                    background: "rgba(11,15,25,0.85)",
                    border: "1px solid rgba(176,183,189,0.25)",
                    borderRadius: 8,
                    padding: "14px 16px",
                    fontFamily: "ui-monospace, Menlo, monospace",
                    fontSize: "0.72rem",
                    lineHeight: 1.8,
                    color: "#b0b7bd",
                    overflowX: "auto",
                    marginTop: 12,
                  }}
                >
                  <div style={{ color: "#38e07c" }}>
                    {wdResult.status} — {fmt(wdResult.amount_usd)} → wallet
                  </div>
                  <div style={{ wordBreak: "break-all" }}>
                    to: <span style={{ color: "#5bc6e9" }}>{wdResult.to_address}</span>
                  </div>
                  <div style={{ wordBreak: "break-all" }}>
                    receipt tx: {wdResult.treasury_tx?.tx} · block #{wdResult.treasury_tx?.block}
                  </div>
                  <div>wallet balance now: {fmt(wdResult.recipient_susd_balance)}</div>
                </div>
              )}
              {treasury?.recent_withdrawals?.length > 0 && (
                <div
                  data-testid="treasury-history"
                  style={{
                    fontFamily: "ui-monospace, Menlo, monospace",
                    fontSize: "0.7rem",
                    color: "#b0b7bd",
                    lineHeight: 1.9,
                    marginTop: 12,
                  }}
                >
                  <div style={{ color: "#e5c158" }}>recent treasury withdrawals</div>
                  {treasury.recent_withdrawals.map((w) => (
                    <div key={w.treasury_tx?.tx} style={{ wordBreak: "break-all" }}>
                      {fmt(w.amount_usd)} → {w.to_address?.slice(0, 12)}… · block #
                      {w.treasury_tx?.block}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div
              data-testid="member-roster-panel"
              style={{ borderTop: "1px solid rgba(176,183,189,0.2)", paddingTop: 20 }}
            >
              <p className="portal-sub-tag" style={{ marginBottom: 16, textAlign: "left" }}>
                Member Roster · Registered Wallets, Live Balances &amp; Sealed Receipts
              </p>

              <form
                data-testid="onboard-member-form"
                onSubmit={onboardMember}
                style={{
                  background: "rgba(11,15,25,0.85)",
                  border: "1px solid rgba(191,155,72,0.35)",
                  borderRadius: 8,
                  padding: "14px 16px",
                  marginBottom: 14,
                }}
              >
                <div style={{ color: "#e5c158", fontSize: "0.72rem", fontFamily: "ui-monospace, Menlo, monospace", marginBottom: 10, letterSpacing: "0.15em", textTransform: "uppercase" }}>
                  Onboard a member — in person, 30 seconds
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <input
                    data-testid="onboard-name-input"
                    required
                    placeholder="Member / shop name"
                    value={onboardForm.label}
                    onChange={(e) => setOnboardForm({ ...onboardForm, label: e.target.value })}
                    style={{ flex: "2 1 160px", background: "rgba(11,15,25,0.9)", border: "1px solid rgba(176,183,189,0.3)", borderRadius: 6, padding: "10px 12px", color: "#e8e4dc", fontSize: "0.8rem" }}
                  />
                  <input
                    data-testid="onboard-phone-input"
                    required
                    placeholder="Phone (+1 …)"
                    value={onboardForm.phone}
                    onChange={(e) => setOnboardForm({ ...onboardForm, phone: e.target.value })}
                    style={{ flex: "1 1 140px", background: "rgba(11,15,25,0.9)", border: "1px solid rgba(176,183,189,0.3)", borderRadius: 6, padding: "10px 12px", color: "#e8e4dc", fontSize: "0.8rem" }}
                  />
                  <input
                    data-testid="onboard-float-input"
                    type="number" min="0" step="0.01"
                    placeholder="Opening float $ (optional)"
                    value={onboardForm.float}
                    onChange={(e) => setOnboardForm({ ...onboardForm, float: e.target.value })}
                    style={{ flex: "1 1 140px", background: "rgba(11,15,25,0.9)", border: "1px solid rgba(176,183,189,0.3)", borderRadius: 6, padding: "10px 12px", color: "#e8e4dc", fontSize: "0.8rem" }}
                  />
                  <button
                    data-testid="onboard-submit-button"
                    type="submit"
                    disabled={onboardBusy}
                    className="prime-clearing-btn"
                    style={{ margin: 0 }}
                  >
                    {onboardBusy ? "MINTING…" : "ONBOARD"}
                  </button>
                </div>
                {onboardError && (
                  <p data-testid="onboard-error" style={{ color: "#f87171", fontSize: "0.72rem", marginTop: 8 }}>{onboardError}</p>
                )}
                {onboardResult && (
                  <div data-testid="onboard-result" style={{ marginTop: 12, background: "rgba(6,20,12,0.6)", border: "1px solid rgba(56,224,124,0.35)", borderRadius: 6, padding: "12px 14px", fontFamily: "ui-monospace, Menlo, monospace", fontSize: "0.75rem", color: "#b0b7bd", lineHeight: 2 }}>
                    <div style={{ color: "#38e07c" }}>{onboardResult.label} is live — hand them these credentials once:</div>
                    <div data-testid="onboard-uuid">Ledger UUID: <span style={{ color: "#5bc6e9", userSelect: "all" }}>{onboardResult.ledger_uuid}</span></div>
                    <div>Phone: {onboardResult.phone} · wallet {onboardResult.address.slice(0, 10)}…{onboardResult.address.slice(-6)} · gas endowed: {onboardResult.gas_endowed ? "yes" : "pending"}{onboardResult.initial_float_usd > 0 ? ` · float $${onboardResult.initial_float_usd} settled` : ""}</div>
                    <div style={{ color: "#e5c158" }}>They sign into /wallet (spend) or /register (sell) with that UUID + phone.</div>
                  </div>
                )}
              </form>

              <div
                data-testid="member-roster-list"
                style={{
                  background: "rgba(11,15,25,0.85)",
                  border: "1px solid rgba(176,183,189,0.25)",
                  borderRadius: 8,
                  padding: "14px 16px",
                  fontFamily: "ui-monospace, Menlo, monospace",
                  fontSize: "0.72rem",
                  lineHeight: 1.9,
                  color: "#b0b7bd",
                  overflowX: "auto",
                }}
              >
                {roster?.members?.length > 0 ? (
                  roster.members.map((member) => (
                    <div
                      key={member.address}
                      data-testid={`roster-member-${member.address}`}
                      style={{
                        borderBottom: "1px solid rgba(176,183,189,0.12)",
                        padding: "10px 0",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                        <span style={{ color: "#e5c158" }}>{member.label}</span>
                        <span
                          data-testid={`roster-balance-${member.address}`}
                          style={{ color: member.susd_balance != null ? "#38e07c" : "#b0b7bd" }}
                        >
                          {member.susd_balance != null ? fmt(member.susd_balance) : "balance unread"}
                          {member.native_gas_balance != null &&
                            ` · gas ${Number(member.native_gas_balance).toFixed(3)}`}
                        </span>
                      </div>
                      <div style={{ wordBreak: "break-all" }}>
                        wallet: <span style={{ color: "#5bc6e9" }}>{member.address}</span> ·{" "}
                        {member.key_custody === "PLATFORM_CUSTODIED" ? "platform-custodied" : "self-custody"}
                      </div>
                      {member.phone && <div>phone: {member.phone} · sms rail: {member.rails?.sms_wallet}</div>}
                      {member.nexorone_ledger_uuid && (
                        <div style={{ wordBreak: "break-all" }}>
                          ledger uuid: {member.nexorone_ledger_uuid} · core sync:{" "}
                          {member.rails?.nexorone_core_sync}
                        </div>
                      )}
                      {member.receipt && (
                        <div data-testid={`roster-receipt-${member.address}`} style={{ wordBreak: "break-all" }}>
                          receipt: <span style={{ color: "#5bc6e9" }}>{member.receipt.receipt_blind_hash}</span>{" "}
                          · {member.receipt.asset_class_code}{" "}
                          {Number(member.receipt.fractional_units_nominal).toLocaleString("en-US")} units · $
                          {Number(member.receipt.asset_acquisition_cost_usd).toLocaleString("en-US")} anchor ·
                          block #{member.receipt.ledger_state_block_height} ·{" "}
                          {member.receipt.is_unhypothecated ? "unhypothecated" : "hypothecated"}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div data-testid="member-roster-empty">
                    No members registered yet — registry entries appear here as they are sealed.
                  </div>
                )}
              </div>
            </div>

            <div
              data-testid="funding-desk-panel"
              style={{ borderTop: "1px solid rgba(176,183,189,0.2)", paddingTop: 20 }}
            >
              <p className="portal-sub-tag" style={{ marginBottom: 16, textAlign: "left" }}>
                Funding Desk · Incoming Retail Wires (manual attestation until the core connects)
              </p>
              <div data-testid="clearing-registry-strip" style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center", marginBottom: 14, padding: "8px 12px", border: "1px solid rgba(229,193,88,0.35)", borderRadius: 8, fontFamily: "ui-monospace, Menlo, monospace", fontSize: "0.72rem" }}>
                <span style={{ color: "#e5c158", fontWeight: 700 }}>CLEARING REGISTRY</span>
                {registry?.deployed ? (
                  <>
                    <span style={{ color: "#38e07c" }} data-testid="registry-status">ON-CHAIN · {registry.address?.slice(0, 10)}…</span>
                    <span style={{ color: "#b0b7bd" }} data-testid="registry-coverage">
                      coverage {registry.reserve_coverage_pct == null ? "∞" : `${registry.reserve_coverage_pct}%`} · floor {registry.liquidity_floor_pct}%{" "}
                      <strong style={{ color: registry.floor_holds ? "#38e07c" : "#ff4455" }}>{registry.floor_holds ? "HOLDS" : "BREACH"}</strong>
                    </span>
                    <span style={{ color: "#b0b7bd" }} data-testid="registry-allowance">
                      desk drawdown ${fmt(registry.remaining_clearing_allowance_usd ?? 0)}
                    </span>
                  </>
                ) : (
                  <>
                    <span style={{ color: "#6b7280" }} data-testid="registry-status">not deployed on this chain — settles ride the direct treasury rail until it is</span>
                    <button
                      data-testid="registry-deploy-button"
                      onClick={deployRegistry}
                      disabled={regBusy}
                      style={{ background: "rgba(229,193,88,0.15)", border: "1px solid rgba(229,193,88,0.6)", color: "#e5c158", borderRadius: 4, padding: "3px 12px", cursor: "pointer", fontFamily: "inherit", fontSize: "0.7rem" }}
                    >
                      {regBusy ? "DEPLOYING…" : "DEPLOY ON-CHAIN"}
                    </button>
                  </>
                )}
              </div>
              <div data-testid="bourg-exchange-strip" style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center", marginBottom: 14, padding: "8px 12px", border: "1px solid rgba(229,193,88,0.35)", borderRadius: 8, fontFamily: "ui-monospace, Menlo, monospace", fontSize: "0.72rem" }}>
                <span style={{ color: "#e5c158", fontWeight: 700 }}>THE BOURG EXCHANGE TR</span>
                <span style={{ color: "#6b7280" }}>cutting house · securitization · treasury · warehouse</span>
                <button
                  data-testid="bourg-exchange-deploy-button"
                  onClick={deployBourgExchangeRegistry}
                  disabled={regBusy}
                  style={{ background: "rgba(229,193,88,0.15)", border: "1px solid rgba(229,193,88,0.6)", color: "#e5c158", borderRadius: 4, padding: "3px 12px", cursor: "pointer", fontFamily: "inherit", fontSize: "0.7rem" }}
                >
                  {regBusy ? "DEPLOYING…" : "DEPLOY BOURG EXCHANGE ON-CHAIN"}
                </button>
              </div>
              <div data-testid="ledger-room-panel" style={{ marginBottom: 14, padding: "10px 12px", border: "1px solid rgba(229,193,88,0.25)", borderRadius: 8, fontFamily: "ui-monospace, Menlo, monospace", fontSize: "0.72rem" }}>
                <div style={{ color: "#e5c158", fontWeight: 700, marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                  <span>LEDGER ROOM — deeds & vault custody</span>
                  <span style={{ display: "flex", gap: 6 }}>
                    <button data-testid="annual-minute-button" onClick={renderAnnualMinute} disabled={regBusy}
                      style={{ background: "rgba(176,183,189,0.12)", border: "1px solid rgba(176,183,189,0.4)", color: "#b0b7bd", borderRadius: 4, padding: "3px 10px", cursor: "pointer", fontFamily: "inherit", fontSize: "0.7rem" }}>
                      RENDER ANNUAL MINUTE
                    </button>
                    {deeds && !deeds.registry_deployed && (
                    <button data-testid="deed-deploy-button" onClick={deployDeedRegistry} disabled={regBusy}
                      style={{ background: "rgba(229,193,88,0.15)", border: "1px solid rgba(229,193,88,0.6)", color: "#e5c158", borderRadius: 4, padding: "3px 12px", cursor: "pointer", fontFamily: "inherit", fontSize: "0.7rem" }}>
                      {regBusy ? "DEPLOYING…" : "DEPLOY DEED LEDGER ON-CHAIN"}
                    </button>
                  )}
                  </span>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                  <input data-testid="deed-apn" placeholder="Parcel APN" value={deedForm.parcel_apn} onChange={(e) => setDeedForm({ ...deedForm, parcel_apn: e.target.value })} style={deskInput} />
                  <input data-testid="deed-county" placeholder="County" value={deedForm.county} onChange={(e) => setDeedForm({ ...deedForm, county: e.target.value })} style={deskInput} />
                  <input data-testid="deed-value" placeholder="Value USD" value={deedForm.fiat_value_usd} onChange={(e) => setDeedForm({ ...deedForm, fiat_value_usd: e.target.value })} style={{ ...deskInput, width: 110 }} />
                  <input data-testid="deed-legal" placeholder="Legal description" value={deedForm.legal_description} onChange={(e) => setDeedForm({ ...deedForm, legal_description: e.target.value })} style={{ ...deskInput, flex: 1, minWidth: 220 }} />
                  <button data-testid="deed-register-button" onClick={registerDeed} disabled={regBusy} style={deskBtn}>REGISTER DEED</button>
                </div>
                {(deeds?.deeds || []).map((d) => (
                  <div key={d.asset_id} data-testid={`deed-row-${d.parcel_apn}`} style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", borderTop: "1px solid rgba(176,183,189,0.15)", paddingTop: 6, marginTop: 6 }}>
                    <span style={{ color: "#e8e9ea", fontWeight: 700 }}>{d.parcel_apn}</span>
                    <span style={{ color: "#6b7280" }}>{d.county} · ${fmt(d.value_basis_usd)} · {d.unallocated_fractions == null ? "chain pending" : `${fmt(d.unallocated_fractions)}/${fmt(d.total_fractions)} in trust`}</span>
                    <span style={{ color: d.liquidation_locked ? "#e5c158" : "#38e07c" }}>{d.liquidation_locked ? "LOCKED" : "UNLOCKED"}</span>
                    <input placeholder="member uuid" value={deedAbsorb[d.asset_id]?.member_uuid || ""} onChange={(e) => setDeedAbsorb({ ...deedAbsorb, [d.asset_id]: { ...(deedAbsorb[d.asset_id] || {}), member_uuid: e.target.value } })} style={{ ...deskInput, width: 210 }} data-testid={`deed-absorb-uuid-${d.parcel_apn}`} />
                    <input placeholder="fractions" value={deedAbsorb[d.asset_id]?.fractions || ""} onChange={(e) => setDeedAbsorb({ ...deedAbsorb, [d.asset_id]: { ...(deedAbsorb[d.asset_id] || {}), fractions: e.target.value } })} style={{ ...deskInput, width: 90 }} data-testid={`deed-absorb-fractions-${d.parcel_apn}`} />
                    <button onClick={() => absorbDeed(d.asset_id)} disabled={regBusy} style={deskBtn} data-testid={`deed-absorb-btn-${d.parcel_apn}`}>ABSORB</button>
                  </div>
                ))}
                <div style={{ color: "#6b7280", marginTop: 10, marginBottom: 4 }}>VAULT — bullion & safe keeping receipts {vault ? `· ${vault.total_bars} bars · ${vault.active_skrs} active SKR · ${vault.audits_logged} audits` : ""}</div>
                {vault && (
                  <div style={{ color: "#b0b7bd", marginBottom: 6 }}>
                    {Object.entries(vault.by_element).map(([el, v]) => (
                      <span key={el} style={{ marginRight: 14 }}>{el}: {v.bars} bars / {v.net_pure_grams}g</span>
                    ))}
                  </div>
                )}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                  <input data-testid="bar-serial" placeholder="Bar serial" value={barForm.bar_serial_number} onChange={(e) => setBarForm({ ...barForm, bar_serial_number: e.target.value })} style={deskInput} />
                  <select data-testid="bar-element" value={barForm.element} onChange={(e) => setBarForm({ ...barForm, element: e.target.value })} style={deskInput}>
                    {["GOLD", "SILVER", "PLATINUM", "COPPER", "PALLADIUM"].map((el) => <option key={el} value={el}>{el}</option>)}
                  </select>
                  <input data-testid="bar-purity" placeholder="Purity 0.9999" value={barForm.purity_percentage} onChange={(e) => setBarForm({ ...barForm, purity_percentage: e.target.value })} style={{ ...deskInput, width: 100 }} />
                  <input data-testid="bar-grams" placeholder="Gross grams" value={barForm.gross_weight_grams} onChange={(e) => setBarForm({ ...barForm, gross_weight_grams: e.target.value })} style={{ ...deskInput, width: 100 }} />
                  <button data-testid="bar-register-button" onClick={registerBar} disabled={regBusy} style={deskBtn}>VAULT BAR</button>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <input data-testid="skr-member" placeholder="member uuid" value={skrForm.member_uuid} onChange={(e) => setSkrForm({ ...skrForm, member_uuid: e.target.value })} style={{ ...deskInput, width: 260 }} />
                  <input data-testid="skr-bars" placeholder="bar serials, comma separated" value={skrForm.bar_serial_numbers} onChange={(e) => setSkrForm({ ...skrForm, bar_serial_numbers: e.target.value })} style={{ ...deskInput, flex: 1, minWidth: 200 }} />
                  <button data-testid="skr-issue-button" onClick={issueSkr} disabled={regBusy} style={deskBtn}>ISSUE SKR</button>
                </div>
                <div style={{ color: "#6b7280", marginTop: 10, marginBottom: 4, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                  <span>INFRASTRUCTURE — tangible assets absorbed with decommissioned cash{infra?.total_tangible_backing_usd != null ? ` · backing the loop: $${fmt(infra.total_tangible_backing_usd)}` : ""}</span>
                  {infra && !infra.registry_deployed && (
                    <button data-testid="infra-deploy-button" onClick={deployInfraRegistry} disabled={regBusy} style={deskBtn}>
                      {regBusy ? "DEPLOYING…" : "DEPLOY INFRA LEDGER ON-CHAIN"}
                    </button>
                  )}
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                  <select data-testid="infra-class" value={infraForm.asset_class} onChange={(e) => setInfraForm({ ...infraForm, asset_class: e.target.value })} style={deskInput}>
                    {["LAND", "EQUIPMENT", "BIOLOGICAL_CAPITAL", "BULLION"].map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <input data-testid="infra-descriptor" placeholder="Serial / APN / legal location" value={infraForm.legal_descriptor} onChange={(e) => setInfraForm({ ...infraForm, legal_descriptor: e.target.value })} style={{ ...deskInput, flex: 1, minWidth: 220 }} />
                  <input data-testid="infra-cost" placeholder="Cash USD" value={infraForm.acquisition_cost_usd} onChange={(e) => setInfraForm({ ...infraForm, acquisition_cost_usd: e.target.value })} style={{ ...deskInput, width: 100 }} />
                  <button data-testid="infra-absorb-button" onClick={absorbInfra} disabled={regBusy} style={deskBtn}>ABSORB ASSET</button>
                </div>
                {(infra?.pending_absorptions || []).map((p) => (
                  <div key={p.short_id} data-testid={`infra-pending-${p.short_id}`} style={{ display: "flex", gap: 10, borderTop: "1px dashed rgba(234,179,8,0.35)", paddingTop: 4, marginTop: 4 }}>
                    <span style={{ color: "#eab308" }}>STAGED {p.short_id}</span>
                    <span style={{ color: "#e8e9ea" }}>{p.asset_class}</span>
                    <span style={{ color: "#6b7280", flex: 1 }}>{p.legal_descriptor} — votes {(p.votes || []).length}/{p.required_approvals}, council signs by SMS: /APPROVE {p.short_id}</span>
                    <span style={{ color: "#eab308" }}>${fmt(p.acquisition_cost_usd)}</span>
                  </div>
                ))}
                {(infra?.assets || []).map((a) => (
                  <div key={a.asset_id} data-testid="infra-row" style={{ display: "flex", gap: 10, borderTop: "1px solid rgba(176,183,189,0.15)", paddingTop: 4, marginTop: 4 }}>
                    <span style={{ color: "#e8e9ea" }}>{a.asset_class}</span>
                    <span style={{ color: "#6b7280", flex: 1 }}>{a.legal_descriptor}</span>
                    <span style={{ color: "#38e07c" }}>${fmt(a.acquisition_cost_usd)}</span>
                  </div>
                ))}
                <div style={{ color: "#6b7280", marginTop: 10, marginBottom: 4, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                  <span>ASSET ENGINE — property titled to a member's name{assetEngine?.floors ? ` · floors: ${assetEngine.floors.reserve_floor_pct}% reserve / ${assetEngine.floors.gold_coverage_pct}% gold` : ""}{assetEngine?.attributed_equity_usd ? ` · attributed equity $${fmt(assetEngine.attributed_equity_usd)}` : ""}</span>
                  {assetEngine && !assetEngine.registry_deployed && (
                    <button data-testid="asset-engine-deploy-button" onClick={deployAssetEngine} disabled={regBusy} style={deskBtn}>
                      {regBusy ? "DEPLOYING…" : "DEPLOY ASSET ENGINE ON-CHAIN"}
                    </button>
                  )}
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                  <select data-testid="asset-class" value={assetForm.property_class} onChange={(e) => setAssetForm({ ...assetForm, property_class: e.target.value })} style={deskInput}>
                    {["REAL_ESTATE", "INFRASTRUCTURE", "METALS_BULLION", "AGRI_ORCHARD"].map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <input data-testid="asset-metadata" placeholder="Deed text / serial / SKR text" value={assetForm.legal_metadata} onChange={(e) => setAssetForm({ ...assetForm, legal_metadata: e.target.value })} style={{ ...deskInput, flex: 1, minWidth: 220 }} />
                  <input data-testid="asset-valuation" placeholder="Value USD" value={assetForm.valuation_usd} onChange={(e) => setAssetForm({ ...assetForm, valuation_usd: e.target.value })} style={{ ...deskInput, width: 100 }} />
                  <input data-testid="asset-member" placeholder="member uuid (title holder)" value={assetForm.member_uuid} onChange={(e) => setAssetForm({ ...assetForm, member_uuid: e.target.value })} style={{ ...deskInput, width: 260 }} />
                  <button data-testid="asset-absorb-button" onClick={absorbAsset} disabled={regBusy} style={deskBtn}>ABSORB PROPERTY</button>
                </div>
                {(assetEngine?.properties || []).map((p) => (
                  <div key={p.property_id} data-testid="asset-row" style={{ display: "flex", gap: 10, borderTop: "1px solid rgba(176,183,189,0.15)", paddingTop: 4, marginTop: 4, alignItems: "center", flexWrap: "wrap" }}>
                    <span style={{ color: "#e8e9ea" }}>{p.property_class}</span>
                    <span style={{ color: "#6b7280", flex: 1, minWidth: 180 }}>{p.legal_metadata} — {p.owner_member_label || p.owner_member_uuid}</span>
                    <span style={{ color: "#38e07c" }}>${fmt(p.valuation_usd)}</span>
                    <input placeholder="liquidate title → member uuid" value={titleMove[p.property_id] || ""} onChange={(e) => setTitleMove({ ...titleMove, [p.property_id]: e.target.value })} style={{ ...deskInput, width: 220 }} data-testid={`asset-liquidate-uuid-${p.property_id.slice(2, 10)}`} />
                    <button onClick={() => liquidateTitle(p.property_id)} disabled={regBusy} style={deskBtn} data-testid={`asset-liquidate-btn-${p.property_id.slice(2, 10)}`}>MOVE TITLE</button>
                  </div>
                ))}
                <div style={{ color: "#6b7280", marginTop: 10, marginBottom: 4 }}>
                  <span>FAMILY COVENANTS — the private 50/50 household rail; linked co-parents text /MAINTAIN [AMOUNT] [ASSET]</span>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                  <input data-testid="covenant-member-a" placeholder="co-parent A member uuid" value={covenantForm.member_a_uuid} onChange={(e) => setCovenantForm({ ...covenantForm, member_a_uuid: e.target.value })} style={{ ...deskInput, flex: 1, minWidth: 240 }} />
                  <input data-testid="covenant-member-b" placeholder="co-parent B member uuid" value={covenantForm.member_b_uuid} onChange={(e) => setCovenantForm({ ...covenantForm, member_b_uuid: e.target.value })} style={{ ...deskInput, flex: 1, minWidth: 240 }} />
                  <button data-testid="covenant-link-button" onClick={linkCovenant} disabled={regBusy} style={deskBtn}>LINK COVENANT</button>
                </div>
                {(covenants?.covenants || []).map((c) => {
                  const view = escrows[c.covenant_id];
                  const f = escrowForm[c.covenant_id] || {};
                  return (
                    <div key={c.covenant_id} data-testid={`covenant-row-${c.covenant_id}`} style={{ borderTop: "1px solid rgba(176,183,189,0.15)", paddingTop: 4, marginTop: 4 }}>
                      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                        <span style={{ color: "#eab308" }}>{c.covenant_id}</span>
                        <span style={{ color: "#e8e9ea" }}>{c.member_a_label || c.member_a_uuid}</span>
                        <span style={{ color: "#6b7280" }}>⇄</span>
                        <span style={{ color: "#e8e9ea", flex: 1 }}>{c.member_b_label || c.member_b_uuid}</span>
                        {c.routing_locked && <span style={{ color: "#38e07c", fontSize: 11 }} data-testid={`covenant-locked-${c.covenant_id}`}>ROUTING LOCKED</span>}
                        {!view?.escrow_deployed && (
                          <button data-testid={`escrow-deploy-${c.covenant_id}`} onClick={() => deployEscrow(c.covenant_id)} disabled={regBusy} style={deskBtn}>DEPLOY 2-OF-2 ESCROW</button>
                        )}
                        {view?.escrow_deployed && !c.routing_locked && (
                          <button data-testid={`covenant-lock-${c.covenant_id}`} onClick={() => lockCovenant(c.covenant_id)} disabled={regBusy} style={deskBtn}>LOCK ROUTING</button>
                        )}
                      </div>
                      {view?.escrow_deployed && (
                        <div style={{ marginTop: 6, marginLeft: 8 }}>
                          <p style={{ color: "#6b7280", fontSize: 11, margin: "4px 0" }} data-testid={`escrow-pool-${c.covenant_id}`}>
                            escrow {view.escrow_address} · pool {view.available_equity_usd != null ? `$${fmt(view.available_equity_usd)}` : "—"}
                            {(view.available_equity_usd || 0) === 0 ? " — the pool opens on a real deposit (funding desk or a parent's wallet)" : ""} · split {view.allocation_split_pct}/{100 - view.allocation_split_pct}
                          </p>
                          {(view.funding_history || []).map((h, i) => (
                            <div key={i} data-testid={`escrow-funding-${c.covenant_id}-${i}`} style={{ display: "flex", gap: 10, fontSize: 12 }}>
                              <span style={{ color: "#38e07c" }}>+${fmt(h.amount_usd)}</span>
                              <span style={{ color: "#6b7280" }}>from {h.source}</span>
                              <span style={{ color: "#6b7280" }}>block {h.block}</span>
                            </div>
                          ))}
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "4px 0" }}>
                            <input placeholder="Deposit USD" value={f.fund_amount || ""} onChange={(e) => setEf(c.covenant_id, { fund_amount: e.target.value })} style={{ ...deskInput, width: 110 }} data-testid={`escrow-fund-amount-${c.covenant_id}`} />
                            <select value={f.fund_source || "TREASURY"} onChange={(e) => setEf(c.covenant_id, { fund_source: e.target.value })} style={deskInput} data-testid={`escrow-fund-source-${c.covenant_id}`}>
                              <option value="TREASURY">treasury desk float</option>
                              <option value={c.member_a_uuid}>{c.member_a_label || "parent A"} wallet</option>
                              <option value={c.member_b_uuid}>{c.member_b_label || "parent B"} wallet</option>
                            </select>
                            <button onClick={() => fundEscrow(c.covenant_id)} disabled={regBusy} style={deskBtn} data-testid={`escrow-fund-btn-${c.covenant_id}`}>FUND POOL</button>
                          </div>
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "4px 0" }}>
                            <select value={f.act_member || c.member_a_uuid} onChange={(e) => setEf(c.covenant_id, { act_member: e.target.value })} style={deskInput} data-testid={`escrow-actor-${c.covenant_id}`}>
                              <option value={c.member_a_uuid}>{c.member_a_label || "parent A"} stages</option>
                              <option value={c.member_b_uuid}>{c.member_b_label || "parent B"} stages</option>
                            </select>
                            <input placeholder="Amount USD" value={f.amount || ""} onChange={(e) => setEf(c.covenant_id, { amount: e.target.value })} style={{ ...deskInput, width: 100 }} data-testid={`escrow-amount-${c.covenant_id}`} />
                            <input placeholder="Purpose (Education, Orchard plot…)" value={f.purpose || ""} onChange={(e) => setEf(c.covenant_id, { purpose: e.target.value })} style={{ ...deskInput, flex: 1, minWidth: 180 }} data-testid={`escrow-purpose-${c.covenant_id}`} />
                            <input placeholder="recipient wallet 0x…" value={f.recipient || ""} onChange={(e) => setEf(c.covenant_id, { recipient: e.target.value })} style={{ ...deskInput, width: 260 }} data-testid={`escrow-recipient-${c.covenant_id}`} />
                            <button onClick={() => proposeEscrow(c.covenant_id, c)} disabled={regBusy} style={deskBtn} data-testid={`escrow-propose-btn-${c.covenant_id}`}>STAGE PROPOSAL</button>
                          </div>
                          {(view.proposals || []).map((p) => (
                            <div key={p.proposal_id} data-testid={`escrow-proposal-${c.covenant_id}-${p.proposal_id}`} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", fontSize: 12 }}>
                              <span style={{ color: p.status === "EXECUTED" ? "#38e07c" : "#eab308" }}>#{p.proposal_id} {p.status}</span>
                              <span style={{ color: "#e8e9ea" }}>${fmt(p.amount_usd)} · {p.purpose}</span>
                              <span style={{ color: "#6b7280", flex: 1 }}>staged by {p.staged_by === c.member_a_uuid ? (c.member_a_label || "A") : (c.member_b_label || "B")}{p.status === "EXECUTED" ? ` · sealed block ${p.countersign_tx?.block}` : ""}</span>
                              {p.status === "PENDING" && (
                                <button onClick={() => countersignEscrow(c.covenant_id, p.proposal_id, p.staged_by === c.member_a_uuid ? c.member_b_uuid : c.member_a_uuid)} disabled={regBusy} style={deskBtn} data-testid={`escrow-countersign-${c.covenant_id}-${p.proposal_id}`}>
                                  COUNTERSIGN ({(p.staged_by === c.member_a_uuid ? c.member_b_label : c.member_a_label) || "other parent"})
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                <div style={{ color: "#6b7280", marginTop: 10, marginBottom: 4, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                  <span>TCU REGISTER — 100 certificate units, committee-approved movements{tcu?.assigned_units != null ? ` · reserve ${tcu.reserve_units} · assigned ${tcu.assigned_units}` : ""}</span>
                  {tcu && !tcu.registry_deployed && (
                    <button data-testid="tcu-deploy-button" onClick={deployTcuRegistry} disabled={regBusy} style={deskBtn}>
                      {regBusy ? "DEPLOYING…" : "DEPLOY TCU REGISTER ON-CHAIN"}
                    </button>
                  )}
                </div>
                {(tcu?.holders || []).map((h) => (
                  <div key={h.member_uuid} data-testid="tcu-holder-row" style={{ display: "flex", gap: 10, borderTop: "1px solid rgba(176,183,189,0.15)", paddingTop: 4, marginTop: 4 }}>
                    <span style={{ color: "#e8e9ea" }}>{h.member_label || h.member_uuid}</span>
                    <span style={{ color: "#6b7280", flex: 1 }}>{h.certificate_number || ""}</span>
                    <span style={{ color: "#e5c158" }}>{h.units} TCU · {h.estate_share_pct}% of estate</span>
                  </div>
                ))}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8, marginBottom: 6 }}>
                  <select data-testid="tcu-action" value={tcuForm.action} onChange={(e) => setTcuForm({ ...tcuForm, action: e.target.value })} style={deskInput}>
                    {["ASSIGN", "TRANSFER", "RETIRE_ON_DEATH"].map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                  {tcuForm.action === "TRANSFER" && (
                    <input data-testid="tcu-from" placeholder="from member uuid" value={tcuForm.from_member_uuid} onChange={(e) => setTcuForm({ ...tcuForm, from_member_uuid: e.target.value })} style={{ ...deskInput, width: 200 }} />
                  )}
                  <input data-testid="tcu-member" placeholder="member uuid" value={tcuForm.member_uuid} onChange={(e) => setTcuForm({ ...tcuForm, member_uuid: e.target.value })} style={{ ...deskInput, width: 200 }} />
                  <input data-testid="tcu-units" placeholder="units" value={tcuForm.units} onChange={(e) => setTcuForm({ ...tcuForm, units: e.target.value })} style={{ ...deskInput, width: 70 }} />
                  <input data-testid="tcu-note" placeholder="resolution note" value={tcuForm.resolution_note} onChange={(e) => setTcuForm({ ...tcuForm, resolution_note: e.target.value })} style={{ ...deskInput, flex: 1, minWidth: 180 }} />
                  <button data-testid="tcu-motion-button" onClick={proposeTcuMotion} disabled={regBusy} style={deskBtn}>PROPOSE MOTION</button>
                </div>
                {(tcu?.motions || []).filter((m) => m.status === "PENDING_APPROVAL").map((m) => (
                  <div key={m.motion_id} data-testid={`tcu-motion-${m.motion_id}`} style={{ display: "flex", gap: 10, alignItems: "center", borderTop: "1px solid rgba(229,193,88,0.25)", paddingTop: 4, marginTop: 4 }}>
                    <span style={{ color: "#e5c158" }}>{m.motion_id}</span>
                    <span style={{ color: "#b0b7bd", flex: 1 }}>{m.action} {m.units}u → {m.member_label || m.member_uuid} · by {m.proposed_by}</span>
                    <button data-testid={`tcu-approve-${m.motion_id}`} onClick={() => approveTcuMotion(m.motion_id)} disabled={regBusy} style={{ ...deskBtn, color: "#38e07c", borderColor: "rgba(56,224,124,0.5)" }}>APPROVE (2nd director)</button>
                    <button data-testid={`tcu-dismiss-${m.motion_id}`} onClick={() => dismissTcuMotion(m.motion_id)} disabled={regBusy} style={{ ...deskBtn, color: "#ff4455", borderColor: "rgba(255,68,85,0.4)" }}>DISMISS</button>
                  </div>
                ))}
              </div>
              {reserve && (
                <div data-testid="reserve-panel" style={{ display: "flex", gap: 18, flexWrap: "wrap", marginBottom: 14, padding: "10px 12px", border: "1px solid rgba(176,183,189,0.25)", borderRadius: 8, fontFamily: "ui-monospace, Menlo, monospace", fontSize: "0.72rem" }}>
                  <span style={{ color: "#b0b7bd" }}>
                    $sUSD OUTSTANDING{" "}
                    <strong data-testid="reserve-outstanding" style={{ color: "#38e07c" }}>
                      {reserve.susd_outstanding == null ? reserve.chain_state : fmt(reserve.susd_outstanding)}
                    </strong>
                  </span>
                  <span style={{ color: "#b0b7bd" }}>
                    BANK CASH{" "}
                    <strong data-testid="reserve-bank-cash" style={{ color: reserve.bank_cash_usd == null ? "#e5c158" : "#38e07c" }}>
                      {reserve.bank_cash_usd == null ? "AWAITING NEXORONE CORE" : fmt(reserve.bank_cash_usd)}
                    </strong>
                  </span>
                  <span style={{ color: "#b0b7bd" }}>
                    COVERAGE{" "}
                    <strong data-testid="reserve-coverage" style={{ color: "#e8e4dc" }}>
                      {reserve.coverage_ratio == null ? "—" : `${(reserve.coverage_ratio * 100).toFixed(2)}%`}
                    </strong>
                  </span>
                </div>
              )}
              {floatData && (
                <div data-testid="float-panel" style={{ display: "flex", gap: 18, flexWrap: "wrap", marginBottom: 14, padding: "10px 12px", border: "1px solid rgba(201,162,39,0.35)", borderRadius: 8, fontFamily: "ui-monospace, Menlo, monospace", fontSize: "0.72rem" }}>
                  <span style={{ color: "#b0b7bd" }}>
                    CARD FLOAT{" "}
                    <strong data-testid="float-total" style={{ color: "#e6c765" }}>
                      {fmt(floatData.float_total_usd)}
                    </strong>
                  </span>
                  <span style={{ color: "#b0b7bd" }}>
                    FUNDED CARDS{" "}
                    <strong data-testid="float-funded" style={{ color: "#e8e4dc" }}>
                      {floatData.funded_wallets} / {floatData.cards_active} issued
                    </strong>
                  </span>
                  <span style={{ color: "#b0b7bd" }}>
                    STARS LIABILITY{" "}
                    <strong data-testid="float-stars" style={{ color: "#e8e4dc" }}>
                      {floatData.stars_outstanding.toLocaleString()}★ · {fmt(floatData.stars_liability_usd)}
                    </strong>
                  </span>
                  <span style={{ color: "#b0b7bd" }}>
                    BREAKAGE ({floatData.breakage_days}D SILENT){" "}
                    <strong data-testid="float-breakage" style={{ color: floatData.breakage_candidates.length ? "#e5c158" : "#38e07c" }}>
                      {floatData.breakage_candidates.length ? floatData.breakage_candidates.join(", ") : "NONE"}
                    </strong>
                  </span>
                </div>
              )}
              <form
                data-testid="funding-match-form"
                onSubmit={matchBankLine}
                style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14, alignItems: "center" }}
              >
                <span style={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: "0.72rem", color: "#5bc6e9" }}>
                  MATCH A BANK LINE →
                </span>
                <input
                  data-testid="match-amount"
                  type="number" min="0.01" step="0.01" required
                  placeholder="Wire amount $"
                  value={matchForm.amount}
                  onChange={(e) => setMatchForm({ ...matchForm, amount: e.target.value })}
                  style={{ flex: "1 1 110px", background: "rgba(11,15,25,0.9)", border: "1px solid rgba(176,183,189,0.3)", borderRadius: 6, padding: "8px 10px", color: "#e8e4dc", fontSize: "0.75rem" }}
                />
                <input
                  data-testid="match-reference"
                  placeholder="Bank memo / reference (UUID if present)"
                  value={matchForm.reference}
                  onChange={(e) => setMatchForm({ ...matchForm, reference: e.target.value })}
                  style={{ flex: "2 1 180px", background: "rgba(11,15,25,0.9)", border: "1px solid rgba(176,183,189,0.3)", borderRadius: 6, padding: "8px 10px", color: "#e8e4dc", fontSize: "0.75rem" }}
                />
                <button
                  data-testid="match-submit"
                  type="submit"
                  disabled={matchBusy || !matchForm.amount}
                  className="prime-clearing-btn"
                  style={{ margin: 0 }}
                >
                  {matchBusy ? "MATCHING…" : "FIND MATCHES"}
                </button>
              </form>
              {matchResults && (
                <div data-testid="match-results" style={{ marginBottom: 14, fontFamily: "ui-monospace, Menlo, monospace", fontSize: "0.72rem" }}>
                  {matchResults.length === 0 ? (
                    <p style={{ color: "#e5c158" }}>No open declaration matches that bank line — settle only when the member declares from their wallet.</p>
                  ) : (
                    matchResults.map((m) => (
                      <div key={m.intention_id} data-testid={`match-candidate-${m.intention_id}`} style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8, padding: "6px 0", borderBottom: "1px solid rgba(176,183,189,0.12)" }}>
                        <span style={{ color: "#e8e4dc" }}>
                          {m.member_label} · declared {fmt(m.declared_amount_usd)}
                          {m.amount_delta_usd !== 0 && <span style={{ color: "#e5c158" }}> (Δ {m.amount_delta_usd > 0 ? "+" : ""}{m.amount_delta_usd.toFixed(2)})</span>}
                        </span>
                        <span>
                          <span style={{ color: m.confidence === "EXACT" ? "#38e07c" : "#e5c158", marginRight: 10 }}>{m.confidence.replaceAll("_", " ")}</span>
                          <button
                            data-testid={`match-use-${m.intention_id}`}
                            onClick={() => applyMatch(m)}
                            className="prime-clearing-btn"
                            style={{ margin: 0, padding: "4px 10px", fontSize: "0.68rem" }}
                          >
                            USE MATCH
                          </button>
                        </span>
                      </div>
                    ))
                  )}
                </div>
              )}
              <div
                data-testid="sales-backfill-queue"
                style={{
                  background: "rgba(11,15,25,0.85)",
                  border: "1px solid rgba(229,193,88,0.35)",
                  borderRadius: 8,
                  padding: "14px 16px",
                  fontFamily: "ui-monospace, Menlo, monospace",
                  fontSize: "0.72rem",
                  lineHeight: 1.9,
                  color: "#b0b7bd",
                  marginBottom: 12,
                }}
              >
                <div style={{ color: "#e5c158", marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <span>SALES BACKFILL — sale lines merchants texted in, waiting for one tap</span>
                  <span style={{ display: "flex", gap: 6 }}>
                    <button
                      data-testid="backfill-reverse-duplicates"
                      onClick={backfillReverseDuplicates}
                      disabled={bfBusy === "REVERSE"}
                      style={{ background: "rgba(255,68,85,0.12)", border: "1px solid rgba(255,68,85,0.45)", color: "#ff4455", borderRadius: 4, padding: "3px 10px", cursor: "pointer", fontFamily: "inherit", fontSize: "0.7rem" }}
                    >
                      {bfBusy === "REVERSE" ? "…" : "REVERSE STALE DUPLICATES"}
                    </button>
                    {backfills?.backfills?.length > 1 && (
                    <button
                      data-testid="backfill-ledger-all"
                      onClick={backfillLedgerAll}
                      disabled={bfBusy === "ALL"}
                      style={{ background: "rgba(56,224,124,0.15)", border: "1px solid rgba(56,224,124,0.5)", color: "#38e07c", borderRadius: 4, padding: "3px 10px", cursor: "pointer", fontFamily: "inherit", fontSize: "0.7rem" }}
                    >
                      {bfBusy === "ALL" ? "…" : `LEDGER ALL (${backfills.backfills.length})`}
                    </button>
                  )}
                  </span>
                </div>
                {backfills?.backfills?.length > 0 ? (
                  backfills.backfills.map((b) => (
                    <div key={b.backfill_id} data-testid={`backfill-row-${b.backfill_id}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <span>
                        <span style={{ color: "#5bc6e9" }}>{(b.created_at || "").slice(5, 16).replace("T", " ")}</span>{" "}
                        <strong style={{ color: "#e8e9ea" }}>{b.merchant_label}</strong> · {b.item_label} ·{" "}
                        <span style={{ color: "#38e07c" }}>${Number(b.amount_usd).toFixed(2)}</span>
                      </span>
                      <span style={{ display: "flex", gap: 6 }}>
                        <button
                          data-testid={`backfill-ledger-${b.backfill_id}`}
                          onClick={() => backfillAction(b.backfill_id, "ledger")}
                          disabled={bfBusy === b.backfill_id}
                          style={{ background: "rgba(56,224,124,0.15)", border: "1px solid rgba(56,224,124,0.5)", color: "#38e07c", borderRadius: 4, padding: "3px 10px", cursor: "pointer", fontFamily: "inherit", fontSize: "0.7rem" }}
                        >
                          {bfBusy === b.backfill_id ? "…" : "LEDGER"}
                        </button>
                        <button
                          data-testid={`backfill-dismiss-${b.backfill_id}`}
                          onClick={() => backfillAction(b.backfill_id, "dismiss")}
                          disabled={bfBusy === b.backfill_id}
                          style={{ background: "transparent", border: "1px solid rgba(176,183,189,0.35)", color: "#b0b7bd", borderRadius: 4, padding: "3px 10px", cursor: "pointer", fontFamily: "inherit", fontSize: "0.7rem" }}
                        >
                          DISMISS
                        </button>
                      </span>
                    </div>
                  ))
                ) : (
                  <div data-testid="backfill-empty" style={{ color: "#6b7280" }}>
                    Queue empty — when a merchant texts a sale line ("Sourdough loaf $4"), it lands here.
                  </div>
                )}
                <div style={{ marginTop: 10, borderTop: "1px solid rgba(229,193,88,0.2)", paddingTop: 8 }}>
                  <div style={{ color: "#6b7280", marginBottom: 4 }}>
                    Order text arrived off-rail? Paste it here — every $ amount queues as its own line.
                  </div>
                  <textarea
                    data-testid="backfill-intake-text"
                    value={bfPaste}
                    onChange={(e) => setBfPaste(e.target.value)}
                    placeholder={"Tahli cinnamon rolls $40\nCJ full dozen $88 …"}
                    rows={3}
                    style={{ width: "100%", background: "rgba(11,15,25,0.9)", border: "1px solid rgba(176,183,189,0.3)", borderRadius: 6, color: "#e8e9ea", fontFamily: "inherit", fontSize: "0.72rem", padding: "6px 8px", boxSizing: "border-box", resize: "vertical" }}
                  />
                  <button
                    data-testid="backfill-intake-submit"
                    onClick={backfillIntake}
                    disabled={bfBusy === "PASTE" || !bfPaste.trim()}
                    style={{ marginTop: 4, background: "rgba(229,193,88,0.12)", border: "1px solid rgba(229,193,88,0.5)", color: "#e5c158", borderRadius: 4, padding: "4px 12px", cursor: "pointer", fontFamily: "inherit", fontSize: "0.7rem" }}
                  >
                    {bfBusy === "PASTE" ? "…" : "QUEUE PASTED TEXT"}
                  </button>
                </div>
              </div>
              <div
                data-testid="funding-desk-list"
                style={{
                  background: "rgba(11,15,25,0.85)",
                  border: "1px solid rgba(176,183,189,0.25)",
                  borderRadius: 8,
                  padding: "14px 16px",
                  fontFamily: "ui-monospace, Menlo, monospace",
                  fontSize: "0.72rem",
                  lineHeight: 1.9,
                  color: "#b0b7bd",
                }}
              >
                {fundingDesk?.intentions?.length > 0 ? (
                  fundingDesk.intentions.map((it) => (
                    <div
                      key={it.intention_id}
                      data-testid={`funding-row-${it.intention_id}`}
                      style={{ borderBottom: "1px solid rgba(176,183,189,0.12)", padding: "10px 0" }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                        <span style={{ color: "#e5c158" }}>{it.member_label} · {it.rail}</span>
                        <span style={{ color: it.status === "SETTLED" ? "#38e07c" : it.status === "DISMISSED" ? "#6b7280" : "#e5c158" }}>
                          {fmt(it.amount_usd)} · {it.status.replaceAll("_", " ")}
                          {it.stale && <span data-testid={`funding-stale-${it.intention_id}`} style={{ color: "#f87171", marginLeft: 8 }}>STALE 7D+</span>}
                        </span>
                      </div>
                      <div>ref: <span style={{ color: "#5bc6e9" }}>{it.intention_id}</span> · declared {new Date(it.declared_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</div>
                      {it.status === "SETTLED" && (
                        <div>received: {fmt(it.amount_received_usd)}{it.external_reference ? ` · bank ref ${it.external_reference}` : ""} · block #{it.settlement_tx?.block} · by {it.settled_by}</div>
                      )}
                      {it.status === "DECLARED" && (
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                          <input
                            data-testid={`settle-amount-${it.intention_id}`}
                            type="number" min="0.01" step="0.01" required
                            placeholder="Amount received $"
                            value={(settleForm[it.intention_id] || {}).amount || ""}
                            onChange={(e) => setSettleForm({ ...settleForm, [it.intention_id]: { ...(settleForm[it.intention_id] || {}), amount: e.target.value } })}
                            style={{ flex: "1 1 120px", background: "rgba(11,15,25,0.9)", border: "1px solid rgba(176,183,189,0.3)", borderRadius: 6, padding: "8px 10px", color: "#e8e4dc", fontSize: "0.75rem" }}
                          />
                          <input
                            data-testid={`settle-ref-${it.intention_id}`}
                            placeholder="Bank reference (optional)"
                            value={(settleForm[it.intention_id] || {}).reference || ""}
                            onChange={(e) => setSettleForm({ ...settleForm, [it.intention_id]: { ...(settleForm[it.intention_id] || {}), reference: e.target.value } })}
                            style={{ flex: "2 1 160px", background: "rgba(11,15,25,0.9)", border: "1px solid rgba(176,183,189,0.3)", borderRadius: 6, padding: "8px 10px", color: "#e8e4dc", fontSize: "0.75rem" }}
                          />
                          <button
                            data-testid={`settle-submit-${it.intention_id}`}
                            onClick={() => settleIntention(it.intention_id)}
                            disabled={fdBusy === it.intention_id || !(settleForm[it.intention_id] || {}).amount}
                            className="prime-clearing-btn"
                            style={{ margin: 0 }}
                          >
                            {fdBusy === it.intention_id ? "SETTLING…" : "WIRE LANDED — SETTLE"}
                          </button>
                          <button
                            data-testid={`dismiss-submit-${it.intention_id}`}
                            onClick={() => dismissIntention(it.intention_id)}
                            disabled={fdBusy === it.intention_id}
                            style={{ margin: 0, background: "transparent", border: "1px solid rgba(176,183,189,0.35)", borderRadius: 6, padding: "8px 12px", color: "#b0b7bd", fontSize: "0.68rem", fontFamily: "ui-monospace, Menlo, monospace", cursor: "pointer" }}
                          >
                            DISMISS
                          </button>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div data-testid="funding-desk-empty">
                    No declared wires — members declare incoming wires from their wallet's "Fund your
                    wallet" panel; they appear here for attestation the moment the bank confirms.
                  </div>
                )}
                {fdMsg && (
                  <p data-testid="funding-desk-message" style={{ color: "#38e07c", marginTop: 8 }}>{fdMsg}</p>
                )}
              </div>
            </div>

            <div
              data-testid="document-vault-panel"
              style={{ borderTop: "1px solid rgba(176,183,189,0.2)", paddingTop: 20 }}
            >
              <p className="portal-sub-tag" style={{ marginBottom: 16, textAlign: "left" }}>
                Council Document Vault · Generated Records & Cutover Packs
              </p>
              <div
                data-testid="document-vault-list"
                style={{
                  background: "rgba(11,15,25,0.85)",
                  border: "1px solid rgba(176,183,189,0.25)",
                  borderRadius: 8,
                  padding: "14px 16px",
                  fontFamily: "ui-monospace, Menlo, monospace",
                  fontSize: "0.72rem",
                  lineHeight: 1.9,
                  color: "#b0b7bd",
                }}
              >
                {documents?.documents?.length > 0 ? (
                  documents.documents.map((doc) => (
                    <div
                      key={doc.filename}
                      data-testid={`document-row-${doc.filename}`}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 12,
                        flexWrap: "wrap",
                        borderBottom: "1px solid rgba(176,183,189,0.12)",
                        padding: "8px 0",
                      }}
                    >
                      <div style={{ wordBreak: "break-all" }}>
                        <span style={{ color: "#5bc6e9" }}>{doc.filename}</span>
                        <span style={{ color: "#b0b7bd" }}>
                          {" "}
                          · {(doc.size_bytes / 1024).toFixed(1)} KB · updated{" "}
                          {new Date(doc.updated_at).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}
                        </span>
                      </div>
                      <button
                        className="prime-clearing-btn"
                        data-testid={`document-download-${doc.filename}`}
                        onClick={() => downloadDocument(doc.filename)}
                        disabled={docBusy === doc.filename}
                        style={{ margin: 0 }}
                      >
                        {docBusy === doc.filename ? "SEALING…" : "DOWNLOAD PDF"}
                      </button>
                    </div>
                  ))
                ) : (
                  <div data-testid="document-vault-empty">
                    No council documents generated yet — packs appear here as they are issued.
                  </div>
                )}
              </div>
            </div>

            <div
              data-testid="nexorone-panel"
              style={{ borderTop: "1px solid rgba(176,183,189,0.2)", paddingTop: 20 }}
            >
              <p className="portal-sub-tag" style={{ marginBottom: 16, textAlign: "left" }}>
                NexorONE Core Banking Bridge
              </p>
              <div
                data-testid="nexorone-status-card"
                style={{
                  background: "rgba(11,15,25,0.85)",
                  border: "1px solid rgba(176,183,189,0.25)",
                  borderRadius: 8,
                  padding: "14px 16px",
                  fontFamily: "ui-monospace, Menlo, monospace",
                  fontSize: "0.72rem",
                  lineHeight: 1.8,
                  color: "#b0b7bd",
                  overflowX: "auto",
                  marginBottom: 16,
                }}
              >
                <div style={{ color: "#e5c158" }}>
                  environment: {nexor?.environment || "TEST_NON_PRODUCTION"} · auth mode:{" "}
                  {nexor?.auth_mode || "api_key"}
                </div>
                <div>
                  bridge state:{" "}
                  <span
                    data-testid="nexorone-state"
                    style={{
                      color:
                        nexor?.state === "UP"
                          ? "#38e07c"
                          : nexor?.state === "NOT_CONFIGURED"
                          ? "#e5c158"
                          : "#ff6b76",
                    }}
                  >
                    {nexor?.state || "CHECKING"}
                  </span>
                </div>
                {nexor?.endpoints_mapped && (
                  <div>
                    endpoint catalog:{" "}
                    {Object.entries(nexor.endpoints_mapped)
                      .map(([name, mapped]) => `${name} ${mapped ? "✓" : "—"}`)
                      .join(" · ")}
                  </div>
                )}
              </div>

              {nexor?.state === "NOT_CONFIGURED" && (
                <div
                  data-testid="nexorone-awaiting"
                  style={{
                    background: "rgba(229,193,88,0.08)",
                    border: "1px solid rgba(229,193,88,0.35)",
                    borderRadius: 8,
                    padding: "14px 16px",
                    fontSize: "0.75rem",
                    lineHeight: 1.7,
                    color: "#e5c158",
                    marginBottom: 16,
                  }}
                >
                  Bridge staged — awaiting the NexorONE integration pack (test environment): base
                  URL, auth mode + credential, and the endpoint paths for status / accounts /
                  balances / transactions / payments. No banking data is displayed until the live
                  adapter connects; nothing shown here is simulated.
                </div>
              )}

              {nexor?.state === "UP" && (
                <div data-testid="nexorone-live-feeds" style={{ marginBottom: 16 }}>
                  {[
                    ["Accounts", nexorAccounts, "nexorone-accounts"],
                    ["Balances", nexorBalances, "nexorone-balances"],
                    ["Transactions", nexorTxns, "nexorone-transactions"],
                  ].map(([label, data, testid]) => (
                    <div
                      key={testid}
                      data-testid={testid}
                      style={{
                        background: "rgba(11,15,25,0.85)",
                        border: "1px solid rgba(176,183,189,0.25)",
                        borderRadius: 8,
                        padding: "14px 16px",
                        fontFamily: "ui-monospace, Menlo, monospace",
                        fontSize: "0.72rem",
                        lineHeight: 1.7,
                        color: "#b0b7bd",
                        overflowX: "auto",
                        marginBottom: 12,
                      }}
                    >
                      <div style={{ color: "#e5c158" }}>{label}</div>
                      <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                        {data ? JSON.stringify(data, null, 2) : "feed pending"}
                      </pre>
                    </div>
                  ))}
                </div>
              )}

              {nexor?.member_portal_url ? (
                <div data-testid="nexorone-member-dashboard" style={{ marginBottom: 16 }}>
                  <a
                    href={nexor.member_portal_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="nexorone-dashboard-link"
                    style={{ color: "#5bc6e9", fontSize: "0.75rem" }}
                  >
                    Open NexorONE Member Banking Dashboard ↗
                  </a>
                  <iframe
                    src={nexor.member_portal_url}
                    title="NexorONE Member Banking Dashboard"
                    data-testid="nexorone-dashboard-frame"
                    style={{
                      width: "100%",
                      height: 480,
                      border: "1px solid rgba(176,183,189,0.25)",
                      borderRadius: 8,
                      marginTop: 10,
                      background: "#0b0f19",
                    }}
                  />
                </div>
              ) : (
                <div
                  data-testid="nexorone-member-dashboard-disabled"
                  style={{
                    fontSize: "0.72rem",
                    color: "#b0b7bd",
                    marginBottom: 16,
                    lineHeight: 1.7,
                  }}
                >
                  Member banking dashboard embed activates when the NexorONE landing/console URL is
                  registered with the bridge.
                </div>
              )}

              {nexorEvents.length > 0 && (
                <div
                  data-testid="nexorone-events"
                  style={{
                    background: "rgba(11,15,25,0.85)",
                    border: "1px solid rgba(176,183,189,0.25)",
                    borderRadius: 8,
                    padding: "14px 16px",
                    fontFamily: "ui-monospace, Menlo, monospace",
                    fontSize: "0.72rem",
                    lineHeight: 1.8,
                    color: "#b0b7bd",
                    overflowX: "auto",
                    marginBottom: 16,
                  }}
                >
                  <div style={{ color: "#e5c158" }}>
                    settlement mirror events → core banking ({nexorEvents.length})
                  </div>
                  {nexorEvents.map((e) => (
                    <div key={e.message_id} style={{ wordBreak: "break-all" }}>
                      <span style={{ color: "#5bc6e9" }}>{e.message_id}</span> · {e.kind} ·{" "}
                      <span
                        style={{
                          color: e.forwarded_to_nexorone ? "#38e07c" : "#e5c158",
                        }}
                      >
                        {e.forward_status}
                      </span>{" "}
                      · {e.iso20022_envelope?.creation_timestamp}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <AssessmentRequestsPanel />

            <button
              className="prime-clearing-btn"
              data-testid="portal-logout-button"
              onClick={() => {
                sessionStorage.removeItem("portal_token");
                setLedger(null);
                setKeyId("");
                setPassphrase("");
              }}
            >
              Close Settlement Session
            </button>
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: 24 }}>
          <a
            href="/"
            data-testid="portal-back-link"
            style={{
              color: "#b0b7bd",
              fontSize: "0.75rem",
              textTransform: "uppercase",
              letterSpacing: "1px",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <ArrowLeft size={13} /> Return to the Institute
          </a>
        </div>
      </div>
    </section>
  );
};

export default Portal;
